import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, useSearchParams, useParams, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { BrandingProvider } from '@/context';
import { AuthProvider } from '@/context/AuthContext';
import type { RoleType } from '@/context/AuthContext';
import { useAuth } from '@/hooks/useAuth';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import { PublicLayout, PortalLayout, AdminLayout } from '@/components/layout';
import { RouteErrorBoundary } from '@/components/common/AppErrorBoundary';
import { PublicCountdownBanner } from '@/components/shared/PublicCountdownBanner';
import { ApplicantTypeSelector, ApplicantDashboard } from '@/components/applicant';
import AdminDashboard from '@/components/admin/AdminDashboard';
import { ApprovalTimeHistogram } from '@/components/admin/ApprovalTimeHistogram';
import { InstagramMetricsPage } from '@/components/admin/InstagramMetricsPage';
import { ProgramImpactPage } from '@/components/admin/ProgramImpactPage';
import { InsightsPage } from '@/components/contestant/InsightsPage';
import { PageHeader } from '@/components/ui/page-header';
import { PillTabs } from '@/components/ui/pill-tabs';
import { KpiTile } from '@/components/ui/kpi-tile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { isMeridianNewScreensEnabled, isMeritEnabled, isCommunityEnabled, isDocumentLibraryEnabled, isApplicationFormEnabled, isSeasonTitleRegistryEnabled } from '@/config/featureFlags';
// The flag-gated `/apply` host (director-form-application-cutover, task 9.1).
// Imported eagerly (not lazy) so its synchronous `isApplicationFormEnabled()`
// branch renders the unmodified Legacy_Application_Flow with no Suspense
// fallback on the flag-off path (Req 1.2, 1.7).
import { ApplyHost } from '@/routes/application-form/ApplyHost';
// --- Lazy-loaded route components -------------------------------------------
//
// Heavy route components are split out via `React.lazy()` so they only
// download when the route is first visited. The chunks Vite emits land
// alongside the main bundle and load on demand. See PR (n) — bundle
// splitting in tasks.md task 19 for context.
//
// Eager imports above are kept eager intentionally: layouts +
// `<ProtectedRoute>` need to render synchronously on every navigation,
// the contestant `<ApplicantDashboard>` is the default landing for
// authenticated contestants, and `<AdminDashboard>` is the default
// landing for admins. The PageHeader / PillTabs primitives are tiny
// and used by many layouts.

const ParticipantDocuments = lazy(() =>
  import('@/components/applicant/ParticipantDocuments').then((m) => ({ default: m.ParticipantDocuments })),
);
const DesignReferencePage = lazy(() => import('@/routes/DesignReferencePage'));
const MeridianPrimitivesPreviewPage = lazy(() => import('@/routes/MeridianPrimitivesPreviewPage'));
const DashboardsPreviewPage = lazy(() => import('@/routes/DashboardsPreviewPage'));
const PodiumPreviewPage = lazy(() => import('@/routes/PodiumPreviewPage'));

// Platform administration (tenant-foundation Task 7.1) — the cross-tenant
// operator surface (tenant provisioning), gated to the PlatformAdmins group
// inside the component (the resolver guard is authoritative).
const PlatformAdmin = lazy(() =>
  import('@/components/admin/PlatformAdmin').then((m) => ({ default: m.PlatformAdmin })),
);

// Self-service tenant join (tenant-foundation Task 6) — a signed-in user appends
// their Membership in the target Tenant from a join link. Adult one-click join;
// the minor-under-guardian path is stubbed pending per-tenant legal config.
// tenant-foundation Task 18 — the `/<slug>` reach entry point (routed last).
const TenantReachLanding = lazy(() =>
  import('@/components/tenant/TenantReachLanding').then((m) => ({
    default: m.TenantReachLanding,
  })),
);
const JoinTenant = lazy(() =>
  import('@/components/tenant/JoinTenant').then((m) => ({ default: m.JoinTenant })),
);
const ParentEnrollment = lazy(() =>
  import('@/components/tenant/ParentEnrollment').then((m) => ({ default: m.ParentEnrollment })),
);
const ParentHub = lazy(() =>
  import('@/components/tenant/ParentHub').then((m) => ({ default: m.ParentHub })),
);

// Application Form builder — dev/staging-only mock harness (application-form-builder).
// Mounts all five surfaces against the in-memory mock client so the feature is
// clickable on localhost with no backend. Registered only off-prod, like the
// other /dev/* preview routes.
const ApplicationFormDevRoute = lazy(() =>
  import('@/routes/dev/ApplicationFormDevRoute').then((m) => ({ default: m.ApplicationFormDevRoute })),
);

// Document Library — dev/staging-only mock harness (director-document-library).
// Mounts the director + contestant document surfaces against the in-memory mock
// client so the feature is clickable on localhost with no backend. Registered
// only off-prod, like the other /dev/* preview routes.
const DocumentLibraryDevRoute = lazy(() =>
  import('@/routes/dev/DocumentLibraryDevRoute').then((m) => ({ default: m.DocumentLibraryDevRoute })),
);

// Document Center — REAL surfaces (director-document-library). Flag-gated behind
// `isDocumentLibraryEnabled()` at the Route registration sites below; when ON
// they REPLACE the legacy DocumentsPage / ParticipantDocuments surfaces. The
// components self-fetch via the Amplify client and enforce their own
// capability gates (canManageUsers / canSeePortal) as defense-in-depth.
const DirectorDocumentCenter = lazy(() =>
  import('@/components/documents/DirectorDocumentCenter').then((m) => ({ default: m.DirectorDocumentCenter })),
);
const ContestantDocumentLibrary = lazy(() =>
  import('@/components/documents/ContestantDocumentLibrary').then((m) => ({ default: m.ContestantDocumentLibrary })),
);

// Herald — community bulletin feed (herald-feed spec, task 7.1). Reachable
// by every authenticated role, so the routes are registered unconditionally
// (not behind the meridian feature flag) per Requirement 1.1.
const HeraldPage = lazy(() =>
  import('@/components/herald/HeraldPage').then((m) => ({ default: m.HeraldPage })),
);

const EnvoyPortalRoute = lazy(() =>
  import('@/routes/meridian/EnvoyPortalRoute').then((m) => ({ default: m.EnvoyPortalRoute })),
);
const EnvoyAdminRoute = lazy(() =>
  import('@/routes/meridian/EnvoyAdminRoute').then((m) => ({ default: m.EnvoyAdminRoute })),
);
const PatronRoute = lazy(() =>
  import('@/routes/meridian/PatronRoute').then((m) => ({ default: m.PatronRoute })),
);
// Sponsor-facing Patron surface (own agreement). GATE LOGIC NOW; portal UI
// deferred (meridian-data-model task 14.4 / 16.5). The sponsor-persona gate is
// enforced inside the component via the canonical matrix evaluator (sponsor is
// a contact facet, not a Role/capability), so it is registered without a
// `requiredCapability` route guard.
const PatronSponsorRoute = lazy(() =>
  import('@/routes/meridian/PatronSponsorRoute').then((m) => ({ default: m.PatronSponsorRoute })),
);
const VaultPortalRoute = lazy(() =>
  import('@/routes/meridian/VaultPortalRoute').then((m) => ({ default: m.VaultPortalRoute })),
);
const VaultAdminRoute = lazy(() =>
  import('@/routes/meridian/VaultAdminRoute').then((m) => ({ default: m.VaultAdminRoute })),
);

// Community Identity — Person-centric admin directory (community-identity
// spec, task 6.1). Flag-gated behind `isCommunityEnabled()` at the Route
// registration site below.
const ConsentAdminRoute = lazy(() =>
  import('@/routes/meridian/ConsentAdminRoute').then((m) => ({ default: m.ConsentAdminRoute })),
);
const CommunityAdminRoute = lazy(() =>
  import('@/routes/meridian/CommunityAdminRoute').then((m) => ({ default: m.CommunityAdminRoute })),
);

// Community Records (Phase 2) — relational record page for one person
// (`/admin/community/:personId`). Flag-gated behind `isCommunityEnabled()`
// alongside the directory route below (community-records spec, task 8.6).
const PersonRecordRoute = lazy(() =>
  import('@/routes/meridian/PersonRecordRoute').then((m) => ({ default: m.PersonRecordRoute })),
);

// Merit judging rebuild — admin setup/monitor + judge app (Phase 0 scaffold).
const MeritSetupRoute = lazy(() =>
  import('@/routes/meridian/MeritSetupRoute').then((m) => ({ default: m.MeritSetupRoute })),
);
const MeritMonitorRoute = lazy(() =>
  import('@/routes/meridian/MeritMonitorRoute').then((m) => ({ default: m.MeritMonitorRoute })),
);
// Merit web judging — the Cognito judge surface (merit-web-judging R1, R4).
// Full-screen shell behind the Merit entry gate; NOT wrapped in AdminLayout.
const MeritJudgeRoute = lazy(() =>
  import('@/routes/meridian/MeritJudgeRoute').then((m) => ({ default: m.MeritJudgeRoute })),
);

// Application Form (application-form-builder) — the real director Form_Builder
// and review/approval admin surfaces wired into the app shell. Flag-gated
// behind `isApplicationFormEnabled()` at the Route registration site below.
const ApplicationBuilderAdminRoute = lazy(() =>
  import('@/routes/application-form/ApplicationBuilderAdminRoute').then((m) => ({
    default: m.ApplicationBuilderAdminRoute,
  })),
);
const ApplicationReviewAdminRoute = lazy(() =>
  import('@/routes/application-form/ApplicationReviewAdminRoute').then((m) => ({
    default: m.ApplicationReviewAdminRoute,
  })),
);
// The single, reusable New_Review_Surface wrapper consumed by the reconciled
// `/admin/approvals` Applications tab (flag-on). Mirrors the Documents seam.
const ApplicationReviewSurface = lazy(() =>
  import('@/routes/application-form/ApplicationReviewSurface').then((m) => ({
    default: m.ApplicationReviewSurface,
  })),
);
const UserManagement = lazy(() =>
  import('@/components/admin/UserManagement').then((m) => ({ default: m.UserManagement })),
);
const SettingsPage = lazy(() =>
  import('@/components/admin/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const DocumentsPage = lazy(() =>
  import('@/components/admin/DocumentsPage').then((m) => ({ default: m.DocumentsPage })),
);
const CalendarPage = lazy(() => import('@/components/shared/CalendarPage'));
const MessagesPage = lazy(() =>
  import('@/components/shared/MessagesPage').then((m) => ({ default: m.MessagesPage })),
);
const PaymentProcessingPage = lazy(() =>
  import('@/components/admin/PaymentProcessingPage').then((m) => ({ default: m.PaymentProcessingPage })),
);
const PaymentConfirmation = lazy(() =>
  import('@/components/payment/PaymentConfirmation').then((m) => ({ default: m.PaymentConfirmation })),
);
const EmailTemplateManager = lazy(() =>
  import('@/components/admin/EmailTemplateManager').then((m) => ({ default: m.EmailTemplateManager })),
);
// Division Registry management surface (division-registry spec, task 11.1).
// Nested as a tab inside the admin Settings screen (Option B) so no new
// always-on nav item is introduced — the AdminSidebar / SidebarRouteConsistency
// contract is left untouched.
const DivisionManagement = lazy(() =>
  import('@/components/admin/DivisionManagement').then((m) => ({ default: m.DivisionManagement })),
);
// Season-scoped Title catalog management surface (season-scoped-title-registry
// spec, task 11). Nested as a tab inside the admin Settings screen (Option B) so
// no new always-on nav item is introduced — the AdminSidebar /
// SidebarRouteConsistency contract is left untouched.
const TitleManagement = lazy(() =>
  import('@/components/admin/TitleManagement').then((m) => ({ default: m.TitleManagement })),
);
// Contestant-number assignment surface (division-registry spec, task 11.2).
// Nested as a sibling section within the same admin Settings "Divisions" tab as
// DivisionManagement (Option B) — no new always-on nav item, so the AdminSidebar
// / SidebarRouteConsistency contract is left untouched.
const ContestantNumbering = lazy(() =>
  import('@/components/admin/ContestantNumbering').then((m) => ({ default: m.ContestantNumbering })),
);
// Canonical Season management surface (season-ssot spec, task 6). Nested as a
// tab inside the admin Settings screen (Option B) so no new always-on nav item
// is introduced — the AdminSidebar / SidebarRouteConsistency contract is left
// untouched. Lists seasons and wires the director to the existing Admin-gated
// createSeason / activateSeason / setSeasonWindows / scheduleSeasonArchive
// mutations (no new business logic).
const SeasonManagement = lazy(() =>
  import('@/components/admin/SeasonManagement').then((m) => ({ default: m.SeasonManagement })),
);

const SignInPage = lazy(() =>
  import('@/components/auth/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const SignUpPage = lazy(() =>
  import('@/components/auth/SignUpPage').then((m) => ({ default: m.SignUpPage })),
);
const PasswordResetPage = lazy(() =>
  import('@/components/auth/PasswordResetPage').then((m) => ({ default: m.PasswordResetPage })),
);

const Contestants = lazy(() => import('@/pages/Contestants'));
const About = lazy(() => import('@/pages/About'));
const AboutTitleholders = lazy(() => import('@/pages/AboutTitleholders'));
const AboutTeam = lazy(() => import('@/pages/AboutTeam'));
const AboutFAQ = lazy(() => import('@/pages/AboutFAQ'));
const AboutRegulations = lazy(() => import('@/pages/AboutRegulations'));
const ContactUs = lazy(() => import('@/pages/ContactUs'));

import type { ApplicantWithProfile } from '@/components/admin/ApprovalQueue';
import { exportProfilePdf, buildExportData, exportListPdf, exportAccountPdf, exportApplicationPdf, DEFAULT_EXPORT_SECTIONS } from '@/utils/exportProfilePdf';
import { canSeePhase1, canSeePhase2, canSeeMarriageInfo, canSeeSocialMedia, canPerformActions } from '@/utils/contestantFieldVisibility';
import type { ExportSections, ApplicationExportDocument } from '@/utils/exportProfilePdf';
import { resolveDisplayType } from '@/lib/application-form/review/reviewDocuments';
import { logSystemAction } from '@/components/admin/ActivityLog';
import { SystemLog, getSystemLog, deleteSystemLogEntryById } from '@/components/admin/ActivityLog';
import type { SystemLogEntry } from '@/components/admin/ActivityLog';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { IdleTimeoutManager } from '@/components/auth/IdleTimeoutManager';
import { OnboardingGate } from '@/components/application-form/onboarding';
import { TenantSuspendedGate } from '@/components/tenant/TenantSuspendedGate';
import { SupportModeBanner } from '@/components/tenant/SupportModeBanner';
// demo-tenant-environment Task 24: the home page's titleholder headshots and
// sponsor logo used to be imported here and hardcoded into the markup. They are
// the ORIGINAL CLIENT's people and partner, so they now live in that tenant's
// `marketing` configuration (`lib/tenantMarketingCopy.ts`) and reach this page
// only when the Idaho tenant resolves. The identifiers that named them
// (`mrsIdahoAmericaHeadshot`, …) are gone with them.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { createApplicant as createApplicantMutation, deleteApplicant as deleteApplicantMutation, deleteApplicantYearProfile as deleteApplicantYearProfileMutation, getProfilePhotoUploadUrl, updateProfilePhoto, removeProfilePhoto, deleteCognitoUser, createAdminUser, changeUserRole, updateUser, sendWelcomeEmail as sendWelcomeEmailMutation } from '@/graphql/mutations';
// Approval-status SSOT (approval-status-ssot-unification, Task 3): the director
// approve/reject/return flow writes the ApplicationResponse SSOT via
// `reviewApplicationResponse`, NOT the legacy AYP `approveApplication`/
// `rejectApplication`/`updateApplicantYearProfile{submissionStatus}` writes.
import { applicationFormClient } from '@/lib/application-form/client';
// Post-decision consistency (approval-status-ssot-unification, Task 6, Req 5.5):
// after a successful director decision, bump the Canonical_Approval_Reader
// invalidation bus so every approval-gating surface re-reads the Approval_SSOT
// and reflects the new status within 5s without a manual reload.
import { invalidateApprovalAccess } from '@/hooks/approvalAccessInvalidation';
import { resolveActiveCompetitionId } from '@/lib/application-form/activeCompetition';
import { personIdForCognitoSub } from '@/lib/application-form/client/callerIdentity';
import { ArrowRight, ChevronLeft, Star, Heart, Users, FileCheck, AlertCircle, AlertTriangle, Check, X, Trash2, ExternalLink, Search, Mail, Phone, MapPin, Briefcase, CheckCircle, Crown, Plus, Clock, Calendar, AtSign, FileText, Undo2, Download, Key, UserPlus, ArrowUpDown,  ClipboardList, Lock, User, Camera, Crop } from 'lucide-react';
import { LegacyAvatar, validateFileSize } from '@/components/ui/LegacyAvatar';

import { listCognitoUsers, getProfilePhotoDownloadUrl } from '@/graphql/queries';
import { CropModal } from '@/components/crop/CropModal';
import { AvatarWithEdit } from '@/components/crop/AvatarWithEdit';
import { useCropProfilePhoto } from '@/hooks/useCropProfilePhoto';
import { Toaster } from '@/components/ui/sonner';
import type { CropParams } from '@/lib/crop-constraints';
import { formatRelativeDate } from '@/utils/formatRelativeDate';
import { deriveNamePairFromUser } from '@/utils/nameFromUser';
import { buildPageantRules } from '@/data/pageantRules';
import { AFFIDAVIT_VERSION } from '@/data/affidavit';
import { EligibilityAffidavit } from '@/components/applicant/EligibilityAffidavit';
// demo-tenant-environment Task 23 (Req 2.2, 2.3): tenant-specific copy —
// division titles, org name, marketing prose — is resolved from configuration
// keyed to the active tenant instead of being written into this file.
import { useTenantCopy } from '@/hooks/useTenantCopy';
import { useDerivedApplicationSeason } from '@/hooks/useDerivedApplicationSeason';
import { divisionTitle } from '@/lib/tenantCopy';

const client = generateClient();

/** Format address as "Street\nCity, State Zip" */
function formatAddress(bg: any, fallback?: string): string {
  if (!bg) return fallback || '—';
  const street = bg.addressStreet || '';
  const cityState = [bg.addressCity, bg.addressState].filter(Boolean).join(', ');
  const line2 = [cityState, bg.addressZip].filter(Boolean).join(' ');
  if (street || line2) return [street, line2].filter(Boolean).join('\n');
  return fallback || '—';
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// SpotlightComingSoon removed — the three Spotlight routes now point at
// the real pages that already existed in the codebase but were never
// wired in: InstagramMetricsPage (admin season leaderboard),
// ProgramImpactPage (admin program-wide roll-up), and InsightsPage
// (contestant self-insights).



/**
 * RouteLoadingFallback — minimal placeholder rendered by `<Suspense>`
 * while a lazy-loaded route component's chunk downloads. Plain
 * inline-styled markup so the fallback is small enough to inline
 * itself in the eager bundle (no Tailwind class dependency).
 */
function RouteLoadingFallback() {
  return (
    <div
      data-testid="route-loading-fallback"
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span style={{ fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#646973' }}>
        Loading…
      </span>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <IdleTimeoutManager />
      <BrandingProvider>
        <Router>
          <ScrollToTop />
          <RouteErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
            {/* Public routes — wrapped with PublicLayout (TopNav + Footer) */}
            <Route path="/" element={
              <PublicLayout>
                <Home />
              </PublicLayout>
            } />
            <Route path="/about" element={
              <PublicLayout>
                <About />
              </PublicLayout>
            } />
            <Route path="/about/titleholders" element={
              <PublicLayout>
                <AboutTitleholders />
              </PublicLayout>
            } />
            <Route path="/about/team" element={
              <PublicLayout>
                <AboutTeam />
              </PublicLayout>
            } />
            <Route path="/about/faqs" element={
              <PublicLayout>
                <AboutFAQ />
              </PublicLayout>
            } />
            <Route path="/about/regulations" element={
              <PublicLayout>
                <AboutRegulations />
              </PublicLayout>
            } />
            <Route path="/about/contact" element={
              <PublicLayout>
                <ContactUs />
              </PublicLayout>
            } />
            <Route path="/contestants" element={
              <PublicLayout>
                <Contestants />
              </PublicLayout>
            } />
            <Route path="/apply" element={
              <ProtectedRoute requiredCapability="canSeePortal" redirectTo="/signup">
                <PortalLayout>
                  <OnboardingGate>
                    <ApplyHost legacyFlow={<ApplicationFlow />} />
                  </OnboardingGate>
                </PortalLayout>
              </ProtectedRoute>
            } />
            <Route path="/portal/payment/confirm" element={
              <ProtectedRoute requiredCapability="canSeePortal" redirectTo="/signin">
                <PublicLayout>
                  <PaymentConfirmation />
                </PublicLayout>
              </ProtectedRoute>
            } />
            <Route path="/signin" element={
              <PublicLayout>
                <SignInPage />
              </PublicLayout>
            } />
            <Route path="/signup" element={
              <PublicLayout>
                <SignUpPage />
              </PublicLayout>
            } />
            <Route path="/reset-password" element={
              <PublicLayout>
                <PasswordResetPage />
              </PublicLayout>
            } />
            <Route path="/profile/:id" element={
              <PublicLayout>
                <div className="pt-32 text-center font-display text-2xl italic">Public Profile</div>
              </PublicLayout>
            } />

            {/* Portal routes — nested with Sidebar layout */}
            <Route path="/portal" element={
              <ProtectedRoute requiredCapability="canSeePortal">
                {/* tenant-foundation Task 18 / Req 15.7: explain an operator hold
                    instead of rendering a portal whose every surface refuses.
                    Additive — the ProtectedRoute guard above is unchanged, and the
                    gate fails OPEN while unresolved (the server is the boundary). */}
                <TenantSuspendedGate>
                  {/* tenant-foundation Task 7.1 / Req 15.6: when an operator is
                      viewing an organization they do not belong to, say so on screen.
                      Renders nothing for ordinary members. */}
                  <SupportModeBanner />
                  <OnboardingGate>
                    <PortalLayout />
                  </OnboardingGate>
                </TenantSuspendedGate>
              </ProtectedRoute>
            }>
              <Route index element={<ApplicantDashboard />} />
              <Route path="calendar" element={<ParticipantCalendar />} />
              {/* Legacy profile surface retired — /apply is the unified profile
                  page. Redirect any /portal/profile entry (nav, notifications,
                  onboarding-nav, payment confirmation) to /apply. */}
              <Route path="profile" element={<Navigate to="/apply" replace />} />
              <Route path="documents" element={isDocumentLibraryEnabled() ? <ContestantDocumentLibrary /> : <ParticipantDocuments />} />
              {/* Guidebook retired from the contestant portal (2026-07-29,
                  operator decision). The route, its nav entry, its page title,
                  and its global-search entries are all removed so the surface is
                  neither reachable nor discoverable. `packages/shared`'s
                  canonical `GUIDEBOOK` content is deliberately untouched — the
                  mobile app-store-readiness work owns that path (Shared Seam 1).
                  `Sidebar.navSuperset` / `App.routeSuperset` pin the absence. */}
              <Route path="service-log" element={<ServiceLog />} />
              <Route path="messages" element={<MessagesPage />} />
              <Route path="insights" element={<InsightsPage />} />
              <Route path="phase2/stage-form" element={<div className="text-center py-16"><h2 className="font-display text-3xl text-foreground mb-4">Stage Form</h2><p className="text-muted-foreground font-sans">Coming Soon!</p></div>} />
              <Route path="phase2/interview-info" element={<div className="text-center py-16"><h2 className="font-display text-3xl text-foreground mb-4">Interview Info Sheet</h2><p className="text-muted-foreground font-sans">Coming Soon!</p></div>} />
              <Route path="phase2/placement-questions" element={<div className="text-center py-16"><h2 className="font-display text-3xl text-foreground mb-4">Placement Questions</h2><p className="text-muted-foreground font-sans">Coming Soon!</p></div>} />
              <Route path="settings" element={<ParticipantSettings />} />
            </Route>

            {/* Legacy routes — redirect to new portal paths */}
            <Route path="/dashboard" element={
              <ProtectedRoute requiredCapability="canSeePortal">
                <PortalLayout>
                  <ApplicantDashboard />
                </PortalLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/phase2/:formType" element={
              <ProtectedRoute requiredCapability="canSeePortal">
                <PortalLayout>
                  <div className="text-center font-display text-2xl italic pt-10">Phase 2 Form</div>
                </PortalLayout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute requiredCapability="canSeePortal">
                <Navigate to="/apply" replace />
              </ProtectedRoute>
            } />
            <Route path="/documents" element={
              <ProtectedRoute requiredCapability="canSeePortal">
                <PortalLayout>
                  {isDocumentLibraryEnabled() ? <ContestantDocumentLibrary /> : <ParticipantDocuments />}
                </PortalLayout>
              </ProtectedRoute>
            } />

            {/* Admin routes — nested with AdminSidebar layout */}
            <Route path="/admin" element={
              <ProtectedRoute requiredCapability="canJudge">
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={
                <ProtectedRoute requiredCapability="canViewAdminDashboard">
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="calendar" element={
                <ProtectedRoute requiredCapability="canJudge">
                  <AdminCalendarWrapper />
                </ProtectedRoute>
              } />
              <Route path="applications" element={<Navigate to="/admin/approvals" replace />} />
              <Route path="applications/:id" element={
                <ProtectedRoute requiredCapability="canManageUsers">
                  <AdminApplicationsPage />
                </ProtectedRoute>
              } />
              <Route path="participants" element={
                <ProtectedRoute requiredCapability="canJudge">
                  <AdminParticipantsPage />
                </ProtectedRoute>
              } />
              <Route path="documents" element={
                <ProtectedRoute requiredCapability="canManageUsers">
                  {isDocumentLibraryEnabled() ? <DirectorDocumentCenter /> : <DocumentsPage />}
                </ProtectedRoute>
              } />
              {/* Approvals surface (approval-rollup-surfaces Req 1.8, 5.4, 5.5).
                  A derived read-only Rollup_Tab is reachable ONLY when BOTH
                  gates hold: (1) the `canManageUsers` capability — enforced here
                  by `ProtectedRoute`, which denies the whole route (rendering no
                  roll-up content) when the capability is absent; and (2) the
                  Application_Form_Flag — enforced inside `SettingsPage` via
                  `useApprovalsTabs()` → `visibleApprovalsTabs(isApplicationFormEnabled(), …)`,
                  so a derived tab is only ever produced when the flag is on AND
                  the capability is held. Because the tab set gates on both,
                  `SettingsPage` clamps a direct `?tab=<derived>` request to the
                  constant `applications` tab whenever either condition is unmet,
                  rendering no roll-up content. This route-level capability guard
                  is defense-in-depth on top of the backend read authorization. */}
              <Route path="approvals" element={
                <ProtectedRoute requiredCapability="canManageUsers">
                  <SettingsPage
                    applicationsContent={
                      isApplicationFormEnabled()
                        ? <ApplicationReviewSurface />
                        : <AdminApplicationsPage embedded />
                    }
                  />
                </ProtectedRoute>
              } />
              <Route path="settings" element={
                <ProtectedRoute requiredCapability="canViewAdminDashboard">
                  <AdminSettingsPage />
                </ProtectedRoute>
              } />
              <Route path="users" element={
                <ProtectedRoute requiredCapability="canManageUsers">
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="messages" element={
                <ProtectedRoute requiredCapability="canViewAdminDashboard">
                  <MessagesPage isAdmin />
                </ProtectedRoute>
              } />
              <Route path="social-metrics" element={<Navigate to="/spotlight/social-metrics" replace />} />
              <Route path="program-impact" element={<Navigate to="/spotlight/program-impact" replace />} />
            </Route>

            {/* Spotlight portal — shares AdminLayout/AdminSidebar with the
                Director admin surface but surfaces only the audience-insights
                pages. Gated on the `canManageUsers` capability: per the
                persona-permission matrix only the admin persona reaches this
                admin surface (frontend gating is defense-in-depth — the backend
                resolvers + Cognito remain authoritative, Req 12.2). */}
            <Route path="/spotlight" element={
              <ProtectedRoute requiredCapability="canManageUsers">
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/spotlight/social-metrics" replace />} />
              <Route path="social-metrics" element={
                <ProtectedRoute requiredCapability="canManageUsers">
                  <InstagramMetricsPage />
                </ProtectedRoute>
              } />
              <Route path="program-impact" element={
                <ProtectedRoute requiredCapability="canManageUsers">
                  <ProgramImpactPage />
                </ProtectedRoute>
              } />
            </Route>

            {/* Herald — community bulletin feed (herald-feed spec, task 7.1).
                Registered unconditionally (not behind the meridian flag) so
                every authenticated persona can reach Herald per Requirement 1.1.
                The portal route serves the lifecycle personas via `canSeePortal`;
                the admin route serves the admin persona via `canManageUsers`. */}
            <Route path="/portal/herald" element={
              <ProtectedRoute requiredCapability="canSeePortal">
                <PortalLayout>
                  <HeraldPage />
                </PortalLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/herald" element={
              <ProtectedRoute requiredCapability="canManageUsers">
                <AdminLayout>
                  <HeraldPage />
                </AdminLayout>
              </ProtectedRoute>
            } />

            {/* Platform administration (tenant-foundation Task 7.1) — the
                cross-tenant operator surface. Deliberately NOT under /admin
                (which gates on `canJudge`, a tenant capability a platform
                operator need not hold); mounted as a standalone AUTHENTICATED
                route and gated to the `PlatformAdmins` group INSIDE the
                component (the resolver guard remains authoritative). */}
            <Route path="/platform-admin" element={
              <ProtectedRoute>
                <PlatformAdmin />
              </ProtectedRoute>
            } />

            {/* Self-service tenant join (tenant-foundation Task 6) — authenticated;
                the caller appends their OWN Membership via a join link
                (/join?tenant=<id>&role=<role>). Adults join themselves here; a
                child is enrolled by their guardian at /parent-signup. */}
            <Route path="/join" element={
              <ProtectedRoute>
                <JoinTenant />
              </ProtectedRoute>
            } />
            {/* Parent/guardian child enrollment (tenant-foundation Task 16) —
                authenticated: the signed-in adult IS the guardian (the server takes
                the guardian from the token, so this page has no "who is the
                guardian" field to spoof). The age gate, the consent requirement and
                the PENDING state are all server decisions. */}
            <Route path="/parent-signup" element={
              <ProtectedRoute>
                <ParentEnrollment />
              </ProtectedRoute>
            } />
            {/* Guardian management (tenant-foundation Task 12 + 16) — the caller's OWN
                family view. Without it a guardian who left mid-enrollment had no way
                back: consent starts PENDING and the signed form is returned later, so
                a child could sit PENDING forever. Also where consent is withdrawn.
                SELF-ONLY: parentHub takes no arguments. */}
            <Route path="/parent" element={
              <ProtectedRoute>
                <ParentHub />
              </ProtectedRoute>
            } />

            {/* Design reference (dev/staging only) — gated so prod never mounts it */}
            {import.meta.env.VITE_STAGE !== 'prod' ? (
              <Route path="/design-reference" element={<DesignReferencePage />} />
            ) : null}

            {/* Meridian primitives preview (dev/staging only) — PR (m.6) Phase 1 check-in canvas. */}
            {import.meta.env.VITE_STAGE !== 'prod' ? (
              <Route path="/dev/m6-preview" element={<MeridianPrimitivesPreviewPage />} />
            ) : null}

            {/* Dashboard refresh preview (dev/staging only) — PR (m.7) check-in canvas. */}
            {import.meta.env.VITE_STAGE !== 'prod' ? (
              <Route path="/dev/dashboards-preview" element={<DashboardsPreviewPage />} />
            ) : null}

            {/* Podium design preview (dev/staging only) — Stream Chat-free
                visual reference for the messaging surface so reviewers
                can inspect the design without API keys. */}
            {import.meta.env.VITE_STAGE !== 'prod' ? (
              <Route path="/dev/podium-preview" element={<PodiumPreviewPage />} />
            ) : null}

            {/* Application Form mock harness (dev/staging only) — click through
                all five surfaces (onboarding, builder, applicant, review, judge)
                against the in-memory mock client, no backend required. */}
            {import.meta.env.VITE_STAGE !== 'prod' ? (
              <Route path="/dev/application-form" element={<ApplicationFormDevRoute />} />
            ) : null}

            {/* Document Library mock harness (dev/staging only) — browse the
                director Document Center + manage library documents, and view
                the contestant shared set, against the in-memory mock client
                with no backend required. */}
            {import.meta.env.VITE_STAGE !== 'prod' ? (
              <Route path="/dev/document-library" element={<DocumentLibraryDevRoute />} />
            ) : null}

            {/* Meridian flag-gated routes (PR (h) — task 8.8) */}
            {isMeridianNewScreensEnabled() && (
              <>
                <Route path="/portal/envoy" element={
                  <ProtectedRoute requiredCapability="canSeePortal">
                    <PortalLayout>
                      <EnvoyPortalRoute />
                    </PortalLayout>
                  </ProtectedRoute>
                } />
                <Route path="/portal/vault" element={
                  <ProtectedRoute requiredCapability="canSeePortal">
                    <PortalLayout>
                      <VaultPortalRoute />
                    </PortalLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/envoy" element={
                  <ProtectedRoute requiredCapability="canManageUsers">
                    <AdminLayout>
                      <EnvoyAdminRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/patron" element={
                  <ProtectedRoute requiredCapability="canManageUsers">
                    <AdminLayout>
                      <PatronRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                {/* Sponsor-facing Patron surface (own agreement) — task 14.4.
                    GATE LOGIC NOW; portal UI deferred. The sponsor-persona gate
                    (admin → all sponsors, sponsor → own agreement, Req 11.5) is
                    enforced inside <PatronSponsorRoute /> via the canonical
                    persona-permission matrix evaluator, because "sponsor" is a
                    contact facet + the Patron product permission, never a Role
                    or Cognito-group capability — so it cannot be expressed by a
                    `requiredCapability` route guard. The component renders only
                    a deferred-UI placeholder for permitted personas and
                    redirects everyone else (defense-in-depth, Req 12.2). */}
                <Route path="/portal/patron" element={<PatronSponsorRoute />} />
                <Route path="/admin/vault" element={
                  <ProtectedRoute requiredCapability="canManageUsers">
                    <AdminLayout>
                      <VaultAdminRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
              </>
            )}

            {/* Community Identity flag-gated route (community-identity spec,
                task 6.1). Gated behind `isCommunityEnabled()` so the
                Person-centric directory ships to prod independently
                (Requirements 10.1, 10.2). Gated on `canViewAdminDashboard`
                (the admin persona — the Director admin surface). */}
            {isCommunityEnabled() && (
              <>
                {/* Tenant-admin parental-consent roster (tenant-foundation Task 17).
                    The first org-facing surface that reads Membership per tenant, so
                    the first place a guardian-enrolled minor is visible at all — the
                    resolver is Admins-gated and the Lambda redacts a child's identity
                    until consent is GRANTED, surfacing the guardian's contact instead. */}
                <Route path="/admin/consent" element={
                  <ProtectedRoute requiredCapability="canViewAdminDashboard">
                    <AdminLayout>
                      <ConsentAdminRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/community" element={
                  <ProtectedRoute requiredCapability="canViewAdminDashboard">
                    <AdminLayout>
                      <CommunityAdminRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                {/* Community Records (Phase 2) — relational record page
                    (community-records spec, task 8.6). Same flag, same
                    `canViewAdminDashboard` gate (admin persona), same
                    AdminLayout (Requirements 1.2, 1.3, 1.4). */}
                <Route path="/admin/community/:personId" element={
                  <ProtectedRoute requiredCapability="canViewAdminDashboard">
                    <AdminLayout>
                      <PersonRecordRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
              </>
            )}

            {/* Merit flag-gated routes — decoupled from the meridian
                flag (PR (h) originally bundled these; merit-only flag
                lets Merit ship to prod without Envoy/Patron/Vault). */}
            {isMeritEnabled() && (
              <>
                {/* Merit judging rebuild — admin surfaces (gated on `canManageUsers`). */}
                <Route path="/admin/merit" element={
                  <ProtectedRoute requiredCapability="canManageUsers">
                    <AdminLayout>
                      <MeritSetupRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/merit/monitor" element={
                  <ProtectedRoute requiredCapability="canManageUsers">
                    <AdminLayout>
                      <MeritMonitorRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                {/* Merit web judging — the Cognito judge surface (merit-web-judging
                    R1, R4). Gated on `canJudge` (admin/poweruser/judge — the
                    coarse defense-in-depth superset; the MeritEntryGate makes the
                    precise admin→setup-redirect / judge→shell / none branch).
                    Wrapped in AdminLayout so the left nav persists on the judge
                    view. Replaces the retired 4-digit-access-code Judge PWA (R2). */}
                <Route path="/merit/judge" element={
                  <ProtectedRoute requiredCapability="canJudge">
                    <AdminLayout>
                      <MeritJudgeRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
              </>
            )}

            {/* Application Form flag-gated routes (application-form-builder) —
                the real director Form_Builder + review/approval admin surfaces.
                Gated behind `isApplicationFormEnabled()` (OFF by default) so a
                deploy is inert until the backend stack is live and the flag is
                flipped. Both are admin-only (`canManageUsers`) and wrapped in
                AdminLayout, mirroring the other flag-gated admin surfaces. */}
            {isApplicationFormEnabled() && (
              <>
                <Route path="/admin/application-builder" element={
                  <ProtectedRoute requiredCapability="canManageUsers">
                    <AdminLayout>
                      <ApplicationBuilderAdminRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/application-review" element={
                  <ProtectedRoute requiredCapability="canManageUsers">
                    <AdminLayout>
                      <ApplicationReviewAdminRoute />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
              </>
            )}

            {/* tenant-foundation Task 18 — tenant reach (`/<slug>`). Mounted LAST,
                immediately before the 404, so it only sees a single path segment
                that matched NO real route above. Slugs can never equal a route
                (reserved-slug denylist + the CI guardrail that keeps that list in
                sync with this file), so this cannot shadow app routing. Reach is
                onboarding SUGAR: it redirects to sign-in (carrying the slug) or to
                the join prefill, and grants no access. */}
            <Route path="/:tenantSlug" element={
              <PublicLayout>
                <TenantReachLanding />
              </PublicLayout>
            } />

            {/* 404 */}
            <Route path="*" element={
              <PublicLayout>
                <div className="pt-32 pb-32 text-center font-display text-2xl italic">
                  Page Not Found
                </div>
              </PublicLayout>
            } />
            </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </Router>
        <Toaster />
      </BrandingProvider>
    </AuthProvider>
  );
}

// ─── Home Page ──────────────────────────────────────────────────────────────

function Home() {
  // demo-tenant-environment Task 23 (Req 2.2, 2.3): the legacy stat tiles are the
  // active tenant's copy, resolved from configuration. A tenant with no legacy
  // prose contributes NO tiles — absence rather than invented marketing copy.
  //
  // Task 24 (Req 13.2, 13.3) extends that to the rest of this page: the hero
  // tagline and established line, the current titleholders (names, titles,
  // platforms, and PHOTOS), the featured sponsor, and the impact paragraph all
  // come from `marketing`. Every one of them was a literal naming the original
  // client, and the headshots were three `import`s of the client's titleholders.
  const tenantCopy = useTenantCopy();
  const marketing = tenantCopy.marketing;

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center overflow-hidden bg-foreground">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&q=80&w=2000"
            alt="Pageant Stage"
            className="w-full h-full object-cover opacity-40"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-2xl">
            {marketing.tagline && (
              <span className="font-sans text-xs uppercase tracking-[0.5em] text-primary mb-6 block">{marketing.tagline}</span>
            )}
            <h1 className="text-6xl md:text-8xl font-display text-background leading-[0.9] mb-8">
              Leadership, <br />
              <span className="font-display italic text-primary">Poise &amp; Purpose</span>
            </h1>
            <p className="text-background/80 font-body text-lg mb-10 max-w-lg leading-relaxed">
              Empowering a community of women with inner beauty, those who are dedicated to community service, and who choose to use the crown for a purpose.
            </p>
            <div className="flex flex-col sm:flex-row gap-6">
              <Link
                to="/apply"
                className="bg-primary text-primary-foreground px-10 py-4 rounded-input font-sans text-xs uppercase tracking-[0.2em] font-bold hover:bg-primary/90 transition-all duration-300 flex items-center justify-center gap-3 group"
              >
                Apply Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/about"
                className="border border-primary text-primary px-10 py-4 font-sans text-xs uppercase tracking-[0.2em] hover:bg-primary hover:text-primary-foreground transition-all duration-300 flex items-center justify-center"
              >
                Our Pageants
              </Link>
            </div>
          </div>
        </div>

        {marketing.establishedLine && (
          <div className="absolute right-12 bottom-12 hidden lg:block">
            <div className="vertical-text text-primary text-[10px] opacity-50">
              {marketing.establishedLine}
            </div>
          </div>
        )}
      </section>

      {/* Public countdown strip — normal-flow sibling below the hero,
          above the hero's z-0 background/gradient (public-site-countdown-banner
          spec, task 6.1; Requirements 1.1, 5.1, 5.2, 5.3, 5.4). */}
      <PublicCountdownBanner />

      {/* Current Titleholders Section */}
      <section className="py-32 bg-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <span className="font-sans text-xs uppercase tracking-[0.4em] text-muted-foreground mb-4 block">The Reigning Royalty</span>
            <h2 className="text-4xl md:text-5xl font-display text-foreground">Current Titleholders</h2>
            <div className="w-24 h-[1px] bg-primary mx-auto mt-6" />
          </div>

          {marketing.titleholders.length === 0 ? (
            <p
              data-testid="home-titleholders-empty"
              className="text-center text-muted-foreground font-body text-base"
            >
              Titleholders for the current season have not been announced yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20">
              {marketing.titleholders.map((queen) => (
                <Link key={queen.slug} to={`/about/titleholders#${queen.slug}`} className="group cursor-pointer no-underline">
                  {queen.image && (
                    <div className="relative mb-8 overflow-hidden">
                      <div className="aspect-[3/4] overflow-hidden rounded-t-[160px] border border-border">
                        <img
                          src={queen.image}
                          alt={queen.name}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-105 group-hover:scale-100"
                        />
                      </div>
                      <div className="absolute inset-0 bg-foreground/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-[160px]" />
                    </div>
                  )}
                  <div className="text-center">
                    <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-primary block mb-2">
                      {queen.title} {queen.year}
                    </span>
                    <h3 className="text-3xl font-display mb-4 text-foreground">{queen.name}</h3>
                    <p className="font-body text-sm text-muted-foreground italic max-w-[240px] mx-auto leading-relaxed">
                      &ldquo;{queen.platform}&rdquo;
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Sponsor Highlight Section — the tenant's own partner, or no section at
          all. A tenant with no sponsor must not advertise somebody else's. */}
      {marketing.sponsor && (
        <section className="py-24 bg-muted" data-testid="home-sponsor">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <span className="font-sans text-xs uppercase tracking-[0.4em] text-muted-foreground mb-4 block">Our Partners</span>
              <h2 className="text-4xl md:text-5xl font-display mb-4 text-foreground">Sponsor Highlight</h2>
              <div className="w-24 h-[1px] bg-primary mx-auto" />
            </div>

            <a href={marketing.sponsor.url} target="_blank" rel="noopener noreferrer" className="block bg-card border border-border rounded-card p-12 md:p-16 flex flex-col md:flex-row items-center gap-12 md:gap-20 hover:shadow-lg hover:border-primary transition-all cursor-pointer">
              <div className="flex-1 text-center md:text-left">
                <p className="text-foreground font-body text-lg leading-relaxed">
                  <span className="font-display text-4xl text-foreground block mb-4">{marketing.sponsor.name}</span>
                  {marketing.sponsor.credit}
                </p>
              </div>
              <div className="flex-shrink-0">
                <img
                  src={marketing.sponsor.image}
                  alt={marketing.sponsor.imageAlt}
                  className="w-48 md:w-64 h-auto object-contain"
                />
              </div>
            </a>
          </div>
        </section>
      )}

      {/* Impact Section */}
      <section className="py-32 bg-foreground text-background overflow-hidden relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&q=80&w=2000"
            alt="Background"
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <span className="font-sans text-xs uppercase tracking-[0.4em] text-primary mb-6 block">Our Impact</span>
              <h2 className="text-5xl font-display mb-8 leading-tight ">More Than Just <br />A Crown</h2>
              {marketing.impactDescription && (
                <p className="text-background/80 font-body text-lg mb-8 leading-relaxed">
                  {marketing.impactDescription}
                </p>
              )}
              <Link
                to="/about"
                className="inline-flex items-center gap-4 font-sans text-xs uppercase tracking-[0.2em] font-bold border-b border-primary pb-2 hover:text-primary hover:border-primary transition-all"
              >
                Learn About Our Pageants <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {/* Legacy stat tiles — the active tenant's own copy (Task 23,
                Req 2.2). Alternating dark/accent treatment is derived from the
                index so the grid stays balanced for any tile count, and a tenant
                with no legacy prose renders no grid at all (Req 2.3: neutral
                means absence, not another tenant's history). */}
            {tenantCopy.legacyHighlights.length > 0 && (
              <div className="hidden lg:block">
                <div className="grid grid-cols-2 gap-4" data-testid="home-legacy-highlights">
                  {tenantCopy.legacyHighlights.map((highlight, idx) => {
                    const onAccent = idx % 2 === 1;
                    return (
                      <div
                        key={highlight.label}
                        className={`relative group/tip aspect-square border p-8 flex flex-col justify-center text-center cursor-default ${
                          onAccent ? 'bg-primary border-primary' : 'bg-foreground/80 border-border'
                        }`}
                      >
                        <span
                          className={`text-4xl font-display italic mb-2 ${
                            onAccent ? 'text-primary-foreground' : 'text-primary'
                          }`}
                        >
                          {highlight.value}
                        </span>
                        <span
                          className={`text-eyebrow-sm ${
                            onAccent ? 'text-primary-foreground' : 'text-background/80'
                          }`}
                        >
                          {highlight.label}
                        </span>
                        <span
                          className={`absolute inset-0 hidden group-hover/tip:flex items-center justify-center p-6 transition-all z-10 ${
                            onAccent ? 'bg-primary/95' : 'bg-foreground/95'
                          }`}
                        >
                          <span
                            className={`font-body text-xs leading-relaxed text-center ${
                              onAccent ? 'text-primary-foreground' : 'text-background'
                            }`}
                          >
                            {highlight.detail}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── Admin Settings Page ─────────────────────────────────────────────────────

/** Settings tab IDs permitted per role (Requirements: 11.1, 11.2, 11.3) */
export const ROLE_SETTINGS_TABS: Record<RoleType, Array<'profile' | 'users' | 'log' | 'email-templates' | 'payments' | 'divisions' | 'titles' | 'seasons' | 'documents' | 'forms'>> = {
  // 'divisions' is the Division Registry management surface (division-registry
  // spec, task 11.1). 'seasons' is the canonical Season management surface
  // (season-ssot spec, task 6). 'documents' is the director Document Center
  // (Library + Season browse), relocated here from the top-level Director nav.
  // 'forms' is the Application Form builder, relocated here from the top-level
  // Director nav (flag-gated by isApplicationFormEnabled()). All Admin-only,
  // nested here rather than added as a sidebar nav item (Option B — keeps the
  // AdminSidebar route-consistency contract untouched).
  admin: ['profile', 'users', 'log', 'email-templates', 'payments', 'divisions', 'titles', 'seasons', 'documents', 'forms'],
  poweruser: ['profile'],
  judge: ['profile'],
  contestant: [],
};

function AdminSettingsPage() {
  const { user: authUser, role } = useAuth();
  // Season SSOT: operative competition year from the active-season seam.
  const { operativeYear } = useActiveSeason();
  const tenantCopy = useTenantCopy();

  // Role-aware settings subpage filtering (Requirements: 11.1, 11.2, 11.3, 11.4, 11.5)
  const allowedTabs = ROLE_SETTINGS_TABS[role] || ['profile'];

  // Protected accounts — cannot be deleted or have their role changed
  const PROTECTED_EMAILS = ['bensontanner@live.com', 'timi.marsh3@gmail.com'];
  const isProtectedUser = (email: string) => PROTECTED_EMAILS.includes(email.toLowerCase());

  // Change password dialog
  const [showPwDialog, setShowPwDialog] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pwLoading, setPwLoading] = useState(false);

  // Create user dialog
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserRole, setNewUserRole] = useState<'mrs-contestant' | 'miss-contestant' | 'admin' | 'poweruser' | 'judge'>('mrs-contestant');
  const [createUserMsg, setCreateUserMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [createUserLoading, setCreateUserLoading] = useState(false);

  // Export log dialog
  const [showExportLog, setShowExportLog] = useState(false);
  const [exportFilters, setExportFilters] = useState<Record<string, boolean>>({ APPROVE: true, REJECT: true, DELETE_APPLICATION: true, DELETE_PARTICIPANT: true, LOCK_PROFILE: true, RETURN_APPLICATION: true });

  // User management state
  interface UserEntry { id: string; fullName: string; email: string; createdAt?: string; lastLoginAt?: string; role?: string; submissionStatus?: string; profilePhotoS3Key?: string }
  const [users, setUsers] = useState<UserEntry[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userSortField, setUserSortField] = useState<'name' | 'role' | 'status' | 'created'>('name');
  const [userSortDir, setUserSortDir] = useState<'asc' | 'desc'>('asc');
  const [deleteTarget, setDeleteTarget] = useState<UserEntry | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [userDeleting, setUserDeleting] = useState(false);

  // Edit user dialog state
  const [editUser, setEditUser] = useState<UserEntry | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Undo state for system log
  const [undoTarget, setUndoTarget] = useState<SystemLogEntry | null>(null);
  const [undoProcessing, setUndoProcessing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Fetch applicants from DynamoDB. The avatar is sourced from the
        // User table (the auth/display projection of the Person SSOT); the
        // legacy Applicant.profilePhotoS3Key duplicate was removed
        // (meridian-data-model task 12.1).
        const result = await client.graphql({ query: `query { listApplicants(limit: 200) { items { id fullName email lastLoginAt createdAt } } }`, authMode: 'userPool' }) as GraphQLResult<{ listApplicants: { items: Array<{ id: string; fullName: string; email: string; lastLoginAt?: string; createdAt?: string }> } }>;
        const apps = result.data?.listApplicants?.items || [];
        // Enrich with profile data
        const enriched: UserEntry[] = await Promise.all(apps.map(async (app) => {
          let role = 'Contestant';
          let submissionStatus = '—';
          try {
            const pr = await client.graphql({ query: `query($aid: ID!) { listApplicantYearProfiles(applicantId: $aid) { competitionYear applicantType submissionStatus } }`, variables: { aid: app.id }, authMode: 'userPool' }) as GraphQLResult<{ listApplicantYearProfiles: Array<{ competitionYear: number; applicantType: string; submissionStatus: string }> }>;
            const profile = pr.data?.listApplicantYearProfiles?.find((p: { competitionYear: number }) => p.competitionYear === operativeYear);
            if (profile) {
              submissionStatus = profile.submissionStatus || '—';
              role = profile.applicantType === 'MRS' ? 'Mrs. Contestant' : profile.applicantType === 'MISS' ? 'Miss Contestant' : 'Contestant';
            }
          } catch {}
          return { ...app, role, submissionStatus, lastLoginAt: app.lastLoginAt };
        }));

        // Fetch Admins, PowerUsers, and Judges from Cognito
        let nonApplicantEntries: UserEntry[] = [];
        const nonApplicantEmails = new Set<string>();
        try {
          const groupConfigs = [
            { groupName: 'Admins', role: 'Admin' },
            { groupName: 'PowerUsers', role: 'Power User' },
            { groupName: 'Judges', role: 'Judge' },
          ];
          const cognitoResults = await Promise.all(
            groupConfigs.map(({ groupName }) =>
              client.graphql({
                query: listCognitoUsers,
                variables: { groupName },
                authMode: 'userPool',
              }) as Promise<GraphQLResult<{ listCognitoUsers: Array<{ email: string; fullName: string | null; status: string; createdAt: string | null }> }>>
            )
          );
          const adminPhotoKeys = new Map<string, string>();
          const emailToRole = new Map<string, string>();
          cognitoResults.forEach((result, idx) => {
            const { role } = groupConfigs[idx];
            const users = result.data?.listCognitoUsers || [];
            users.forEach(d => {
              nonApplicantEmails.add(d.email.toLowerCase());
              // First group wins (Admins > PowerUsers > Judges)
              if (!emailToRole.has(d.email.toLowerCase())) {
                emailToRole.set(d.email.toLowerCase(), role);
              }
            });
          });

          // Pull photo keys from the User table (the single source of
          // truth per avatar-ssot-cleanup). The Cognito
          // `profilePhotoS3Key` field is deprecated and no longer
          // driving avatar rendering.
          try {
            const usersResult = await client.graphql({
              query: `query { listUsers(limit: 1000) { items { email profilePhotoS3Key } } }`,
              authMode: 'userPool',
            }) as GraphQLResult<{ listUsers: { items: Array<{ email: string; profilePhotoS3Key?: string | null }> } }>;
            const rows = usersResult.data?.listUsers?.items ?? [];
            for (const u of rows) {
              if (u.email && u.profilePhotoS3Key) {
                adminPhotoKeys.set(u.email.toLowerCase(), u.profilePhotoS3Key);
              }
            }
          } catch (uErr) {
            console.warn('Failed to fetch User-table photos for admin list:', uErr);
          }
          const applicantEmails = new Set(enriched.map(u => u.email.toLowerCase()));
          // Build entries for non-applicant Cognito users
          cognitoResults.forEach((result, idx) => {
            const { role } = groupConfigs[idx];
            const users = result.data?.listCognitoUsers || [];
            users.forEach(d => {
              if (!applicantEmails.has(d.email.toLowerCase()) && emailToRole.get(d.email.toLowerCase()) === role) {
                nonApplicantEntries.push({
                  id: `cognito-${d.email}`,
                  fullName: d.fullName || d.email,
                  email: d.email,
                  createdAt: d.createdAt || undefined,
                  role,
                  submissionStatus: d.status === 'CONFIRMED' ? 'Active' : d.status || '—',
                  // Photo comes from User table (avatar-ssot-cleanup),
                  // not from the deprecated Cognito field.
                  profilePhotoS3Key: adminPhotoKeys.get(d.email.toLowerCase()),
                });
              }
            });
          });

          // Merge roles and photo keys (from the User table) into applicant
          // entries. The avatar always comes from the User-table overlay now
          // that the legacy Applicant.profilePhotoS3Key duplicate is gone
          // (meridian-data-model task 12.1).
          for (const u of enriched) {
            const role = emailToRole.get(u.email.toLowerCase());
            if (role) {
              u.role = role;
            }
            if (!u.profilePhotoS3Key) {
              u.profilePhotoS3Key = adminPhotoKeys.get(u.email.toLowerCase()) || undefined;
            }
          }
        } catch (err) {
          console.error('Failed to fetch Cognito groups:', err);
        }

        const allUsers = [...nonApplicantEntries, ...enriched];
        setUsers(allUsers);
      } catch {} finally { setUsersLoading(false); }
    })();
  }, [operativeYear]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setPwMsg({ type: 'error', text: 'Passwords do not match.' }); return; }
    setPwLoading(true); setPwMsg(null);
    try {
      const { updatePassword } = await import('aws-amplify/auth');
      await updatePassword({ oldPassword, newPassword });
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
      setTimeout(() => { setShowPwDialog(false); setPwMsg(null); }, 1500);
    } catch (err: any) { setPwMsg({ type: 'error', text: err?.message || 'Failed.' }); }
    finally { setPwLoading(false); }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserEmail || !newUserFirstName || !newUserLastName) {
      setCreateUserMsg({ type: 'error', text: 'Email, first name, and last name are required.' });
      return;
    }
    setCreateUserLoading(true); setCreateUserMsg(null);
    try {
      // Determine Cognito group based on role
      const groupName = (newUserRole === 'mrs-contestant' || newUserRole === 'miss-contestant') ? 'Applicants'
        : newUserRole === 'admin' ? 'Admins'
        : newUserRole === 'poweruser' ? 'PowerUsers'
        : 'Judges';

      const trimmedFirstName = newUserFirstName.trim();
      const trimmedLastName = newUserLastName.trim();
      const composedFullName = `${trimmedFirstName} ${trimmedLastName}`.trim();

      // Create Cognito user via AdminCreateUser (temp password emailed
      // by Cognito). Per the structured-name-identity spec, this
      // mutation expects firstName + lastName (not fullName).
      const adminResult = await client.graphql({
        query: createAdminUser,
        variables: {
          email: newUserEmail,
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          groupName,
        },
        authMode: 'userPool',
      }) as GraphQLResult<{ createAdminUser: { success: boolean; message: string } }>;

      const adminData = adminResult.data?.createAdminUser;
      if (adminData && !adminData.success) {
        setCreateUserMsg({ type: 'error', text: adminData.message });
        setCreateUserLoading(false);
        return;
      }

      // Channel auto-enrollment has moved to Stream Chat: admins are auto-added
      // via `autoIncludeGroups` on Stream channels during the Post-Confirmation
      // trigger (see .kiro/specs/stream-chat-messaging-migration/). Nothing to
      // do client-side here.

      // Create applicant record and year profile only for contestant roles
      if (newUserRole === 'mrs-contestant' || newUserRole === 'miss-contestant') {
        const createResult = await client.graphql({ query: createApplicantMutation, variables: { input: { firstName: trimmedFirstName, lastName: trimmedLastName, email: newUserEmail } }, authMode: 'userPool' }) as GraphQLResult<{ createApplicant: { id: string } }>;
        const newAppId = createResult.data?.createApplicant?.id;

        if (newAppId) {
          const applicantType = newUserRole === 'mrs-contestant' ? 'MRS' : 'MISS';
          try {
            await client.graphql({
              query: `mutation($aid: ID!, $yr: Int!, $at: ApplicantType!) { createApplicantYearProfile(applicantId: $aid, competitionYear: $yr, applicantType: $at) { applicantId } }`,
              variables: { aid: newAppId, yr: operativeYear, at: applicantType },
              authMode: 'userPool',
            });
          } catch {}
        }
      }

      setCreateUserMsg({ type: 'success', text: `User ${composedFullName} created. A temporary password has been sent to ${newUserEmail}.` });
      // Refresh user list
      const result = await client.graphql({ query: `query { listApplicants(limit: 200) { items { id fullName email createdAt } } }`, authMode: 'userPool' }) as GraphQLResult<{ listApplicants: { items: Array<{ id: string; fullName: string; email: string; createdAt?: string }> } }>;
      const apps = result.data?.listApplicants?.items || [];
      setUsers(apps.map(a => ({ ...a, role: 'Contestant', submissionStatus: '—' })));
      setNewUserEmail('');
      setNewUserFirstName('');
      setNewUserLastName('');
      setNewUserRole('mrs-contestant');
      setTimeout(() => { setShowCreateUser(false); setCreateUserMsg(null); }, 1500);
    } catch (err: any) { setCreateUserMsg({ type: 'error', text: err?.message || 'Failed to create user.' }); }
    finally { setCreateUserLoading(false); }
  };

  const handleExportLog = async () => {
    const entries = await getSystemLog();
    const selectedTypes = Object.entries(exportFilters).filter(([, v]) => v).map(([k]) => k);
    const filtered = entries.filter(e => selectedTypes.includes(e.action));
    if (filtered.length === 0) { return; }
    const actionLabels: Record<string, string> = { DELETE_APPLICATION: 'Deleted Application', DELETE_PARTICIPANT: 'Deleted Contestant', APPROVE: 'Approved', REJECT: 'Rejected', LOCK_PROFILE: 'Locked Profile', RETURN_APPLICATION: 'Returned to Applicant' };
    const csvRows = ['Timestamp,Action,Performed By,Target Name,Target Email,Details'];
    filtered.forEach(e => {
      csvRows.push(`"${new Date(e.timestamp).toLocaleString()}","${actionLabels[e.action] || e.action}","${e.performedBy}","${e.targetName}","${e.targetEmail}","${(e.details || '').replace(/"/g, '""')}"`);
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `system-log-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    setShowExportLog(false);
  };

  const handleUndoAction = async () => {
    if (!undoTarget) return;
    setUndoProcessing(true);
    try {
      // Log the undo action
      logSystemAction({ action: 'RETURN_APPLICATION', performedBy: authUser?.fullName || authUser?.email || 'Admin', targetName: undoTarget.targetName, targetEmail: undoTarget.targetEmail, targetId: undoTarget.targetId, details: `Undo: ${undoTarget.action} — reversed by admin` });
      // Remove the original entry from DynamoDB
      await deleteSystemLogEntryById(undoTarget.id);
      setUndoTarget(null);
      setLogKey(k => k + 1); // Force SystemLog to re-render
    } catch (err) { console.error('Undo failed:', err); }
    finally { setUndoProcessing(false); }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget || deleteConfirmText !== 'Delete this user') return;
    if (isProtectedUser(deleteTarget.email)) return;
    setUserDeleting(true);
    try {
      // Delete year profiles
      const profiles = await client.graphql({ query: `query($aid: ID!) { listApplicantYearProfiles(applicantId: $aid) { competitionYear } }`, variables: { aid: deleteTarget.id }, authMode: 'userPool' }) as GraphQLResult<{ listApplicantYearProfiles: Array<{ competitionYear: number }> }>;
      for (const p of (profiles.data?.listApplicantYearProfiles || [])) {
        try { await client.graphql({ query: deleteApplicantYearProfileMutation, variables: { applicantId: deleteTarget.id, competitionYear: p.competitionYear }, authMode: 'userPool' }); } catch {}
      }
      // Delete Applicant record (only if it's not a director-only entry)
      if (!deleteTarget.id.startsWith('director-')) {
        try { await client.graphql({ query: deleteApplicantMutation, variables: { id: deleteTarget.id }, authMode: 'userPool' }); } catch {}
      }
      // Delete the Cognito user so the email can be reused
      try { await client.graphql({ query: deleteCognitoUser, variables: { email: deleteTarget.email } }); } catch {}

      // Stream Chat channel membership cleanup on user delete is handled by
      // Stream's server-side user deactivation (deleteUser API) — not by
      // mutating channels one-by-one from the client. For now this path is
      // left to the admin's manual cleanup in the Stream dashboard; a future
      // task may wire `streamClient.deleteUser(cognitoSub)` into this flow.

      logSystemAction({ action: 'DELETE_PARTICIPANT', performedBy: authUser?.fullName || authUser?.email || 'Admin', targetName: deleteTarget.fullName, targetEmail: deleteTarget.email, targetId: deleteTarget.id });
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
      setDeleteTarget(null); setDeleteConfirmText('');
    } catch (err) { console.error('Delete failed:', err); }
    finally { setUserDeleting(false); }
  };

  // System log key to force re-render after undo
  const [logKey, setLogKey] = useState(0);
  // Settings tab state — default to 'profile' (My Profile tab)
  const [settingsTab, setSettingsTab] = useState<'profile' | 'users' | 'log' | 'email-templates' | 'payments' | 'divisions' | 'titles' | 'seasons' | 'documents' | 'forms'>('profile');

  // Redirect to first allowed tab if current tab is not permitted for this role (Req 11.4, 11.5)
  useEffect(() => {
    if (allowedTabs.length > 0 && !allowedTabs.includes(settingsTab)) {
      setSettingsTab(allowedTabs[0]);
    }
  }, [role, settingsTab, allowedTabs]);

  // My Profile tab state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profilePhotoS3Key, setProfilePhotoS3Key] = useState<string | null>(null);
  const [profileApplicantId, setProfileApplicantId] = useState<string | null>(null);
  const [profileMemberSince, setProfileMemberSince] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadMsg, setUploadMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [exportingAccount, setExportingAccount] = useState(false);

  // Crop modal state for My Profile tab (avatar-crop feature)
  const [settingsCropModalOpen, setSettingsCropModalOpen] = useState(false);
  const [settingsCropImageUrl, setSettingsCropImageUrl] = useState<string>('');
  const { cropPhoto: settingsCropPhoto } = useCropProfilePhoto();

  // Fetch current user's profile photo on mount
  useEffect(() => {
    if (!authUser?.email) { setProfileLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        // User table is the single source of truth for profile photos
        // (avatar-ssot-cleanup). Query both the User row (for the photo
        // key) and the Applicant row (for the applicantId used by the
        // upload-photo flow). Applicant.profilePhotoS3Key is deprecated
        // and no longer consulted here.
        const [userResult, applicantResult] = await Promise.all([
          client.graphql({
            query: `query { listUsers(limit: 1000) { items { email profilePhotoS3Key createdAt } } }`,
            authMode: 'userPool',
          }) as Promise<GraphQLResult<{ listUsers: { items: Array<{ email: string; profilePhotoS3Key?: string | null; createdAt?: string | null }> } }>>,
          client.graphql({
            query: `query { listApplicants(limit: 200) { items { id email } } }`,
            authMode: 'userPool',
          }) as Promise<GraphQLResult<{ listApplicants: { items: Array<{ id: string; email: string }> } }>>,
        ]);
        if (cancelled) return;
        const userMatch = userResult.data?.listUsers?.items?.find(
          (u) => u.email?.toLowerCase() === authUser.email?.toLowerCase(),
        );
        if (userMatch?.profilePhotoS3Key) {
          setProfilePhotoS3Key(userMatch.profilePhotoS3Key);
        }
        if (userMatch?.createdAt) {
          setProfileMemberSince(userMatch.createdAt);
        }
        const applicantMatch = applicantResult.data?.listApplicants?.items?.find(
          (a) => a.email === authUser.email,
        );
        if (applicantMatch) setProfileApplicantId(applicantMatch.id);
      } catch (err) {
        console.error('Failed to fetch profile photo:', err);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authUser?.email]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadMsg(null);

    if (!validateFileSize(file.size)) {
      setUploadMsg({ type: 'error', text: 'File must be smaller than 5 MB.' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleCancelPreview = () => {
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadMsg(null);
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile || !authUser) return;
    setUploading(true);
    setUploadMsg(null);
    try {
      // Use applicantId if available, otherwise use cognitoSub for admins
      const userId = profileApplicantId || `director-${authUser.cognitoSub}`;

      // 1. Get pre-signed upload URL
      const urlResult = await client.graphql({
        query: getProfilePhotoUploadUrl,
        variables: { userId, contentType: selectedFile.type },
      }) as GraphQLResult<{ getProfilePhotoUploadUrl: { uploadUrl: string; s3Key: string; expiresIn: number } }>;

      const { uploadUrl, s3Key } = urlResult.data!.getProfilePhotoUploadUrl;

      // 2. Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': selectedFile.type },
        body: selectedFile,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      // 3. Save S3 key to User table (primary) and Applicant table (backward compatibility)
      // Requirement 10.1, 10.2, 10.4: Write to User table, stop writing to Cognito custom attributes
      await client.graphql({
        query: updateUser,
        variables: { input: { cognitoSub: authUser.cognitoSub, profilePhotoS3Key: s3Key } },
        authMode: 'userPool',
      });

      // Continue writing to User table via updateProfilePhoto mutation
      try {
        await client.graphql({
          query: updateProfilePhoto,
          variables: { input: { profilePhotoS3Key: s3Key } },
          authMode: 'userPool',
        });
      } catch {
        // Non-critical — updateUser already set profilePhotoS3Key
      }

      setProfilePhotoS3Key(s3Key);
      setUploadMsg({ type: 'success', text: 'Profile photo updated successfully.' });
      handleCancelPreview();
    } catch (err: any) {
      console.error('Upload failed:', err);
      setUploadMsg({ type: 'error', text: err?.message || 'Upload failed. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!authUser) return;
    setRemoving(true);
    setUploadMsg(null);
    try {
      // Remove from User table (primary)
      // Requirement 10.3: Set profilePhotoS3Key to null on User table
      await client.graphql({
        query: updateUser,
        variables: { input: { cognitoSub: authUser.cognitoSub, profilePhotoS3Key: null } },
        authMode: 'userPool',
      });

      // Also remove via removeProfilePhoto mutation (operates on User table)
      try {
        await client.graphql({
          query: removeProfilePhoto,
          authMode: 'userPool',
        });
      } catch {
        // Non-critical — updateUser already cleared profilePhotoS3Key
      }
      setProfilePhotoS3Key(null);
      setUploadMsg({ type: 'success', text: 'Profile photo removed.' });
    } catch (err: any) {
      console.error('Remove failed:', err);
      setUploadMsg({ type: 'error', text: err?.message || 'Failed to remove photo. Please try again.' });
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Administration">Settings</PageHeader>

      {/* Per-subpage actions: the Change Password (My Profile), Create User
          (User Management), and Export System Logs (System Log) triggers now
          live inside their respective subpages rather than a global header,
          so each action is scoped to where it applies. */}

      {/* Tab Navigation — filtered by role (Req 11.1, 11.2, 11.3).
          PR (m.6) Phase 2: switched from underline tabs to PillTabs
          for stylistic parity with the rest of the system. The
          previous implementation hand-rolled `border-b-2` underline
          tabs with chevron prev/next paddles for overflow scrolling;
          PillTabs provides the canonical pill shape, with horizontal
          overflow handled natively by the flex-wrap on small viewports. */}
      {(() => {
        const allTabs = [
          { id: 'profile' as const,         label: 'My Profile' },
          { id: 'users' as const,           label: 'User Management' },
          { id: 'log' as const,             label: 'System Log' },
          { id: 'email-templates' as const, label: 'Email Templates' },
          { id: 'payments' as const,        label: 'Payment Processing' },
          { id: 'divisions' as const,       label: 'Divisions' },
          { id: 'titles' as const,          label: 'Titles' },
          { id: 'seasons' as const,         label: 'Seasons' },
          { id: 'documents' as const,       label: 'Documents' },
          { id: 'forms' as const,           label: 'Application Builder' },
        ];
        const settingsTabs = allTabs.filter(tab =>
          allowedTabs.includes(tab.id) &&
          // The season Title catalog subpage (season-scoped-title-registry, task
          // 14) is gated behind `isSeasonTitleRegistryEnabled()`: hide the
          // 'Titles' tab until the backfill has seeded the catalog and the flag
          // is flipped on the target environment.
          (tab.id !== 'titles' || isSeasonTitleRegistryEnabled()) &&
          // The Application Form builder subpage is gated behind
          // `isApplicationFormEnabled()` (relocated here from the top-level
          // Director nav): hide the 'Forms' tab when the flag is off.
          (tab.id !== 'forms' || isApplicationFormEnabled()),
        );
        return (
          <PillTabs
            ariaLabel="Settings"
            activeId={settingsTab}
            onChange={(id) => setSettingsTab(id)}
            items={settingsTabs}
          />
        );
      })()}

      {/* My Profile Tab Content */}
      {settingsTab === 'profile' && (
      <section className="bg-card border border-border rounded-card shadow-subtle p-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h3 className="font-display text-page-title text-foreground">My Profile</h3>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={async () => {
                setExportingAccount(true);
                try {
                  await exportAccountPdf({
                    fullName: authUser?.fullName || '',
                    email: authUser?.email || '',
                    role: role === 'admin' ? 'Admin' : role === 'poweruser' ? 'Power User' : role === 'judge' ? 'Judge' : 'Contestant',
                    tenantName: tenantCopy.orgName,
                    memberSince: profileMemberSince ? new Date(profileMemberSince).toLocaleDateString() : undefined,
                    profilePhotoS3Key: profilePhotoS3Key || undefined,
                  }, tenantCopy);
                } catch (err) {
                  console.error('Failed to export account PDF:', err);
                } finally {
                  setExportingAccount(false);
                }
              }}
              disabled={exportingAccount}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 min-h-[var(--touch-target-min)] border border-border bg-card text-foreground text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
            >
              <Download className="w-4 h-4" /> {exportingAccount ? 'Exporting…' : 'Download My Profile (PDF)'}
            </button>
            <button onClick={() => setShowPwDialog(true)} className="inline-flex items-center gap-2 rounded-full px-6 py-3 min-h-[var(--touch-target-min)] bg-foreground text-background text-eyebrow-sm hover:opacity-90 transition-opacity border-none cursor-pointer">
              <Key className="w-4 h-4" /> Change Password
            </button>
          </div>
        </div>
        {profileLoading ? (
          <p className="text-muted-foreground font-sans text-center py-8">Loading…</p>
        ) : (
          <div className="flex flex-col items-center gap-6">
            {/* Avatar Preview */}
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="w-32 h-32 rounded-full object-cover border-2 border-border"
              />
            ) : (
              <AvatarWithEdit
                profilePhotoS3Key={profilePhotoS3Key || undefined}
                fullName={authUser?.fullName || ''}
                size="lg"
                variant="admin"
                canEdit={!!profilePhotoS3Key}
                originalPhotoS3Key={profilePhotoS3Key}
                cropParams={null}
                onEditClick={async () => {
                  if (!profilePhotoS3Key) return;
                  try {
                    const result = await client.graphql({
                      query: getProfilePhotoDownloadUrl,
                      variables: { s3Key: profilePhotoS3Key },
                      authMode: 'userPool',
                    }) as GraphQLResult<{ getProfilePhotoDownloadUrl: string }>;
                    const url = result.data?.getProfilePhotoDownloadUrl;
                    if (url) {
                      setSettingsCropImageUrl(url);
                      setSettingsCropModalOpen(true);
                    }
                  } catch (err) {
                    console.error('Failed to fetch image for crop:', err);
                  }
                }}
              />
            )}

            {/* User Info */}
            <div className="text-center">
              <h4 className="font-display text-xl text-foreground">{authUser?.fullName || '—'}</h4>
              <p className="text-eyebrow-sm text-primary mt-1">{authUser?.email || '—'}</p>
            </div>

            {/* Upload / Remove Actions */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              {previewUrl && selectedFile ? (
                <>
                  <button
                    onClick={handleConfirmUpload}
                    disabled={uploading}
                    className="flex items-center gap-2 px-5 py-3 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary/90 transition-colors border-none cursor-pointer disabled:opacity-50 rounded-input"
                  >
                    {uploading ? 'Uploading…' : <><Check className="w-4 h-4" /> Confirm</>}
                  </button>
                  <button
                    onClick={handleCancelPreview}
                    disabled={uploading}
                    className="flex items-center gap-2 px-5 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input bg-card text-muted-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <X className="w-4 h-4" /> Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-5 py-3 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary/90 transition-colors border-none cursor-pointer rounded-input"
                  >
                    <Camera className="w-4 h-4" /> Upload Photo
                  </button>
                  {profilePhotoS3Key && (
                    <button
                      onClick={handleRemovePhoto}
                      disabled={removing}
                      className="flex items-center gap-2 px-5 py-3 min-h-[var(--touch-target-min)] border border-destructive/20 bg-card text-destructive-text font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-destructive-muted transition-colors cursor-pointer disabled:opacity-50 rounded-input"
                    >
                      {removing ? 'Removing…' : <><Trash2 className="w-4 h-4" /> Remove Photo</>}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Status Messages */}
            {uploadMsg && (
              <p className={`text-eyebrow-sm ${uploadMsg.type === 'success' ? 'text-success-text' : 'text-destructive-text'}`}>
                {uploadMsg.text}
              </p>
            )}
          </div>
        )}

        {/* Crop Modal for My Profile avatar */}
        <CropModal
          open={settingsCropModalOpen}
          imageUrl={settingsCropImageUrl}
          initialCropParams={null}
          onConfirm={async (params) => {
            if (!profilePhotoS3Key) return;
            const result = await settingsCropPhoto(profilePhotoS3Key, params);
            if (result) {
              setSettingsCropModalOpen(false);
              setProfilePhotoS3Key(result.croppedPhotoS3Key);
            }
          }}
          onCancel={() => setSettingsCropModalOpen(false)}
        />
      </section>
      )}

      {/* Tab Content */}
      {settingsTab === 'users' && role === 'admin' && (
      <div className="space-y-6">
        <PageHeader
          size="sm"
          action={
            <div className="flex items-center gap-4">
              <span className="text-eyebrow-xs text-muted-foreground">{users.length} users</span>
              <button onClick={() => setShowCreateUser(true)} className="flex items-center gap-2 px-4 py-2 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary/90 transition-colors border-none cursor-pointer rounded-input">
                <UserPlus className="w-3 h-3" /> Create User
              </button>
            </div>
          }
        >
          User Management
        </PageHeader>
        {usersLoading ? (
          <p className="text-muted-foreground font-sans text-center py-8">Loading…</p>
        ) : (
          <div className="rounded-card border border-border bg-card shadow-subtle divide-y divide-border overflow-hidden">
            {/* Table Header */}
            <div className="flex items-center p-4 bg-muted/50">
              <div className="flex-1 min-w-0">
                <button onClick={() => { if (userSortField === 'name') setUserSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUserSortField('name'); setUserSortDir('asc'); } }} className="flex items-center gap-2 text-eyebrow-xs text-primary font-bold bg-transparent border-none cursor-pointer hover:text-foreground transition-colors touch-target">
                  <Users className="w-3 h-3 text-muted-foreground" />User<ArrowUpDown className={`w-3 h-3 ${userSortField === 'name' ? 'text-foreground' : 'text-muted-foreground'}`} />
                </button>
              </div>
              <div className="w-28 text-center hidden md:block">
                <button onClick={() => { if (userSortField === 'role') setUserSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUserSortField('role'); setUserSortDir('asc'); } }} className="flex items-center gap-1 justify-center text-eyebrow-xs text-primary font-bold bg-transparent border-none cursor-pointer hover:text-foreground transition-colors w-full touch-target">
                  <Crown className="w-3 h-3 text-muted-foreground" />Role<ArrowUpDown className={`w-3 h-3 ${userSortField === 'role' ? 'text-foreground' : 'text-muted-foreground'}`} />
                </button>
              </div>
              <div className="w-28 text-center hidden lg:block">
                <button onClick={() => { if (userSortField === 'created') setUserSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUserSortField('created'); setUserSortDir('asc'); } }} className="flex items-center gap-1 justify-center text-eyebrow-xs text-primary font-bold bg-transparent border-none cursor-pointer hover:text-foreground transition-colors w-full touch-target">
                  <Calendar className="w-3 h-3 text-muted-foreground" />Created<ArrowUpDown className={`w-3 h-3 ${userSortField === 'created' ? 'text-foreground' : 'text-muted-foreground'}`} />
                </button>
              </div>
              <div className="w-28 text-center hidden lg:block">
                <span className="text-eyebrow-xs text-primary font-bold flex items-center gap-1 justify-center"><Clock className="w-3 h-3 text-muted-foreground" />Last Login</span>
              </div>
              <div className="w-20 text-center hidden xl:block">
                <div className="flex gap-1 justify-center">
                  <span className="text-eyebrow-xs text-primary font-bold">Access</span>
                  <span className="text-eyebrow-xs text-primary font-bold">Mobile</span>
                </div>
              </div>
              <div className="w-10" />
            </div>
            {[...users].sort((a, b) => {
              const dir = userSortDir === 'asc' ? 1 : -1;
              if (userSortField === 'name') return a.fullName.localeCompare(b.fullName) * dir;
              if (userSortField === 'role') return (a.role || '').localeCompare(b.role || '') * dir;
              if (userSortField === 'status') return (a.submissionStatus || '').localeCompare(b.submissionStatus || '') * dir;
              if (userSortField === 'created') return ((a.createdAt || '').localeCompare(b.createdAt || '')) * dir;
              return 0;
            }).map(u => (
              <div key={u.id} className="flex items-center p-4 hover:bg-muted/30 transition-colors group cursor-pointer" onClick={() => { setEditUser(u); setEditName(u.fullName); setEditEmail(u.email); setEditRole(u.role?.toLowerCase().includes('admin') ? 'admin' : u.role?.toLowerCase().includes('power') ? 'poweruser' : u.role?.toLowerCase().includes('judge') ? 'judge' : u.role?.toLowerCase().includes('miss') ? 'miss-contestant' : 'mrs-contestant'); setEditMsg(null); }}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <LegacyAvatar profilePhotoS3Key={u.profilePhotoS3Key} fullName={u.fullName} size="md" variant="neutral" className="flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-sans text-xs font-bold text-foreground truncate">{u.fullName}</p>
                    <p className="text-eyebrow-sm text-primary truncate mt-0.5">{u.email}</p>
                  </div>
                </div>
                <div className="w-28 text-center hidden md:block">
                  <span className="text-eyebrow-sm text-foreground">{u.role || 'Contestant'}</span>
                </div>
                <div className="w-28 text-center hidden lg:block">
                  <span className="text-eyebrow-sm text-foreground">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</span>
                </div>
                <div className="w-28 text-center hidden lg:block">
                  <span className="text-eyebrow-sm text-foreground" title={u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : ''}>{u.lastLoginAt ? formatRelativeDate(u.lastLoginAt) : '—'}</span>
                </div>
                <div className="flex items-center gap-2 w-20 justify-center hidden xl:flex">
                  {!isProtectedUser(u.email) && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const isEnabled = u.submissionStatus !== 'Disabled';
                          // When disabling access, also disable mobile
                          if (isEnabled) {
                            client.graphql({ query: `mutation($email: String!, $enabled: Boolean!) { setUserEnabled(email: $email, enabled: $enabled) { success } }`, variables: { email: u.email, enabled: false }, authMode: 'userPool' });
                            client.graphql({ query: `mutation($email: String!, $enabled: Boolean!) { setMobileAccess(email: $email, enabled: $enabled) { success } }`, variables: { email: u.email, enabled: false }, authMode: 'userPool' });
                            setUsers(prev => prev.map(x => x.id === u.id ? { ...x, submissionStatus: 'Disabled', mobileAccessEnabled: false } : x));
                          } else {
                            client.graphql({ query: `mutation($email: String!, $enabled: Boolean!) { setUserEnabled(email: $email, enabled: $enabled) { success } }`, variables: { email: u.email, enabled: true }, authMode: 'userPool' });
                            setUsers(prev => prev.map(x => x.id === u.id ? { ...x, submissionStatus: 'Active' } : x));
                          }
                        }}
                        className={`w-7 h-4 rounded-full relative transition-colors border-none cursor-pointer touch-target ${u.submissionStatus === 'Disabled' ? 'bg-destructive-muted' : 'bg-success-muted'}`}
                        title={u.submissionStatus === 'Disabled' ? 'Account disabled — click to enable' : 'Account enabled — click to disable'}
                      >
                        <div className={`w-3 h-3 rounded-full bg-white shadow absolute top-0.5 transition-all ${u.submissionStatus === 'Disabled' ? 'left-0.5' : 'left-3.5'}`} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const hasMobile = (u as any).mobileAccessEnabled !== false;
                          client.graphql({ query: `mutation($email: String!, $enabled: Boolean!) { setMobileAccess(email: $email, enabled: $enabled) { success } }`, variables: { email: u.email, enabled: !hasMobile }, authMode: 'userPool' });
                          setUsers(prev => prev.map(x => x.id === u.id ? { ...x, mobileAccessEnabled: !hasMobile } : x));
                        }}
                        className={`w-7 h-4 rounded-full relative transition-colors border-none cursor-pointer touch-target ${(u as any).mobileAccessEnabled === false ? 'bg-destructive-muted' : 'bg-info-muted'}`}
                        title={(u as any).mobileAccessEnabled === false ? 'Mobile access off — click to enable' : 'Mobile access on — click to disable'}
                      >
                        <div className={`w-3 h-3 rounded-full bg-white shadow absolute top-0.5 transition-all ${(u as any).mobileAccessEnabled === false ? 'left-0.5' : 'left-3.5'}`} />
                      </button>
                    </>
                  )}
                </div>
                <div className="w-10 flex justify-end">
                  {!isProtectedUser(u.email) && (
                  <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(u); }} className="p-2 touch-target bg-destructive-muted text-destructive-text hover:bg-destructive hover:text-destructive-foreground transition-all rounded border-none cursor-pointer opacity-0 group-hover:opacity-100" title="Delete User">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {settingsTab === 'log' && role === 'admin' && (
      <div className="space-y-6">
        <div className="flex justify-end">
          <button onClick={() => setShowExportLog(true)} className="inline-flex items-center gap-2 rounded-full px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input bg-card text-muted-foreground text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer">
            <Download className="w-4 h-4" /> Export System Logs
          </button>
        </div>
        <SystemLog key={logKey} />
      </div>
      )}

      {settingsTab === 'email-templates' && role === 'admin' && (
      <section>
        <EmailTemplateManager />
      </section>
      )}

      {settingsTab === 'payments' && role === 'admin' && (
      <section>
        <PaymentProcessingPage />
      </section>
      )}

      {settingsTab === 'divisions' && role === 'admin' && (
      <section className="space-y-12">
        <DivisionManagement />
        <ContestantNumbering />
      </section>
      )}

      {settingsTab === 'titles' && role === 'admin' && isSeasonTitleRegistryEnabled() && (
      <section>
        <TitleManagement />
      </section>
      )}

      {settingsTab === 'seasons' && role === 'admin' && (
      <section>
        <SeasonManagement />
      </section>
      )}

      {settingsTab === 'documents' && role === 'admin' && (
      <section>
        <Suspense fallback={<RouteLoadingFallback />}>
          {isDocumentLibraryEnabled() ? <DirectorDocumentCenter /> : <DocumentsPage />}
        </Suspense>
      </section>
      )}

      {settingsTab === 'forms' && role === 'admin' && isApplicationFormEnabled() && (
      <section>
        <Suspense fallback={<RouteLoadingFallback />}>
          <ApplicationBuilderAdminRoute />
        </Suspense>
      </section>
      )}

      {/* Change Password Dialog */}
      {showPwDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => { setShowPwDialog(false); setPwMsg(null); setOldPassword(''); setNewPassword(''); setConfirmPassword(''); }} />
          <div className="relative bg-card rounded-card border border-border shadow-prominent max-w-md w-full mx-4 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl text-foreground">Change Password</h3>
              <button onClick={() => { setShowPwDialog(false); setPwMsg(null); }} className="p-2 touch-target hover:bg-muted rounded-full transition-colors bg-transparent border-none cursor-pointer"><X className="w-5 h-5 text-muted-foreground" /></button>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-5">
              <div className="space-y-2">
                <label className="text-eyebrow-sm text-muted-foreground">Current Password</label>
                <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} required className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm" />
              </div>
              <div className="space-y-2">
                <label className="text-eyebrow-sm text-muted-foreground">New Password</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm" />
              </div>
              <div className="space-y-2">
                <label className="text-eyebrow-sm text-muted-foreground">Confirm New Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm" />
              </div>
              {pwMsg && <p className={`text-eyebrow-sm ${pwMsg.type === 'success' ? 'text-success-text' : 'text-destructive-text'}`}>{pwMsg.text}</p>}
              <div className="flex gap-4 pt-2">
                <button type="submit" disabled={pwLoading} className="flex-grow bg-primary text-primary-foreground py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary transition-colors disabled:opacity-50 border-none cursor-pointer">
                  {pwLoading ? 'Changing…' : 'Update Password'}
                </button>
                <button type="button" onClick={() => { setShowPwDialog(false); setPwMsg(null); }} className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer bg-transparent">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User Dialog */}
      {showCreateUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => { setShowCreateUser(false); setCreateUserMsg(null); }} />
          <div className="relative bg-card rounded-card border border-border shadow-prominent max-w-md w-full mx-4 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl text-foreground">Create New User</h3>
              <button onClick={() => { setShowCreateUser(false); setCreateUserMsg(null); }} className="p-2 touch-target hover:bg-muted rounded-full transition-colors bg-transparent border-none cursor-pointer"><X className="w-5 h-5 text-muted-foreground" /></button>
            </div>
            <form onSubmit={handleCreateUser} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-eyebrow-sm text-muted-foreground">First Name</label>
                  <input
                    type="text"
                    value={newUserFirstName}
                    onChange={e => setNewUserFirstName(e.target.value)}
                    required
                    placeholder="Jane"
                    className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-eyebrow-sm text-muted-foreground">Last Name</label>
                  <input
                    type="text"
                    value={newUserLastName}
                    onChange={e => setNewUserLastName(e.target.value)}
                    required
                    placeholder="Doe"
                    className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-eyebrow-sm text-muted-foreground">Email</label>
                <input type="email" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} required placeholder="jane@example.com" className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm" />
              </div>

              <div className="space-y-2">
                <label className="text-eyebrow-sm text-muted-foreground">Role</label>
                <select
                  value={newUserRole}
                  onChange={e => setNewUserRole(e.target.value as typeof newUserRole)}
                  className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm bg-transparent cursor-pointer"
                >
                  <option value="mrs-contestant">Mrs. Contestant</option>
                  <option value="miss-contestant">Miss Contestant</option>
                  <option value="poweruser">Power User</option>
                  <option value="judge">Judge</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {createUserMsg && <p className={`text-eyebrow-sm ${createUserMsg.type === 'success' ? 'text-success-text' : 'text-destructive-text'}`}>{createUserMsg.text}</p>}
              <div className="flex gap-4 pt-2">
                <button type="submit" disabled={createUserLoading} className="flex-grow bg-primary text-primary-foreground py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary transition-colors disabled:opacity-50 border-none cursor-pointer">
                  {createUserLoading ? 'Creating…' : 'Create User'}
                </button>
                <button type="button" onClick={() => { setShowCreateUser(false); setCreateUserMsg(null); }} className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer bg-transparent">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Dialog */}
      {editUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => { setEditUser(null); setEditMsg(null); }} />
          <div className="relative bg-card rounded-card border border-border shadow-prominent max-w-md w-full mx-4 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl text-foreground">Edit User</h3>
              <button onClick={() => { setEditUser(null); setEditMsg(null); }} className="p-2 touch-target hover:bg-muted rounded-full transition-colors bg-transparent border-none cursor-pointer"><X className="w-5 h-5 text-muted-foreground" /></button>
            </div>
            <div className="space-y-5">
              <div className="space-y-2">
                <label className="text-eyebrow-sm text-muted-foreground">Full Name</label>
                <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm" />
              </div>
              <div className="space-y-2">
                <label className="text-eyebrow-sm text-muted-foreground">Email</label>
                <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm" />
              </div>
              <div className="space-y-2">
                <label className="text-eyebrow-sm text-muted-foreground">Role</label>
                <select
                  value={editRole}
                  onChange={e => setEditRole(e.target.value)}
                  disabled={isProtectedUser(editUser.email)}
                  className={`w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm bg-transparent ${isProtectedUser(editUser.email) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <option value="mrs-contestant">Mrs. Contestant</option>
                  <option value="miss-contestant">Miss Contestant</option>
                  <option value="poweruser">Power User</option>
                  <option value="judge">Judge</option>
                  <option value="admin">Admin</option>
                </select>
                {isProtectedUser(editUser.email) && (
                  <p className="text-eyebrow-xs text-muted-foreground mt-1">This account is protected and cannot have its role changed.</p>
                )}
              </div>
              {editMsg && <p className={`text-eyebrow-sm ${editMsg.type === 'success' ? 'text-success-text' : 'text-destructive-text'}`}>{editMsg.text}</p>}
              <div className="flex flex-col gap-3 pt-2">
                <button
                  onClick={() => alert('Password reset email sent')}
                  className="w-full py-3 min-h-[var(--touch-target-min)] border border-border font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-foreground hover:bg-muted transition-colors cursor-pointer bg-transparent"
                >
                  Force Password Reset
                </button>
                {!isProtectedUser(editUser?.email ?? '') && (
                  <button
                    onClick={async () => {
                      if (!editUser) return;
                      setEditSaving(true);
                      setEditMsg(null);
                      try {
                        const isCurrentlyEnabled = editUser.submissionStatus !== 'Disabled';
                        const newEnabled = !isCurrentlyEnabled;
                        await client.graphql({
                          query: `mutation($email: String!, $enabled: Boolean!) { setUserEnabled(email: $email, enabled: $enabled) { success message } }`,
                          variables: { email: editUser.email, enabled: newEnabled },
                          authMode: 'userPool',
                        });
                        setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, submissionStatus: newEnabled ? 'Active' : 'Disabled' } : u));
                        setEditUser(prev => prev ? { ...prev, submissionStatus: newEnabled ? 'Active' : 'Disabled' } : prev);
                        setEditMsg({ type: 'success', text: newEnabled ? 'User enabled' : 'User disabled' });
                      } catch (err) {
                        setEditMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update user status' });
                      } finally {
                        setEditSaving(false);
                      }
                    }}
                    disabled={editSaving}
                    className={`w-full py-3 min-h-[var(--touch-target-min)] border font-sans text-[10px] uppercase tracking-[0.2em] font-bold transition-colors cursor-pointer bg-transparent disabled:opacity-50 ${
                      editUser?.submissionStatus === 'Disabled'
                        ? 'border-success/40 text-success-text hover:bg-success-muted'
                        : 'border-warning/40 text-warning-text hover:bg-warning-muted'
                    }`}
                  >
                    {editUser?.submissionStatus === 'Disabled' ? 'Enable User' : 'Disable User'}
                  </button>
                )}
                {!isProtectedUser(editUser?.email ?? '') && (
                  <button
                    onClick={async () => {
                      if (!editUser) return;
                      setEditSaving(true);
                      setEditMsg(null);
                      try {
                        const currentMobile = (editUser as any).mobileAccessEnabled !== false;
                        const newMobile = !currentMobile;
                        await client.graphql({
                          query: `mutation($email: String!, $enabled: Boolean!) { setMobileAccess(email: $email, enabled: $enabled) { success message } }`,
                          variables: { email: editUser.email, enabled: newMobile },
                          authMode: 'userPool',
                        });
                        setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, mobileAccessEnabled: newMobile } : u));
                        setEditUser(prev => prev ? { ...prev, mobileAccessEnabled: newMobile } : prev);
                        setEditMsg({ type: 'success', text: newMobile ? 'Mobile access enabled' : 'Mobile access disabled' });
                      } catch (err) {
                        setEditMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update mobile access' });
                      } finally {
                        setEditSaving(false);
                      }
                    }}
                    disabled={editSaving}
                    className={`w-full py-3 min-h-[var(--touch-target-min)] border font-sans text-[10px] uppercase tracking-[0.2em] font-bold transition-colors cursor-pointer bg-transparent disabled:opacity-50 ${
                      (editUser as any)?.mobileAccessEnabled === false
                        ? 'border-success/40 text-success-text hover:bg-success-muted'
                        : 'border-info/40 text-info-text hover:bg-info-muted'
                    }`}
                  >
                    {(editUser as any)?.mobileAccessEnabled === false ? 'Enable Mobile Access' : 'Disable Mobile Access'}
                  </button>
                )}
                {!isProtectedUser(editUser?.email ?? '') && editUser && !editUser.id.startsWith('cognito-') && (
                  <button
                    onClick={async () => {
                      if (!editUser) return;
                      setEditSaving(true);
                      setEditMsg(null);
                      try {
                        const currentlyVisible = (editUser as any).publicProfileVisible !== false;
                        const newVisible = !currentlyVisible;
                        await client.graphql({
                          query: `mutation($applicantId: ID!, $competitionYear: Int!, $visible: Boolean!) { setPublicProfileVisible(applicantId: $applicantId, competitionYear: $competitionYear, visible: $visible) { applicantId } }`,
                          variables: { applicantId: editUser.id, competitionYear: operativeYear, visible: newVisible },
                          authMode: 'userPool',
                        });
                        setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, publicProfileVisible: newVisible } : u));
                        setEditUser(prev => prev ? { ...prev, publicProfileVisible: newVisible } : prev);
                        setEditMsg({ type: 'success', text: newVisible ? 'Public profile visible' : 'Public profile hidden' });
                      } catch (err) {
                        setEditMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update visibility' });
                      } finally {
                        setEditSaving(false);
                      }
                    }}
                    disabled={editSaving}
                    className={`w-full py-3 min-h-[var(--touch-target-min)] border font-sans text-[10px] uppercase tracking-[0.2em] font-bold transition-colors cursor-pointer bg-transparent disabled:opacity-50 ${
                      (editUser as any)?.publicProfileVisible === false
                        ? 'border-success/40 text-success-text hover:bg-success-muted'
                        : 'border-purple-300 text-purple-700 hover:bg-purple-50'
                    }`}
                  >
                    {(editUser as any)?.publicProfileVisible === false ? 'Show on Public Page' : 'Hide from Public Page'}
                  </button>
                )}
                <div className="flex gap-4">
                  <button
                    onClick={async () => {
                      if (!editUser) return;
                      setEditSaving(true);
                      setEditMsg(null);
                      try {
                        // Update applicant record if it exists (not a director/poweruser/judge-only entry)
                        if (!editUser.id.startsWith('director-') && !editUser.id.startsWith('poweruser-') && !editUser.id.startsWith('judge-')) {
                          const [firstName, ...lastParts] = editName.split(' ');
                          const lastName = lastParts.join(' ') || '';
                          await client.graphql({
                            query: `mutation($input: UpdateApplicantInput!) { updateApplicant(input: $input) { id } }`,
                            variables: { input: { id: editUser.id, firstName, lastName, email: editEmail } },
                            authMode: 'userPool',
                          });
                        }

                        // Determine old and new Cognito group names for role change
                        const roleToGroup = (r: string): string => {
                          if (r === 'admin') return 'Admins';
                          if (r === 'poweruser') return 'PowerUsers';
                          if (r === 'judge') return 'Judges';
                          return 'Applicants';
                        };
                        const oldRole = editUser.role?.toLowerCase().includes('admin') ? 'admin'
                          : editUser.role?.toLowerCase().includes('power') ? 'poweruser'
                          : editUser.role?.toLowerCase().includes('judge') ? 'judge'
                          : editUser.role?.toLowerCase().includes('miss') ? 'miss-contestant'
                          : 'mrs-contestant';
                        const oldGroupName = roleToGroup(oldRole);
                        const newGroupName = roleToGroup(editRole);

                        // Call changeUserRole mutation if the group actually changed
                        if (oldGroupName !== newGroupName && !isProtectedUser(editUser.email)) {
                          await client.graphql({
                            query: changeUserRole,
                            variables: { email: editEmail, oldGroupName, newGroupName },
                          });
                        }

                        // Map editRole to display label for the user list
                        const roleLabel = editRole === 'admin' ? 'Admin'
                          : editRole === 'poweruser' ? 'Power User'
                          : editRole === 'judge' ? 'Judge'
                          : editRole === 'miss-contestant' ? 'Miss Contestant'
                          : 'Mrs. Contestant';

                        setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, fullName: editName, email: editEmail, role: roleLabel } : u));
                        setEditMsg({ type: 'success', text: 'User updated successfully' });
                        setTimeout(() => { setEditUser(null); setEditMsg(null); }, 1500);
                      } catch (err: unknown) {
                        setEditMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update user' });
                      } finally {
                        setEditSaving(false);
                      }
                    }}
                    disabled={editSaving}
                    className="flex-grow bg-primary text-primary-foreground py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary transition-colors disabled:opacity-50 border-none cursor-pointer"
                  >
                    {editSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button onClick={() => { setEditUser(null); setEditMsg(null); }} className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer bg-transparent">Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export System Logs Dialog */}
      {showExportLog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setShowExportLog(false)} />
          <div className="relative bg-card rounded-card border border-border shadow-prominent max-w-md w-full mx-4 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl text-foreground">Export System Logs</h3>
              <button onClick={() => setShowExportLog(false)} className="p-2 touch-target hover:bg-muted rounded-full transition-colors bg-transparent border-none cursor-pointer"><X className="w-5 h-5 text-muted-foreground" /></button>
            </div>
            <p className="font-sans text-sm text-muted-foreground mb-5">Select which log types to include in the CSV export:</p>
            <div className="space-y-3 mb-6">
              {([['APPROVE', 'Approvals'], ['REJECT', 'Rejections'], ['DELETE_APPLICATION', 'Deleted Applications'], ['DELETE_PARTICIPANT', 'Deleted Contestants'], ['LOCK_PROFILE', 'Profile Locks'], ['RETURN_APPLICATION', 'Returned Applications']] as const).map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer group">
                  <input type="checkbox" checked={exportFilters[key] || false} onChange={e => setExportFilters(prev => ({ ...prev, [key]: e.target.checked }))} className="w-4 h-4 accent-primary" />
                  <span className="text-eyebrow-sm text-muted-foreground group-hover:text-foreground transition-colors">{label}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-4">
              <button onClick={handleExportLog} disabled={!Object.values(exportFilters).some(v => v)} className="flex-grow flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary transition-colors disabled:opacity-40 border-none cursor-pointer">
                <Download className="w-4 h-4" /> Export CSV
              </button>
              <button onClick={() => setShowExportLog(false)} className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer bg-transparent">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }} />
          <div className="relative bg-card rounded-card border border-border shadow-prominent max-w-md w-full mx-4 p-8">
            <h3 className="font-display text-2xl text-destructive-text mb-4">Delete User</h3>
            <p className="font-sans text-sm text-muted-foreground mb-4">Permanently delete <span className="font-bold text-foreground">{deleteTarget.fullName}</span> and all their data.</p>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-eyebrow-sm text-destructive-text">Type "Delete this user" to confirm</label>
                <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder="Delete this user" className="w-full border-b border-destructive/20 py-2 focus:border-destructive outline-none transition-colors font-sans text-sm" />
              </div>
              <div className="flex gap-4">
                <button onClick={handleDeleteUser} disabled={deleteConfirmText !== 'Delete this user' || userDeleting} className="flex-grow bg-destructive text-destructive-foreground py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-destructive/90 transition-colors disabled:opacity-40 border-none cursor-pointer">
                  {userDeleting ? 'Deleting…' : 'Delete'}
                </button>
                <button onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }} className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer bg-transparent">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Undo Action Confirmation */}
      {undoTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setUndoTarget(null)} />
          <div className="relative bg-card rounded-card border border-border shadow-prominent max-w-md w-full mx-4 p-8">
            <h3 className="font-display text-2xl text-foreground mb-4">Undo Action</h3>
            <p className="font-sans text-sm text-muted-foreground mb-2">Are you sure you want to undo this action?</p>
            <div className="bg-muted p-4 border border-border mb-6">
              <p className="text-eyebrow-sm text-primary mb-1">Action</p>
              <p className="font-sans text-sm text-foreground font-bold">{undoTarget.action.replace(/_/g, ' ')}</p>
              <p className="text-eyebrow-sm text-primary mt-3 mb-1">Target</p>
              <p className="font-sans text-sm text-foreground">{undoTarget.targetName} ({undoTarget.targetEmail})</p>
              <p className="text-eyebrow-sm text-primary mt-3 mb-1">Date</p>
              <p className="font-sans text-sm text-foreground">{new Date(undoTarget.timestamp).toLocaleString()}</p>
            </div>
            <div className="flex gap-4">
              <button onClick={handleUndoAction} disabled={undoProcessing} className="flex-grow flex items-center justify-center gap-2 bg-warning text-warning-foreground py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-warning/90 transition-colors disabled:opacity-50 border-none cursor-pointer">
                <Undo2 className="w-4 h-4" /> {undoProcessing ? 'Processing…' : 'Confirm Undo'}
              </button>
              <button onClick={() => setUndoTarget(null)} className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer bg-transparent">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Participant Settings ────────────────────────────────────────────────────

function ParticipantSettings() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const tenantCopy = useTenantCopy();
  // Self-service "Download My Profile (PDF)" export (Req: self-export). The
  // applicant path mirrors ApplyHost's competitionId resolution: the derived
  // active-intake season, never a director-only explicit ?competitionId= param.
  const { competitionYear: derivedApplicationYear } = useDerivedApplicationSeason();
  const [exportingApplication, setExportingApplication] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pwLoading, setPwLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Tab state — default to 'profile' (My Profile tab) per Requirement 1.2
  const [settingsTab, setSettingsTab] = useState<'profile' | 'account' | 'documents'>('profile');

  // My Profile tab state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profilePhotoS3Key, setProfilePhotoS3Key] = useState<string | null>(null);
  const [profileApplicantId, setProfileApplicantId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadMsg, setUploadMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);

  // Fetch current user's applicant record and profile photo on mount
  useEffect(() => {
    if (!user?.email) { setProfileLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        // The User table is the single source of truth for the avatar (the
        // auth/display projection of the Person SSOT). The applicantId is still
        // read from the Applicant row for the upload-photo flow, but the photo
        // key comes from the User row — the legacy Applicant.profilePhotoS3Key
        // duplicate was removed (meridian-data-model task 12.1).
        const [userResult, applicantResult] = await Promise.all([
          client.graphql({
            query: `query { listUsers(limit: 1000) { items { email profilePhotoS3Key } } }`,
            authMode: 'userPool',
          }) as Promise<GraphQLResult<{ listUsers: { items: Array<{ email: string; profilePhotoS3Key?: string | null }> } }>>,
          client.graphql({
            query: `query { listApplicants(limit: 200) { items { id email } } }`,
            authMode: 'userPool',
          }) as Promise<GraphQLResult<{ listApplicants: { items: Array<{ id: string; email: string }> } }>>,
        ]);
        const userMatch = userResult.data?.listUsers?.items?.find(
          (u) => u.email?.toLowerCase() === user.email?.toLowerCase(),
        );
        const match = applicantResult.data?.listApplicants?.items?.find(a => a.email === user.email);
        if (!cancelled) {
          if (match) setProfileApplicantId(match.id);
          setProfilePhotoS3Key(userMatch?.profilePhotoS3Key || null);
        }
      } catch (err) {
        console.error('Failed to fetch profile photo:', err);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadMsg(null);

    if (!validateFileSize(file.size)) {
      setUploadMsg({ type: 'error', text: 'File must be smaller than 5 MB.' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleCancelPreview = () => {
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadMsg(null);
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile || !user) return;
    setUploading(true);
    setUploadMsg(null);
    try {
      // If no Applicant record exists yet, create one first
      let appId = profileApplicantId;
      if (!appId) {
        const { firstName, lastName } = deriveNamePairFromUser(user);
        const createResult = await client.graphql({
          query: createApplicantMutation,
          variables: { input: { email: user.email, firstName, lastName, cognitoSub: user.cognitoSub } },
          authMode: 'userPool',
        }) as GraphQLResult<{ createApplicant: { id: string } }>;
        appId = createResult.data?.createApplicant?.id ?? null;
        if (appId) setProfileApplicantId(appId);
      }
      if (!appId) {
        throw new Error('Could not create applicant record.');
      }

      // 1. Get pre-signed upload URL
      const urlResult = await client.graphql({
        query: getProfilePhotoUploadUrl,
        variables: { userId: appId, contentType: selectedFile.type },
      }) as GraphQLResult<{ getProfilePhotoUploadUrl: { uploadUrl: string; s3Key: string; expiresIn: number } }>;

      const { uploadUrl, s3Key } = urlResult.data!.getProfilePhotoUploadUrl;

      // 2. Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': selectedFile.type },
        body: selectedFile,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      // 3. Save S3 key to User table (primary) and Applicant table (backward compatibility)
      // Requirement 10.1, 10.2: Write profilePhotoS3Key to User table
      await client.graphql({
        query: updateUser,
        variables: { input: { cognitoSub: user.cognitoSub, profilePhotoS3Key: s3Key } },
        authMode: 'userPool',
      });

      // Continue writing to User table via updateProfilePhoto mutation
      try {
        await client.graphql({
          query: updateProfilePhoto,
          variables: { input: { profilePhotoS3Key: s3Key } },
          authMode: 'userPool',
        });
      } catch {
        // Non-critical — updateUser already set profilePhotoS3Key
      }

      setProfilePhotoS3Key(s3Key);
      setUploadMsg({ type: 'success', text: 'Profile photo updated successfully.' });
      handleCancelPreview();
    } catch (err: any) {
      console.error('Upload failed:', err);
      setUploadMsg({ type: 'error', text: err?.message || 'Upload failed. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!user) return;
    setRemoving(true);
    setUploadMsg(null);
    try {
      // Remove from User table (primary)
      // Requirement 10.3: Set profilePhotoS3Key to null on User table
      await client.graphql({
        query: updateUser,
        variables: { input: { cognitoSub: user.cognitoSub, profilePhotoS3Key: null } },
        authMode: 'userPool',
      });

      // Also remove via removeProfilePhoto mutation (operates on User table)
      try {
        await client.graphql({
          query: removeProfilePhoto,
          authMode: 'userPool',
        });
      } catch {
        // Non-critical — updateUser already cleared profilePhotoS3Key
      }
      setProfilePhotoS3Key(null);
      setUploadMsg({ type: 'success', text: 'Profile photo removed.' });
    } catch (err: any) {
      console.error('Remove failed:', err);
      setUploadMsg({ type: 'error', text: err?.message || 'Failed to remove photo. Please try again.' });
    } finally {
      setRemoving(false);
    }
  };

  const handleExportApplication = async () => {
    if (derivedApplicationYear == null) {
      setUploadMsg({ type: 'error', text: 'No active competition to export from yet.' });
      return;
    }
    setExportingApplication(true);
    setUploadMsg(null);
    try {
      const competitionId = resolveActiveCompetitionId(derivedApplicationYear, user?.activeTenantId);
      // Uploaded documents and the catalog labels that name them are supplementary
      // to the rendered application itself — a failure reading either soft-fails to
      // an empty list (the PDF simply omits the Documents section) rather than
      // blocking the export the user actually asked for.
      const [response, uploadedDocs, catalogEntries] = await Promise.all([
        applicationFormClient.getMyRenderedResponse(competitionId),
        applicationFormClient.listMyUploadedDocuments(derivedApplicationYear).catch(() => []),
        applicationFormClient.getDocumentCatalog(competitionId).catch(() => []),
      ]);
      if (response.sections.length === 0) {
        setUploadMsg({ type: 'error', text: 'No application on file yet — nothing to export.' });
        return;
      }
      const catalogLabels = new Map(catalogEntries.map((entry) => [entry.id, entry.label]));
      const documents: ApplicationExportDocument[] = uploadedDocs.map((doc) => ({
        label: resolveDisplayType(doc.documentType, doc.catalogEntryId, catalogLabels),
        fileName: doc.fileName,
        uploadedAt: doc.uploadedAt,
      }));
      await exportApplicationPdf(response, tenantCopy, documents);
    } catch (err: any) {
      console.error('Failed to export application PDF:', err);
      setUploadMsg({ type: 'error', text: err?.message || 'Failed to export your application. Please try again.' });
    } finally {
      setExportingApplication(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setPwMsg({ type: 'error', text: 'Passwords do not match.' }); return; }
    if (newPassword.length < 8) { setPwMsg({ type: 'error', text: 'Password must be at least 8 characters.' }); return; }
    setPwLoading(true);
    setPwMsg(null);
    try {
      const { updatePassword } = await import('aws-amplify/auth');
      await updatePassword({ oldPassword, newPassword });
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err: any) {
      setPwMsg({ type: 'error', text: err?.message || 'Failed to change password.' });
    } finally { setPwLoading(false); }
  };

  const handleDeleteProfile = async () => {
    if (deleteConfirm !== 'Delete my profile') return;
    setDeleting(true);
    try {
      // Find applicant record and delete it
      const result = await client.graphql({
        query: `query { listApplicants(limit: 200) { items { id email } } }`,
        authMode: 'userPool',
      }) as GraphQLResult<{ listApplicants: { items: Array<{ id: string; email: string }> } }>;
      const match = result.data?.listApplicants?.items?.find(a => a.email === user?.email);
      if (match) {
        // Delete year profiles
        const profiles = await client.graphql({
          query: `query($aid: ID!) { listApplicantYearProfiles(applicantId: $aid) { competitionYear } }`,
          variables: { aid: match.id },
          authMode: 'userPool',
        }) as GraphQLResult<{ listApplicantYearProfiles: Array<{ competitionYear: number }> }>;
        for (const p of (profiles.data?.listApplicantYearProfiles || [])) {
          try { await client.graphql({ query: deleteApplicantYearProfileMutation, variables: { applicantId: match.id, competitionYear: p.competitionYear }, authMode: 'userPool' }); } catch {}
        }
        await client.graphql({ query: deleteApplicantMutation, variables: { id: match.id }, authMode: 'userPool' });
      }
      // Delete Cognito user
      try {
        const { deleteUser } = await import('aws-amplify/auth');
        await deleteUser();
      } catch {}
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Failed to delete profile:', err);
    } finally { setDeleting(false); }
  };

  const participantTabs = [
    { id: 'profile' as const, label: 'My Profile', icon: User },
    { id: 'documents' as const, label: 'Documents', icon: FileText },
    { id: 'account' as const, label: 'Account', icon: Key },
  ];

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Account">Settings</PageHeader>

      {/* Tab Navigation — PillTabs (PR (e2)) */}
      <PillTabs
        ariaLabel="Settings sections"
        activeId={settingsTab}
        onChange={(id) => setSettingsTab(id as 'profile' | 'account' | 'documents')}
        items={participantTabs.map(tab => ({
          id: tab.id,
          label: tab.label,
        }))}
      />

      {/* My Profile Tab Content */}
      {settingsTab === 'profile' && (
      <section className="bg-card border border-border rounded-card shadow-sm p-8">
        <h3 className="font-display text-2xl mb-8 text-foreground">My Profile</h3>
        {profileLoading ? (
          <p className="text-muted-foreground font-sans text-center py-8">Loading…</p>
        ) : (
          <div className="flex flex-col items-center gap-6">
            {/* Avatar Preview (lg = 128×128) — Requirement 1.3 */}
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="w-32 h-32 rounded-full object-cover border-2 border-border"
              />
            ) : (
              <LegacyAvatar
                profilePhotoS3Key={profilePhotoS3Key || undefined}
                fullName={user?.fullName || ''}
                size="lg"
                variant="contestant"
              />
            )}

            {/* User Info — Requirement 1.4 */}
            <div className="text-center">
              <h4 className="font-display text-xl text-foreground">{user?.fullName || '—'}</h4>
              <p className="text-eyebrow-sm text-muted-foreground mt-1">{user?.email || '—'}</p>
            </div>

            {/* Download My Profile (PDF) — self-service application export */}
            <button
              onClick={handleExportApplication}
              disabled={exportingApplication}
              className="flex items-center gap-2 px-5 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input bg-card text-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
            >
              <Download className="w-4 h-4" /> {exportingApplication ? 'Exporting…' : 'Download My Profile (PDF)'}
            </button>

            {/* Upload / Remove Actions — Requirements 1.5, 2.1, 2.8, 2.9 */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              {previewUrl && selectedFile ? (
                <>
                  <button
                    onClick={handleConfirmUpload}
                    disabled={uploading}
                    className="flex items-center gap-2 px-5 py-3 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary/90 transition-colors border-none cursor-pointer disabled:opacity-50 rounded-input"
                  >
                    {uploading ? 'Uploading…' : <><Check className="w-4 h-4" /> Confirm</>}
                  </button>
                  <button
                    onClick={handleCancelPreview}
                    disabled={uploading}
                    className="flex items-center gap-2 px-5 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input bg-card text-muted-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <X className="w-4 h-4" /> Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-5 py-3 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary/90 transition-colors border-none cursor-pointer rounded-input"
                  >
                    <Camera className="w-4 h-4" /> Upload Photo
                  </button>
                  {profilePhotoS3Key && (
                    <button
                      onClick={handleRemovePhoto}
                      disabled={removing}
                      className="flex items-center gap-2 px-5 py-3 min-h-[var(--touch-target-min)] border border-destructive/30 bg-card text-destructive-text font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-destructive-muted transition-colors cursor-pointer disabled:opacity-50 rounded-input"
                    >
                      {removing ? 'Removing…' : <><Trash2 className="w-4 h-4" /> Remove Photo</>}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Hidden file input — Requirement 2.1 */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Status Messages — Requirements 2.5, 2.6, 2.7 */}
            {uploadMsg && (
              <p className={`text-eyebrow-sm ${uploadMsg.type === 'success' ? 'text-success-text' : 'text-destructive-text'}`}>
                {uploadMsg.text}
              </p>
            )}
          </div>
        )}
      </section>
      )}

      {/* Documents Tab Content — relocated from the portal sidebar. */}
      {settingsTab === 'documents' && (
      <section>
        <Suspense fallback={<RouteLoadingFallback />}>
          {isDocumentLibraryEnabled() ? <ContestantDocumentLibrary /> : <ParticipantDocuments />}
        </Suspense>
      </section>
      )}

      {/* Account Tab Content */}
      {settingsTab === 'account' && (
      <div className="space-y-12">
        {/* Change Password */}
        <section className="bg-card border border-border rounded-card shadow-sm p-8">
          <h3 className="font-display text-2xl mb-6 text-foreground">Change Password</h3>
          <form onSubmit={handleChangePassword} className="space-y-6 max-w-md">
            <div className="space-y-2">
              <label className="text-eyebrow-sm text-muted-foreground">Current Password</label>
              <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} required className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm text-foreground bg-transparent" />
            </div>
            <div className="space-y-2">
              <label className="text-eyebrow-sm text-muted-foreground">New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm text-foreground bg-transparent" />
            </div>
            <div className="space-y-2">
              <label className="text-eyebrow-sm text-muted-foreground">Confirm New Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm text-foreground bg-transparent" />
            </div>
            {pwMsg && (
              <p className={`text-eyebrow-sm ${pwMsg.type === 'success' ? 'text-success-text' : 'text-destructive-text'}`}>{pwMsg.text}</p>
            )}
            <button type="submit" disabled={pwLoading} className="bg-primary text-primary-foreground px-8 py-3 min-h-[var(--touch-target-min)] rounded-input font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 border-none cursor-pointer">
              {pwLoading ? 'Changing…' : 'Change Password'}
            </button>
          </form>
        </section>

        {/* Delete Profile */}
        <section className="bg-card border border-destructive/30 shadow-sm p-8">
          <h3 className="font-display text-2xl mb-4 text-destructive">Danger Zone</h3>
          {!showDeleteSection ? (
            <button onClick={() => setShowDeleteSection(true)} className="bg-destructive/10 text-destructive px-6 py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-destructive hover:text-destructive-foreground transition-colors border-none cursor-pointer rounded">
              Delete My Profile
            </button>
          ) : (
            <div className="space-y-4 max-w-md">
              <p className="font-sans text-sm text-destructive">This will permanently delete your profile, application data, documents, and account. This cannot be undone.</p>
              <div className="space-y-2">
                <label className="text-eyebrow-sm text-destructive">Type "Delete my profile" to confirm</label>
                <input type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="Delete my profile" className="w-full border-b border-destructive/30 py-2 focus:border-destructive outline-none transition-colors font-sans text-sm text-foreground bg-transparent" />
              </div>
              <div className="flex gap-4">
                <button onClick={handleDeleteProfile} disabled={deleteConfirm !== 'Delete my profile' || deleting} className="bg-destructive text-destructive-foreground px-6 py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-destructive/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-none cursor-pointer">
                  {deleting ? 'Deleting…' : 'Permanently Delete'}
                </button>
                <button onClick={() => { setShowDeleteSection(false); setDeleteConfirm(''); }} className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer bg-transparent">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
      )}
    </div>
  );
}

// ─── Participant Calendar (checks approval status) ──────────────────────────

function ParticipantCalendar() {
  const { user } = useAuth();
  // Season SSOT: operative competition year from the active-season seam.
  const { operativeYear } = useActiveSeason();
  const [isApproved, setIsApproved] = useState(false);

  useEffect(() => {
    if (!user?.email) return;
    (async () => {
      try {
        const result = await client.graphql({
          query: `query { listApplicants(limit: 200) { items { id email } } }`,
          authMode: 'userPool',
        }) as GraphQLResult<{ listApplicants: { items: Array<{ id: string; email: string }> } }>;
        const match = result.data?.listApplicants?.items?.find(a => a.email === user.email);
        if (!match) return;
        const profileResult = await client.graphql({
          query: `query($aid: ID!) { listApplicantYearProfiles(applicantId: $aid) { competitionYear submissionStatus } }`,
          variables: { aid: match.id },
          authMode: 'userPool',
        }) as GraphQLResult<{ listApplicantYearProfiles: Array<{ competitionYear: number; submissionStatus: string }> }>;
        const profile = profileResult.data?.listApplicantYearProfiles?.find(p => p.competitionYear === operativeYear);
        if (profile?.submissionStatus === 'APPROVED') setIsApproved(true);
      } catch {}
    })();
  }, [user?.email, operativeYear]);

  return <CalendarPage isApproved={isApproved} />;
}

// ─── Service Log (Participant) ──────────────────────────────────────────────

function ServiceLog() {
  const { user } = useAuth();

  interface VolunteerActivity {
    id: string;
    applicantId: string;
    organizationName: string;
    activityDate: string;
    hoursContributed: number;
    description: string;
    createdAt: string;
  }

  const [entries, setEntries] = useState<VolunteerActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [applicantId, setApplicantId] = useState<string | null>(null);
  const [newEntry, setNewEntry] = useState({
    date: '',
    activity: '',
    hours: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Look up applicant by email, then fetch volunteer activities
  useEffect(() => {
    if (!user?.email) { setLoading(false); return; }
    let cancelled = false;

    async function fetchData() {
      try {
        const result = (await client.graphql({
          query: `query { listApplicants(limit: 200) { items { id email } } }`,
          authMode: 'userPool',
        })) as GraphQLResult<{ listApplicants: { items: Array<{ id: string; email: string }> } }>;

        const match = result.data?.listApplicants?.items?.find(a => a.email === user!.email);
        if (!match || cancelled) { if (!cancelled) setLoading(false); return; }

        setApplicantId(match.id);

        const volResult = (await client.graphql({
          query: `query($applicantId: ID!) { listVolunteerActivities(applicantId: $applicantId) { id applicantId organizationName activityDate hoursContributed description createdAt } }`,
          variables: { applicantId: match.id },
          authMode: 'userPool',
        })) as GraphQLResult<{ listVolunteerActivities: VolunteerActivity[] }>;

        if (!cancelled) {
          setEntries(volResult.data?.listVolunteerActivities || []);
        }
      } catch (err) {
        console.error('Failed to fetch service log:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [user?.email]);

  const totalHours = entries.reduce((sum, e) => sum + (e.hoursContributed || 0), 0);

  // Format hours with up to 2 decimal places, trimming trailing zeros so
  // "3" stays "3" and "2.5" stays "2.5" (never "2.50" or "3.5000000001").
  const formatHours = (h: number): string => {
    const rounded = Math.round(h * 100) / 100;
    return rounded % 1 === 0 ? String(rounded) : rounded.toString();
  };

  // Render an ISO date (YYYY-MM-DD) as a readable label without shifting the
  // day across timezones (parse the parts rather than `new Date(iso)`).
  const formatActivityDate = (iso: string): string => {
    const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!parts) return iso;
    const [, y, m, d] = parts;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEntry.date || !newEntry.activity || !newEntry.hours) return;
    if (!applicantId) {
      console.error('[ServiceLog] No applicantId found — cannot add entry. User email:', user?.email);
      alert('Unable to add entry. Your profile may not be set up yet. Please complete your profile first.');
      return;
    }
    setSubmitting(true);
    try {
      const result = (await client.graphql({
        query: `mutation($input: CreateVolunteerActivityInput!) { createVolunteerActivity(input: $input) { id applicantId organizationName activityDate hoursContributed description createdAt } }`,
        variables: {
          input: {
            applicantId,
            organizationName: newEntry.activity,
            activityDate: newEntry.date,
            hoursContributed: parseFloat(newEntry.hours),
            description: newEntry.description || '—',
          },
        },
        authMode: 'userPool',
      })) as GraphQLResult<{ createVolunteerActivity: VolunteerActivity }>;

      const created = result.data?.createVolunteerActivity;
      if (created) {
        setEntries([created, ...entries]);
      }
      setNewEntry({ date: '', activity: '', hours: '', description: '' });
    } catch (err) {
      console.error('Failed to create volunteer activity:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await client.graphql({
        query: `mutation($id: ID!) { deleteVolunteerActivity(id: $id) }`,
        variables: { id },
        authMode: 'userPool',
      });
      setEntries(entries.filter(e => e.id !== id));
    } catch (err) {
      console.error('Failed to delete volunteer activity:', err);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <PageHeader eyebrow="Contestant Dashboard">Service Activity Log</PageHeader>
        <div className="text-center py-12 text-muted-foreground font-sans">Loading service log…</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Contestant Dashboard"
        body="Track your community service hours, appearances, and impact. All entries are subject to verification by the pageant committee."
      >
        Service Activity Log
      </PageHeader>

      {/* KPI strip — mirrors the dashboard KpiTile row (m.6 aesthetic). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="service-log-kpis">
        <KpiTile
          label="Total Hours"
          value={formatHours(totalHours)}
          caption="Logged year to date"
          variant="tinted"
          data-testid="kpi-total-hours"
        />
        <KpiTile
          label="Entries Logged"
          value={entries.length}
          caption={entries.length === 1 ? '1 activity' : `${entries.length} activities`}
          data-testid="kpi-entries"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add Entry Form */}
        <div className="lg:col-span-1">
          <div className="bg-card border border-border rounded-card shadow-subtle p-6 sm:p-8">
            <h3 className="font-display text-page-title text-foreground mb-6">Log New Activity</h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="service-log-date">Date</Label>
                <Input
                  id="service-log-date"
                  type="date"
                  value={newEntry.date}
                  onChange={e => setNewEntry({ ...newEntry, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-log-activity">Activity Name</Label>
                <Input
                  id="service-log-activity"
                  type="text"
                  placeholder="e.g. Food Bank Volunteer"
                  value={newEntry.activity}
                  onChange={e => setNewEntry({ ...newEntry, activity: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-log-hours">Hours</Label>
                <Input
                  id="service-log-hours"
                  type="number"
                  step="0.25"
                  min="0.25"
                  placeholder="0"
                  value={newEntry.hours}
                  onChange={e => setNewEntry({ ...newEntry, hours: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-log-description">Description</Label>
                <Textarea
                  id="service-log-description"
                  rows={3}
                  placeholder="Briefly describe your impact..."
                  value={newEntry.description}
                  onChange={e => setNewEntry({ ...newEntry, description: e.target.value })}
                />
              </div>
              <Button type="submit" size="lg" disabled={submitting} className="w-full">
                <Plus className="w-4 h-4" /> {submitting ? 'Adding…' : 'Add to Log'}
              </Button>
            </form>
          </div>
        </div>

        {/* Entries List */}
        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-card shadow-subtle overflow-hidden">
            <div className="p-6 border-b border-border">
              <h3 className="font-display text-page-title text-foreground">Recent Activities</h3>
            </div>
            <div className="divide-y divide-border">
              {entries.length === 0 ? (
                <div className="p-12 sm:p-16 text-center">
                  <div className="mx-auto w-14 h-14 rounded-card bg-surface-tinted flex items-center justify-center text-tertiary mb-4">
                    <Clock className="w-6 h-6" />
                  </div>
                  <h4 className="font-display text-xl text-foreground mb-1">No activities logged yet</h4>
                  <p className="text-muted-foreground font-sans text-sm max-w-xs mx-auto">
                    Add your first service activity using the form to start tracking your impact.
                  </p>
                </div>
              ) : (
                entries.map((entry) => (
                  <div key={entry.id} className="p-6 sm:p-8 hover:bg-muted/30 transition-colors group">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex items-start gap-4 min-w-0">
                        <div className="w-11 h-11 shrink-0 bg-surface-tinted rounded-card flex items-center justify-center text-tertiary">
                          <Calendar className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-display text-xl text-foreground truncate">{entry.organizationName}</h4>
                          <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                            <span className="text-eyebrow-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {formatHours(entry.hoursContributed)} Hours
                            </span>
                            <span className="text-eyebrow-sm text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> {formatActivityDate(entry.activityDate)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Delete ${entry.organizationName}`}
                        onClick={() => handleDelete(entry.id)}
                        className="shrink-0 text-muted-foreground hover:text-destructive md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {entry.description && entry.description !== '—' && (
                      <p className="mt-4 md:ml-[3.75rem] text-foreground font-sans text-sm leading-relaxed">
                        {entry.description}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Application Flow (PRESERVED — all GraphQL, auth, draft logic intact) ───

type ApplicantType = 'MRS' | 'MISS';

function ApplicationFlow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tenantCopy = useTenantCopy();
  const pageantRules = useMemo(() => buildPageantRules(tenantCopy), [tenantCopy]);
  const [step, setStep] = useState<'loading' | 'select' | 'documents' | 'rules' | 'affidavit' | 'submitted'>('loading');
  const [applicantType, setApplicantType] = useState<ApplicantType | null>(null);
  const [applicantId, setApplicantId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [rulesError, setRulesError] = useState(false);
  const [desiredTitle, setDesiredTitle] = useState('');
  const [maritalStatus, setMaritalStatus] = useState<'SINGLE' | 'DIVORCED' | 'WIDOWED' | null>(null);
  // Season SSOT: operative competition year from the active-season seam.
  const { operativeYear: competitionYear } = useActiveSeason();

  // Check for existing draft on mount
  useEffect(() => {
    if (!user?.email) {
      setStep('select');
      return;
    }
    let cancelled = false;
    async function checkExistingDraft() {
      try {
        const result = (await client.graphql({
          query: `query { listApplicants(limit: 200) { items { id email fullName } } }`,
          authMode: 'userPool',
        })) as GraphQLResult<{ listApplicants: { items: Array<{ id: string; email: string }> } }>;

        const match = result.data?.listApplicants?.items?.find(a => a.email === user!.email);
        if (!match || cancelled) {
          if (!cancelled) setStep('select');
          return;
        }

        const profileResult = (await client.graphql({
          query: `query($aid: ID!) { listApplicantYearProfiles(applicantId: $aid) { competitionYear applicantType submissionStatus requestedTitle approvedTitle titleApproved phase1Data { dateOfBirth birthplace usCitizen eyeColor hairColor occupation currentCommunityInvolvement hobbies talents spouseName marriageDate generalBackground } digitalSignature } }`,
          variables: { applicantId: match.id },
          authMode: 'userPool',
        })) as GraphQLResult<{ listApplicantYearProfiles: Array<{ applicantId: string; competitionYear: number; applicantType: string; submissionStatus: string; phase1Data: Record<string, unknown> | null; digitalSignature: string | null }> }>;

        const yearProfile = profileResult.data?.listApplicantYearProfiles?.find(
          p => p.competitionYear === competitionYear
        );

        if (cancelled) return;

        if (!yearProfile) {
          setApplicantId(match.id);
          setStep('select');
          return;
        }

        if (yearProfile.submissionStatus === 'APPROVED' || yearProfile.submissionStatus === 'REJECTED') {
          navigate('/portal/profile', { replace: true });
          return;
        }

        setApplicantId(match.id);
        setApplicantType(yearProfile.applicantType as ApplicantType);
        setStep('documents');
      } catch (err) {
        console.error('Failed to check for existing draft:', err);
        if (!cancelled) setStep('select');
      }
    }
    checkExistingDraft();
    return () => { cancelled = true; };
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTypeSelect = async (type: ApplicantType) => {
    setApplicantType(type);
    setCreateError(null);

    let newApplicantId = applicantId;
    // True when this is the user's first-ever ApplicantYearProfile for
    // *any* competition year. The welcome email fires exactly once per
    // contestant, specifically on the moment they first commit to a
    // Mrs/Miss division + title. Previously we gated on
    // `createApplicant`, but the post-authentication Lambda now
    // back-fills a minimal Applicant row on first sign-in, which meant
    // the welcome email was never fired for contestants who logged in
    // before reaching the Apply flow. Checking year-profile count is
    // stable: it stays at 0 until this very handler writes the first
    // profile.
    let shouldSendWelcomeEmail = false;

    // If no applicantId yet, check for existing applicant first before creating
    if (user?.email && !newApplicantId) {
      try {
        const existingResult = await client.graphql({
          query: `query { listApplicants(limit: 200) { items { id email } } }`,
          authMode: 'userPool',
        }) as GraphQLResult<{ listApplicants: { items: Array<{ id: string; email: string }> } }>;
        const existing = existingResult.data?.listApplicants?.items?.find(a => a.email === user!.email);
        if (existing) {
          newApplicantId = existing.id;
          setApplicantId(newApplicantId);
        }
      } catch {}
    }

    if (user?.email && !newApplicantId) {
      try {
        const { firstName, lastName } = deriveNamePairFromUser(user);
        const result = await client.graphql({
          query: createApplicantMutation,
          variables: { input: { email: user.email, firstName, lastName, cognitoSub: user.cognitoSub } },
          authMode: 'userPool',
        }) as GraphQLResult<{ createApplicant: { id: string } }>;
        newApplicantId = result.data?.createApplicant?.id ?? null;
        setApplicantId(newApplicantId);
      } catch (err) {
        console.error('Failed to create applicant:', err);
        setCreateError('Failed to create your application. Please try again.');
        return;
      }
    }

    if (newApplicantId) {
      // Determine whether this handler is about to create the applicant's
      // first-ever year profile. If yes, we'll fire the welcome email
      // after the profile is written. We check before the mutation so we
      // know whether *we* are the ones bringing the count from 0 → 1.
      try {
        const existingProfiles = await client.graphql({
          query: `query($aid: ID!) { listApplicantYearProfiles(applicantId: $aid) { competitionYear } }`,
          variables: { aid: newApplicantId },
          authMode: 'userPool',
        }) as GraphQLResult<{ listApplicantYearProfiles: Array<{ applicantId: string; competitionYear: number }> }>;
        const count = existingProfiles.data?.listApplicantYearProfiles?.length ?? 0;
        shouldSendWelcomeEmail = count === 0;
      } catch {
        // Non-fatal — err on the side of not re-sending
        shouldSendWelcomeEmail = false;
      }
    }

    if (newApplicantId) {
      try {
        await client.graphql({
          query: `mutation($aid: ID!, $yr: Int!, $at: ApplicantType!) { createApplicantYearProfile(applicantId: $aid, competitionYear: $yr, applicantType: $at) { applicantId } }`,
          variables: { aid: newApplicantId, yr: competitionYear, at: type },
          authMode: 'userPool',
        });
      } catch {
        // May already exist
      }

      // Save the requested title and marital status
      if (desiredTitle.trim() || (type === 'MISS' && maritalStatus)) {
        try {
          await client.graphql({
            query: `mutation($input: UpdateApplicantYearProfileInput!) { updateApplicantYearProfile(input: $input) { applicantId } }`,
            variables: { input: {
              applicantId: newApplicantId,
              competitionYear,
              ...(desiredTitle.trim() ? { requestedTitle: desiredTitle.trim() } : {}),
              ...(type === 'MISS' && maritalStatus ? { phase1Data: { maritalStatus } } : {}),
            } },
            authMode: 'userPool',
          });
        } catch {}
      }
    }

    // Fire-and-forget welcome email — sent once per contestant on the
    // first year-profile they create. Returning contestants and
    // contestants toggling divisions mid-flow both skip this path because
    // `shouldSendWelcomeEmail` is only true when the applicant had zero
    // year profiles before this handler ran. The call is wrapped in its
    // own try/catch so a Brevo outage can't block the user from
    // progressing to the documents step.
    if (shouldSendWelcomeEmail && user?.email) {
      (async () => {
        try {
          await client.graphql({
            query: sendWelcomeEmailMutation,
            variables: {
              email: user.email,
              fullName: user.fullName || user.email!,
              temporaryPassword: null,
              role: 'contestant',
            },
            authMode: 'userPool',
          });
        } catch (err) {
          console.warn('Welcome email enqueue failed (non-fatal):', err);
        }
      })();
    }

    setStep('documents');
  };

  const handleDocumentsSubmit = () => {
    setStep('rules');
  };

  const handleRulesSubmit = async () => {
    if (!rulesAccepted || !contractAccepted) {
      setRulesError(true);
      return;
    }
    if (applicantId) {
      try {
        // Save contract acceptance as digital signature — status stays DRAFT
        await client.graphql({
          query: `mutation($input: UpdateApplicantYearProfileInput!) { updateApplicantYearProfile(input: $input) { applicantId } }`,
          variables: {
            input: {
              applicantId,
              competitionYear,
              digitalSignature: user?.fullName || 'Accepted',
            },
          },
          authMode: 'userPool',
        });
      } catch (err) {
        console.error('Failed to save contract signature:', err);
      }
    }
    setStep('affidavit');
  };

  const handleAffidavitSign = async (payload: {
    signedAt: string;
    signedName: string;
    version: string;
  }) => {
    if (!applicantId) {
      throw new Error('Missing applicant id');
    }
    // Fetch current phase1Data so we preserve fields set earlier (e.g. maritalStatus)
    let existingPhase1: Record<string, unknown> = {};
    try {
      const existing = (await client.graphql({
        query: `query($aid: ID!) { listApplicantYearProfiles(applicantId: $aid) { competitionYear phase1Data { maritalStatus } } }`,
        variables: { aid: applicantId },
        authMode: 'userPool',
      })) as GraphQLResult<{
        listApplicantYearProfiles: Array<{
          competitionYear: number;
          phase1Data?: { maritalStatus?: string | null } | null;
        }>;
      }>;
      const yp = existing.data?.listApplicantYearProfiles?.find(
        (p) => p.competitionYear === competitionYear
      );
      if (yp?.phase1Data?.maritalStatus) {
        existingPhase1.maritalStatus = yp.phase1Data.maritalStatus;
      }
    } catch {
      // Non-fatal — proceed with just the affidavit fields
    }

    await client.graphql({
      query: `mutation($input: UpdateApplicantYearProfileInput!) { updateApplicantYearProfile(input: $input) { applicantId } }`,
      variables: {
        input: {
          applicantId,
          competitionYear,
          phase1Data: {
            ...existingPhase1,
            eligibilityAffidavitSigned: true,
            eligibilityAffidavitSignedAt: payload.signedAt,
            eligibilityAffidavitSignedName: payload.signedName,
            eligibilityAffidavitVersion: payload.version,
          },
        },
      },
      authMode: 'userPool',
    });

    setStep('submitted');
  };

  const isAfterDocs = step === 'rules' || step === 'affidavit' || step === 'submitted';
  const isAfterRules = step === 'affidavit' || step === 'submitted';
  const steps = [
    { label: 'Select Type & Title', status: step === 'select' ? 'active' : (step !== 'loading' ? 'completed' : 'upcoming') },
    { label: 'Documents', status: step === 'documents' ? 'active' : isAfterDocs ? 'completed' : 'upcoming' },
    { label: 'Rules & Agreement', status: step === 'rules' ? 'active' : isAfterRules ? 'completed' : 'upcoming' },
    { label: 'Eligibility Affidavit', status: step === 'affidavit' ? 'active' : step === 'submitted' ? 'completed' : 'upcoming' },
    { label: 'Complete', status: step === 'submitted' ? 'active' : 'upcoming' },
  ];

  useEffect(() => {
    if (step === 'submitted') {
      const timer = setTimeout(() => navigate('/portal/profile', { replace: true }), 3000);
      return () => clearTimeout(timer);
    }
  }, [step, navigate]);

  return (
    <div className="min-h-screen bg-muted pt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-16">
        <div className="grid grid-cols-12 gap-8">
          {/* Left Sidebar: Step Progress */}
          <aside className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <span className="font-sans text-[10px] uppercase tracking-wider text-muted-foreground mb-6 block">
                Application Progress
              </span>
              <nav className="space-y-4" data-testid="step-progress">
                {steps.map((s, i) => {
                  const isActive = s.status === 'active';
                  const isCompleted = s.status === 'completed';
                  const isUpcoming = s.status === 'upcoming';
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-4 transition-all duration-300 ease-out ${isUpcoming ? 'opacity-50' : 'opacity-100'}`}
                      data-testid={`step-${i}`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-all duration-300 ease-out ${
                          isActive
                            ? 'bg-primary text-primary-foreground shadow-lg'
                            : isCompleted
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-primary/20 text-muted-foreground'
                        }`}
                        data-testid={`step-circle-${i}`}
                      >
                        {isCompleted ? '✓' : i + 1}
                      </div>
                      <div>
                        <span className="font-sans text-sm font-semibold text-foreground block">{s.label}</span>
                        <span className="font-sans text-[10px] uppercase tracking-wider text-muted-foreground">
                          {isActive ? 'In Progress' : isCompleted ? 'Complete' : 'Upcoming'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Form Area */}
          <main className="col-span-12 lg:col-span-8">
            <div className="bg-card p-10 lg:p-16 border border-border rounded-card shadow-sm">
              {step === 'loading' && (
                <div className="flex items-center justify-center py-12">
                  <p className="text-muted-foreground font-sans">Checking for existing application…</p>
                </div>
              )}

              {step === 'select' && (
                <>
                  <Link to="/" className="text-muted-foreground font-sans text-xs uppercase tracking-wider hover:opacity-80 transition-all duration-300 ease-out mb-6 inline-block">
                    ← Back to Home
                  </Link>
                  {createError && (
                    <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-3 mb-4">
                      {createError}
                    </div>
                  )}
                  <ApplicantTypeSelector value={applicantType} onSelect={(type) => { setApplicantType(type); setDesiredTitle(type === 'MRS' ? 'Mrs. ' : 'Miss '); if (type === 'MRS') setMaritalStatus(null); }} maritalStatus={maritalStatus} onMaritalStatusSelect={setMaritalStatus} />

                  {applicantType && (
                    <div className="mt-8 space-y-6">
                      <div className="space-y-2">
                        <label className="text-eyebrow-sm text-muted-foreground">Desired Title</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={desiredTitle}
                            onChange={(e) => setDesiredTitle(e.target.value)}
                            placeholder="Boise"
                            className="w-full border-b border-border py-2 focus:border-primary outline-none transition-colors font-sans text-sm"
                          />
                          {desiredTitle && desiredTitle.trim().length <= 5 && (
                            <span className="absolute left-0 top-0 py-2 font-sans text-sm pointer-events-none">
                              <span className="invisible">{desiredTitle}</span>
                              <span className="text-muted-foreground">Boise</span>
                            </span>
                          )}
                        </div>
                        <p className="text-eyebrow-xs text-muted-foreground">
                          Enter the title you wish to compete under. Subject to director approval.
                        </p>
                      </div>
                      <button
                        onClick={() => handleTypeSelect(applicantType)}
                        className="px-8 py-3 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:shadow-lg transition-all duration-300"
                      >
                        Continue
                      </button>
                    </div>
                  )}
                </>
              )}

              {step === 'documents' && (
                <div>
                  <button
                    onClick={() => setStep('select')}
                    className="bg-transparent border-none text-muted-foreground cursor-pointer mb-5 text-base font-sans text-xs uppercase tracking-wider hover:opacity-80 transition-all duration-300 ease-out touch-target"
                  >
                    ← Back to Type Selection
                  </button>

                  <h2 className="font-display text-2xl font-bold text-foreground mb-2">Required Documents</h2>
                  <p className="text-muted-foreground mb-8 font-sans text-sm leading-relaxed">
                    The following documents are required for your application. You can upload them now from your Documents page, or after completing this application. Proof of marital eligibility is handled through the Sworn Declaration of Eligibility in the next steps — no marriage certificate, divorce decree, or death certificate upload is required.
                  </p>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-4 bg-muted p-5 border border-border">
                      <div className="w-5 h-5 rounded-full border border-border flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="block text-eyebrow-sm font-bold text-foreground">Proof of Residency</span>
                        <span className="text-[10px] text-primary italic">Utility bill, lease agreement, or state ID</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 bg-muted p-5 border border-border">
                      <div className="w-5 h-5 rounded-full border border-border flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="block text-eyebrow-sm font-bold text-foreground">Headshot</span>
                        <span className="text-[10px] text-primary italic">Photo of your face where you are the only subject. Clear and high resolution. Can be updated later.</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <button
                      onClick={handleDocumentsSubmit}
                      className="px-8 py-3 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans font-bold transition-all duration-300 hover:shadow-lg"
                    >
                      Continue to Rules & Agreement
                    </button>
                  </div>
                </div>
              )}

              {step === 'rules' && (
                <div>
                  <button
                    onClick={() => setStep('documents')}
                    className="bg-transparent border-none text-muted-foreground cursor-pointer mb-5 text-base font-sans text-xs uppercase tracking-wider hover:opacity-80 transition-all duration-300 ease-out touch-target"
                  >
                    ← Back to Documents
                  </button>

                  <h2 className="font-display text-2xl font-bold text-foreground mb-2">Rules and Regulations</h2>
                  <p className="text-muted-foreground mb-6">
                    Please read the following rules and regulations carefully before submitting your application.
                  </p>

                  <div className="bg-muted p-6 lg:p-8 mb-8 max-h-[60vh] overflow-y-auto border border-border">
                    <ol className="list-decimal list-outside pl-5 space-y-4 font-sans text-sm text-foreground leading-relaxed">
                      {pageantRules.map((rule, idx) => (
                        <li key={idx}>{rule}</li>
                      ))}
                    </ol>
                  </div>

                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="rules-acceptance"
                        checked={rulesAccepted}
                        onChange={(e) => {
                          setRulesAccepted(e.target.checked);
                          if (e.target.checked && contractAccepted) setRulesError(false);
                        }}
                        className="mt-0.5 h-4 w-4 rounded bg-primary/20 border-none text-foreground focus:ring-ring/30"
                        data-testid="rules-acceptance-checkbox"
                      />
                      <label htmlFor="rules-acceptance" className="font-sans text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        I have read, understand, and agree to abide by all rules and regulations listed above.
                      </label>
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="contract-acceptance"
                        checked={contractAccepted}
                        onChange={(e) => {
                          setContractAccepted(e.target.checked);
                          if (e.target.checked && rulesAccepted) setRulesError(false);
                        }}
                        className="mt-0.5 h-4 w-4 rounded bg-primary/20 border-none text-foreground focus:ring-ring/30"
                        data-testid="contract-acceptance-checkbox"
                      />
                      <label htmlFor="contract-acceptance" className="font-sans text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        I accept the Idaho Pageants Contract and certify that all information provided in this application is true and accurate to the best of my knowledge.
                      </label>
                    </div>

                    {rulesError && (
                      <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg px-4 py-3 text-sm" role="alert">
                        You must accept both the rules and the contract to submit your application.
                      </div>
                    )}

                    <button
                      onClick={handleRulesSubmit}
                      className="px-8 py-3 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans font-bold transition-all duration-300 hover:shadow-lg"
                      data-testid="submit-application-button"
                    >
                      Continue to Eligibility Affidavit
                    </button>
                  </div>
                </div>
              )}

              {step === 'affidavit' && (
                <EligibilityAffidavit
                  layout="page"
                  signerName={user?.fullName || user?.email || ''}
                  signature={{ signed: false, version: AFFIDAVIT_VERSION }}
                  onSign={handleAffidavitSign}
                  onBack={() => setStep('rules')}
                />
              )}

              {step === 'submitted' && (
                <div className="text-center">
                  <div className="bg-primary/20 text-foreground p-8 mb-8">
                    <h2 className="font-display text-2xl font-bold mb-4">Profile Created!</h2>
                    <p className="font-sans text-sm leading-relaxed">
                      Your contestant profile has been created. Please complete your profile details
                      and upload required documents. When you're ready, click "Submit Application" from your profile page.
                    </p>
                    <p className="font-sans text-sm mt-4 text-muted-foreground">
                      Redirecting to your profile...
                    </p>
                  </div>
                  <Link
                    to="/portal/profile"
                    className="inline-block bg-primary text-primary-foreground font-sans font-bold px-8 py-3 rounded-input hover:opacity-90 transition-all duration-300 ease-out no-underline"
                  >
                    Go to My Profile
                  </Link>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}


// ─── Admin Applications Page (table + slide-over detail panel) ──────────────

function AdminApplicationsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tenantCopy = useTenantCopy();
  const { id: routeId } = useParams();
  // Detail mode: the dedicated full-page route `/admin/applications/:id`
  // renders this component non-embedded with an `:id` param. In that mode
  // the list/table chrome is hidden and the applicant detail is rendered
  // full-width in-flow inside the admin layout (not as a slide-over).
  const detailRoute = !embedded && !!routeId;
  const [searchParams, setSearchParams] = useSearchParams();
  const [applicants, setApplicants] = useState<ApplicantWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || 'all');
  const [appSortField, setAppSortField] = useState<'name' | 'title' | 'requested' | 'status'>('name');
  const [appSortDir, setAppSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedApp, setSelectedApp] = useState<ApplicantWithProfile | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [showReturnInput, setShowReturnInput] = useState(false);
  const [returnNote, setReturnNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const [approvedTitleInput, setApprovedTitleInput] = useState('');
  const [titleDuplicateError, setTitleDuplicateError] = useState<string | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedAppDocStatus, setSelectedAppDocStatus] = useState<{ total: number; verified: number } | null>(null);
  const [selectedAppDuesPaid, setSelectedAppDuesPaid] = useState(false);
  const [appMultiSelectMode, setAppMultiSelectMode] = useState(false);
  const [appSelectedIds, setAppSelectedIds] = useState<Set<string>>(new Set());
  const [showAppExportDialog, setShowAppExportDialog] = useState(false);
  const [appExportSections, setAppExportSections] = useState<ExportSections>({ ...DEFAULT_EXPORT_SECTIONS });
  // Season SSOT: operative competition year from the active-season seam.
  const { operativeYear: competitionYear } = useActiveSeason();

  // Sync status filter with URL params
  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    if (status === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ status });
    }
  };

  const fetchApplicants = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch applicants and all Users in parallel. The User table is the
      // single source of truth for profilePhotoS3Key (the auth/display
      // projection of the Person SSOT). The legacy Applicant.profilePhotoS3Key
      // duplicate was removed (meridian-data-model task 12.1), so the avatar is
      // sourced entirely from the User table and overlaid onto each applicant.
      const [applicantResult, usersResult] = await Promise.all([
        client.graphql({
          query: `query { listApplicants(limit: 200) { items { id fullName email cognitoSub homePhone mobilePhone } } }`,
          authMode: 'userPool',
        }) as Promise<GraphQLResult<{ listApplicants: { items: ApplicantWithProfile[] } }>>,
        client.graphql({
          query: `query { listUsers(limit: 1000) { items { email profilePhotoS3Key } } }`,
          authMode: 'userPool',
        }) as Promise<GraphQLResult<{ listUsers: { items: Array<{ email: string; profilePhotoS3Key?: string | null }> } }>>,
      ]);
      const userPhotoByEmail = new Map<string, string>();
      for (const u of (usersResult.data?.listUsers?.items || [])) {
        if (u.email && u.profilePhotoS3Key) {
          userPhotoByEmail.set(u.email.toLowerCase(), u.profilePhotoS3Key);
        }
      }
      const rawApps = applicantResult.data?.listApplicants?.items || [];
      const apps = rawApps.map(a => {
        const userPhoto = a.email ? userPhotoByEmail.get(a.email.toLowerCase()) : undefined;
        // Avatar comes from the User table (SSOT). May be undefined for
        // applicants without a User-table photo.
        return { ...a, profilePhotoS3Key: userPhoto };
      });
      const withProfiles = await Promise.all(apps.map(async (app) => {
        try {
          const profileResult = await client.graphql({
            query: `query($applicantId: ID!) { listApplicantYearProfiles(applicantId: $applicantId) { competitionYear applicantType submissionStatus profileLocked outstandingFees digitalSignature signedAt directorNotes requestedTitle approvedTitle titleApproved phase1Data { age dateOfBirth birthplace usCitizen eyeColor hairColor occupation currentCommunityInvolvement hobbies talents spouseName marriageDate generalBackground } stageFormData { stageName height hairColor eyeColor occupation fitnessRoutine mostImportantLearning husbandName yearsMarried } interviewInfoData { nameAndTitle dateOfBirth cityOfResidency education occupation careerGoals activitiesAndHobbies communityInvolvement leadershipRoles interestingFacts pageantAccomplishmentGoals spouseName childrenNames childrenAges } placementQuestionsData { top15Response interviewJudgesQuestion } } }`,
            variables: { applicantId: app.id },
            authMode: 'userPool',
          }) as GraphQLResult<{ listApplicantYearProfiles: ApplicantWithProfile['yearProfiles'] }>;
          return { ...app, yearProfiles: profileResult.data?.listApplicantYearProfiles || [] };
        } catch { return { ...app, yearProfiles: [] }; }
      }));
      // Filter out records with no year profile for the current competition year
      // (e.g., admin/director-only records created by the login tracking Lambda)
      const contestantsOnly = withProfiles.filter(a =>
        a.yearProfiles?.some(p => p.competitionYear === competitionYear)
      );
      setApplicants(contestantsOnly);
    } catch (err) { console.error('Failed to fetch:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchApplicants(); }, [fetchApplicants]);

  // Auto-open applicant detail from URL param (e.g., legacy links to
  // /admin/approvals?applicant=<id>). The canonical way to open an
  // applicant is now the dedicated route /admin/applications/<id>, so
  // here we simply forward to that page (kept as a backward-compat
  // fallback for any links still using the query param).
  useEffect(() => {
    const applicantId = searchParams.get('applicant');
    if (applicantId && !detailRoute) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('applicant');
      setSearchParams(newParams, { replace: true });
      navigate('/admin/applications/' + applicantId);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Route-driven open: when rendered at /admin/applications/:id, open the
  // matching applicant's detail once the applicant list has loaded
  // (mirrors the legacy ?applicant= behavior, but driven by the route).
  useEffect(() => {
    if (detailRoute && routeId && applicants.length > 0 && selectedApp?.id !== routeId) {
      const found = applicants.find(a => a.id === routeId);
      if (found) openDetail(found);
    }
  }, [applicants, routeId, detailRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async (appId: string) => {
    setProcessing(true);
    const target = applicants.find(a => a.id === appId);

    // Require a profile photo / headshot before approval. The User table is
    // the single source of truth for profilePhotoS3Key (per
    // backend-standards.md). Earlier code read `target.profilePhotoS3Key`
    // from the Applicant table, which is stale for any user whose avatar
    // was updated after unified-user-identity shipped — the HEADSHOT upload
    // and the ApplicantProfilePage avatar upload both now call the
    // `updateProfilePhoto` mutation, which writes to User but not back to
    // Applicant. So we do a live lookup on User via getUserByEmail here.
    let currentPhotoKey: string | undefined;
    try {
      const userResult = await client.graphql({
        query: `query($email: String!) { getUserByEmail(email: $email) { profilePhotoS3Key } }`,
        variables: { email: target?.email || '' },
        authMode: 'userPool',
      }) as GraphQLResult<{ getUserByEmail: { profilePhotoS3Key?: string | null } | null }>;
      currentPhotoKey = userResult.data?.getUserByEmail?.profilePhotoS3Key ?? undefined;
    } catch (err) {
      console.error('Failed to look up User record for approve check:', err);
    }

    if (!currentPhotoKey) {
      setProcessing(false);
      setApprovalError(`Cannot approve: ${target?.fullName || 'Applicant'} has not uploaded a profile photo (headshot). A headshot is required before approval.`);
      return;
    }
    setApprovalError(null);

    // Approval writes the ApplicationResponse SSOT (via reviewApplicationResponse),
    // keyed on the applicant's durable personId derived from their cognitoSub.
    if (!target?.cognitoSub) {
      setProcessing(false);
      setApprovalError(`Cannot approve: ${target?.fullName || 'Applicant'} has no linked account, so the approval cannot be written to the application record.`);
      return;
    }

    try {
      const competitionId = resolveActiveCompetitionId(competitionYear, user?.activeTenantId);
      const applicantPersonId = await personIdForCognitoSub(target.cognitoSub);
      await applicationFormClient.reviewApplicationResponse(competitionId, applicantPersonId, 'APPROVE');
      // SystemLog entry is written by the backend approveApplicationAuditLog
      // pipeline function — no frontend log here to avoid duplicate rows.
      // Approval email is now sent by the backend pipeline (approveApplicationSendEmailFn)
      // so it works regardless of which client (web or mobile) triggers the approval.
      fetchApplicants();
      invalidateApprovalAccess();
      if (selectedApp?.id === appId) {
        setSelectedProfile((prev: any) => prev ? { ...prev, submissionStatus: 'APPROVED' } : prev);
      }
    } catch (err) {
      // Surface the backend error in the approval-error banner so admins
      // don't see a silent no-op if the mutation fails (e.g. condition
      // check mismatch or auth issue). Previously the catch only logged
      // to console, which looked like "I clicked Approve and nothing
      // happened" from the admin's side.
      console.error('Approve failed:', err);
      const message = (err as any)?.errors?.[0]?.message
        || (err as any)?.message
        || 'Approve failed. Please try again.';
      setApprovalError(`Approve failed: ${message}`);
    }
    finally { setProcessing(false); }
  };

  const handleReject = async (appId: string, note: string) => {
    if (!note.trim()) return;
    setProcessing(true);
    const target = applicants.find(a => a.id === appId);
    if (!target?.cognitoSub) {
      setProcessing(false);
      setApprovalError(`Cannot reject: ${target?.fullName || 'Applicant'} has no linked account to write the decision to.`);
      return;
    }
    try {
      const competitionId = resolveActiveCompetitionId(competitionYear, user?.activeTenantId);
      const applicantPersonId = await personIdForCognitoSub(target.cognitoSub);
      await applicationFormClient.reviewApplicationResponse(competitionId, applicantPersonId, 'REJECT', note);
      // SystemLog entry is written by the backend rejectApplicationAuditLog
      // pipeline function — no frontend log here to avoid duplicate rows.
      fetchApplicants();
      invalidateApprovalAccess();
      if (selectedApp?.id === appId) {
        setSelectedProfile((prev: any) => prev ? { ...prev, submissionStatus: 'REJECTED' } : prev);
      }
    } catch (err) { console.error('Reject failed:', err); }
    finally { setProcessing(false); setShowRejectInput(false); setRejectNote(''); }
  };

  const handleReturn = async (appId: string, note: string) => {
    if (!note.trim()) return;
    setProcessing(true);
    const target = applicants.find(a => a.id === appId);
    if (!target?.cognitoSub) {
      setProcessing(false);
      setApprovalError(`Cannot return: ${target?.fullName || 'Applicant'} has no linked account to write the decision to.`);
      return;
    }
    try {
      // "Return" = send the application back to the applicant to fix. Mapped to
      // REQUEST_CHANGES on the ApplicationResponse SSOT (→ DRAFT + reviewReturn),
      // replacing the legacy AYP `updateApplicantYearProfile{submissionStatus:'DRAFT'}` write.
      const competitionId = resolveActiveCompetitionId(competitionYear, user?.activeTenantId);
      const applicantPersonId = await personIdForCognitoSub(target.cognitoSub);
      await applicationFormClient.reviewApplicationResponse(competitionId, applicantPersonId, 'REQUEST_CHANGES', note.trim());
      logSystemAction({ action: 'RETURN_APPLICATION', performedBy: user?.email || 'Unknown', targetName: target?.fullName || appId, targetEmail: target?.email || '', targetId: appId, details: `Returned for ${competitionYear}. Note: ${note}` });
      fetchApplicants();
      invalidateApprovalAccess();
      if (selectedApp?.id === appId) {
        setSelectedProfile((prev: any) => prev ? { ...prev, submissionStatus: 'DRAFT', directorNotes: note.trim() } : prev);
      }
    } catch (err) { console.error('Return failed:', err); }
    finally { setProcessing(false); setShowReturnInput(false); setReturnNote(''); }
  };

  const handleDelete = async (appId: string) => {
    const target = applicants.find(a => a.id === appId);
    const profiles = target?.yearProfiles || [];
    for (const p of profiles) {
      try { await client.graphql({ query: deleteApplicantYearProfileMutation, variables: { applicantId: appId, competitionYear: p.competitionYear }, authMode: 'userPool' }); } catch {}
    }
    try { await client.graphql({ query: deleteApplicantMutation, variables: { id: appId }, authMode: 'userPool' }); } catch {}
    if (selectedApp?.id === appId) { setSelectedApp(null); setSelectedProfile(null); }
    fetchApplicants();
  };

  const openDetail = (app: ApplicantWithProfile) => {
    setSelectedApp(app);
    const profile = app.yearProfiles?.find(p => p.competitionYear === competitionYear);
    setSelectedProfile(profile || null);
    setApprovedTitleInput(profile?.approvedTitle || profile?.requestedTitle || '');
    // Check for duplicate title on selection
    const titleVal = profile?.approvedTitle || profile?.requestedTitle || '';
    if (titleVal.trim()) {
      const dup = applicants.find(a => {
        if (a.id === app.id) return false;
        const p = a.yearProfiles?.find((pr: any) => pr.competitionYear === competitionYear);
        return p?.approvedTitle && p.approvedTitle.toLowerCase() === titleVal.trim().toLowerCase();
      });
      setTitleDuplicateError(dup ? `This title is already approved for ${dup.fullName}` : null);
    } else {
      setTitleDuplicateError(null);
    }
    setShowRejectInput(false);
    setRejectNote('');
    setShowReturnInput(false);
    setReturnNote('');
    setSelectedAppDocStatus(null);
    // Load dues paid status from year profile
    setSelectedAppDuesPaid(false);
    (async () => {
      try {
        const profileResult = await client.graphql({
          query: `query($aid: ID!, $yr: Int!) { getApplicantYearProfile(applicantId: $aid, competitionYear: $yr) { duesPaid verifiedDocumentIds } }`,
          variables: { aid: app.id, yr: competitionYear },
          authMode: 'userPool',
        }) as GraphQLResult<{ getApplicantYearProfile: { duesPaid?: boolean; verifiedDocumentIds?: string[] } | null }>;
        const profileData = profileResult.data?.getApplicantYearProfile;
        setSelectedAppDuesPaid(!!profileData?.duesPaid);
      } catch { setSelectedAppDuesPaid(false); }
    })();
    // Fetch doc verification status for this applicant
    (async () => {
      try {
        const [docResult, profileResult] = await Promise.all([
          client.graphql({
            query: `query($aid: ID!, $yr: Int!) { listDocumentReferences(applicantId: $aid, competitionYear: $yr) { id } }`,
            variables: { aid: app.id, yr: competitionYear },
            authMode: 'userPool',
          }) as any,
          client.graphql({
            query: `query($aid: ID!, $yr: Int!) { getApplicantYearProfile(applicantId: $aid, competitionYear: $yr) { verifiedDocumentIds } }`,
            variables: { aid: app.id, yr: competitionYear },
            authMode: 'userPool',
          }) as any,
        ]);
        const docIds: string[] = (docResult.data?.listDocumentReferences || []).map((d: any) => d.id);
        const verifiedDocIds: string[] = profileResult.data?.getApplicantYearProfile?.verifiedDocumentIds || [];
        const verifiedSet = new Set<string>(verifiedDocIds);
        const verifiedCount = docIds.filter(id => verifiedSet.has(id)).length;
        setSelectedAppDocStatus({ total: docIds.length, verified: verifiedCount });
      } catch {
        setSelectedAppDocStatus({ total: 0, verified: 0 });
      }
    })();
  };

  const filtered = applicants.filter(a => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!a.fullName.toLowerCase().includes(q) && !a.email.toLowerCase().includes(q)) return false;
    }
    if (statusFilter !== 'all') {
      const profile = a.yearProfiles?.find(p => p.competitionYear === competitionYear);
      const status = profile?.submissionStatus || 'DRAFT';
      if (statusFilter.toUpperCase() !== status) return false;
    }
    return true;
  }).sort((a, b) => {
    const dir = appSortDir === 'asc' ? 1 : -1;
    if (appSortField === 'name') return a.fullName.localeCompare(b.fullName) * dir;
    if (appSortField === 'title') {
      const tA = a.yearProfiles?.find(p => p.competitionYear === competitionYear);
      const tB = b.yearProfiles?.find(p => p.competitionYear === competitionYear);
      const titleA = (tA?.approvedTitle || tA?.requestedTitle || '').toLowerCase();
      const titleB = (tB?.approvedTitle || tB?.requestedTitle || '').toLowerCase();
      return titleA.localeCompare(titleB) * dir;
    }
    if (appSortField === 'requested') {
      const rA = a.yearProfiles?.find(p => p.competitionYear === competitionYear)?.createdAt || '';
      const rB = b.yearProfiles?.find(p => p.competitionYear === competitionYear)?.createdAt || '';
      return rA.localeCompare(rB) * dir;
    }
    if (appSortField === 'status') {
      const sA = a.yearProfiles?.find(p => p.competitionYear === competitionYear)?.submissionStatus || 'DRAFT';
      const sB = b.yearProfiles?.find(p => p.competitionYear === competitionYear)?.submissionStatus || 'DRAFT';
      return sA.localeCompare(sB) * dir;
    }
    return 0;
  });

  return (
    <div className="space-y-6 relative">
      {!embedded && !detailRoute && (
      <>
      <PageHeader
        eyebrow="Registration Management"
      >
        Applications
      </PageHeader>
      <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => { setAppMultiSelectMode(!appMultiSelectMode); setAppSelectedIds(new Set()); }}
              className={`pill-shape touch-target transition-colors cursor-pointer flex items-center gap-2 ${
                appMultiSelectMode ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              <CheckCircle className="w-3.5 h-3.5" /> {appMultiSelectMode ? 'Cancel' : 'Select'}
            </button>
            {appMultiSelectMode && (
              <button
                onClick={() => {
                  const filtered = applicants.filter(a => {
                    const profile = a.yearProfiles?.find(p => p.competitionYear === competitionYear);
                    if (!profile) return false;
                    if (statusFilter !== 'all' && profile.submissionStatus !== statusFilter) return false;
                    return true;
                  });
                  if (appSelectedIds.size === filtered.length) setAppSelectedIds(new Set());
                  else setAppSelectedIds(new Set(filtered.map(a => a.id)));
                }}
                className="pill-shape touch-target transition-colors cursor-pointer flex items-center gap-2 bg-card border border-border text-muted-foreground hover:bg-muted"
              >
                Select All
              </button>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search applicants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-border bg-card font-sans text-xs outline-none focus:border-primary transition-colors w-full sm:w-64 rounded-input"
              />
            </div>
      </div>
      </>
      )}
      {!embedded && !detailRoute && (
        <ApprovalTimeHistogram applicants={applicants} competitionYear={competitionYear} />
      )}
      {embedded && (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-5 h-5 text-primary" />
            <h2 className="font-display text-2xl text-foreground">Applications</h2>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => { setAppMultiSelectMode(!appMultiSelectMode); setAppSelectedIds(new Set()); }}
              className={`pill-shape touch-target transition-colors cursor-pointer flex items-center gap-2 ${
                appMultiSelectMode ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              <CheckCircle className="w-3 h-3" /> {appMultiSelectMode ? 'Cancel' : 'Select'}
            </button>
            <button
              onClick={() => {
                setAppSelectedIds(new Set(filtered.map(a => a.id)));
                setShowAppExportDialog(true);
              }}
              className="pill-shape touch-target transition-colors cursor-pointer flex items-center gap-2 bg-card border border-border text-muted-foreground hover:bg-muted"
            >
              <Download className="w-3 h-3" /> Export List
            </button>
            <div className="flex items-center gap-2 text-[9px] font-sans uppercase tracking-widest">
              <span className="text-success-text">{applicants.filter(a => a.yearProfiles?.find(p => p.competitionYear === competitionYear)?.submissionStatus === 'APPROVED').length} approved</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-primary">{applicants.filter(a => a.yearProfiles?.find(p => p.competitionYear === competitionYear)?.submissionStatus === 'SUBMITTED').length} submitted</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative flex-grow max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search applicants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border bg-card font-sans text-xs outline-none focus:border-primary transition-colors rounded-input"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { label: 'All', value: 'all' },
              { label: 'Draft', value: 'DRAFT' },
              { label: 'Submitted', value: 'SUBMITTED' },
              { label: 'Approved', value: 'APPROVED' },
              { label: 'Rejected', value: 'REJECTED' },
            ].map(f => (
              <button
                key={f.value}
                onClick={() => handleStatusFilter(f.value)}
                className={`pill-shape touch-target transition-colors ${
                  statusFilter === f.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      )}

      {!detailRoute && (
      <div className="rounded-card border border-border bg-card shadow-subtle overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground font-sans">Loading applications…</div>
        ) : (
          <div className="divide-y divide-border">
            {/* Sort Header — desktop only. On mobile each card row
                surfaces its own labels per field, so the shared
                header is unnecessary and would just bleed the
                grid-column widths off the right edge of the
                viewport. */}
            <div className="hidden sm:grid grid-cols-[1fr_1fr_100px_100px_80px] gap-4 items-center p-4 bg-muted/50">
              <button onClick={() => { if (appSortField === 'name') setAppSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setAppSortField('name'); setAppSortDir('asc'); } }} className="flex items-center gap-2 text-eyebrow-xs text-primary font-bold bg-transparent border-none cursor-pointer hover:text-foreground transition-colors touch-target">
                <Users className="w-3 h-3 text-muted-foreground" />Contestant<ArrowUpDown className={`w-3 h-3 ${appSortField === 'name' ? 'text-foreground' : 'text-muted-foreground'}`} />
              </button>
              <button onClick={() => { if (appSortField === 'title') setAppSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setAppSortField('title'); setAppSortDir('asc'); } }} className="flex items-center gap-2 text-eyebrow-xs text-primary font-bold bg-transparent border-none cursor-pointer hover:text-foreground transition-colors touch-target">
                <Crown className="w-3 h-3 text-muted-foreground" />Title<ArrowUpDown className={`w-3 h-3 ${appSortField === 'title' ? 'text-foreground' : 'text-muted-foreground'}`} />
              </button>
              <button onClick={() => { if (appSortField === 'requested') setAppSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setAppSortField('requested'); setAppSortDir('asc'); } }} className="flex items-center gap-2 text-eyebrow-xs text-primary font-bold bg-transparent border-none cursor-pointer hover:text-foreground transition-colors hidden sm:flex touch-target">
                <Clock className="w-3 h-3 text-muted-foreground" />Requested<ArrowUpDown className={`w-3 h-3 ${appSortField === 'requested' ? 'text-foreground' : 'text-muted-foreground'}`} />
              </button>
              <button onClick={() => { if (appSortField === 'status') setAppSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setAppSortField('status'); setAppSortDir('asc'); } }} className="flex items-center gap-2 text-eyebrow-xs text-primary font-bold bg-transparent border-none cursor-pointer hover:text-foreground transition-colors touch-target">
                <FileCheck className="w-3 h-3 text-muted-foreground" />Status<ArrowUpDown className={`w-3 h-3 ${appSortField === 'status' ? 'text-foreground' : 'text-muted-foreground'}`} />
              </button>
              <span className="text-eyebrow-xs text-primary font-bold text-right">Actions</span>
            </div>
            {filtered.map((app) => {
              const profile = app.yearProfiles?.find(p => p.competitionYear === competitionYear);
              const status = profile?.submissionStatus || 'DRAFT';
              const category = profile?.applicantType === 'MRS' ? 'Mrs.' : profile?.applicantType === 'MISS' ? 'Miss' : '—';
              const displayTitle = profile?.approvedTitle || profile?.requestedTitle || '—';
              const requestedDate = profile?.createdAt;

              return (
                <div
                  key={app.id}
                  className={`flex flex-col sm:grid sm:grid-cols-[1fr_1fr_100px_100px_80px] gap-3 sm:gap-4 sm:items-center p-4 hover:bg-muted/30 transition-colors ${appSelectedIds.has(app.id) ? 'bg-muted' : ''}`}
                >
                  {/* Primary identity column (mobile + desktop) */}
                  <div className="flex items-center gap-3 min-w-0">
                    {appMultiSelectMode && (
                      <input
                        type="checkbox"
                        checked={appSelectedIds.has(app.id)}
                        onChange={() => {
                          setAppSelectedIds(prev => {
                            const next = new Set(prev);
                            if (next.has(app.id)) next.delete(app.id);
                            else next.add(app.id);
                            return next;
                          });
                        }}
                        className="w-4 h-4 rounded border-border text-foreground focus:ring-ring shrink-0 cursor-pointer"
                      />
                    )}
                    <button
                      onClick={() => navigate('/admin/applications/' + app.id)}
                      className="flex items-center gap-3 text-left hover:opacity-70 transition-opacity bg-transparent border-none cursor-pointer min-w-0 touch-target"
                    >
                    <LegacyAvatar profilePhotoS3Key={app.profilePhotoS3Key} fullName={app.fullName} size="md" variant="contestant" />
                    <div className="min-w-0">
                      <h4 className="font-sans text-xs font-bold text-foreground truncate">{app.fullName}</h4>
                      <span className="text-[10px] text-muted-foreground font-sans uppercase tracking-widest">{category} Division</span>
                    </div>
                  </button>
                  </div>

                  {/* Title — labeled on mobile, plain on desktop */}
                  <div className="min-w-0 flex sm:block items-baseline justify-between gap-3">
                    <span className="sm:hidden text-eyebrow-xs text-muted-foreground shrink-0">Title</span>
                    <p className="font-sans text-xs font-medium text-foreground truncate sm:text-left text-right">{displayTitle}</p>
                  </div>

                  {/* Requested date — labeled on mobile, hidden on small desktop */}
                  <div className="flex sm:block items-baseline justify-between gap-3">
                    <span className="sm:hidden text-eyebrow-xs text-muted-foreground shrink-0">Requested</span>
                    <span className="text-eyebrow-sm text-muted-foreground sm:text-left text-right">
                      {requestedDate ? new Date(requestedDate).toLocaleDateString() : '—'}
                    </span>
                  </div>

                  {/* Status — labeled on mobile, plain on desktop */}
                  <div className="flex sm:block items-center justify-between gap-3">
                    <span className="sm:hidden text-eyebrow-xs text-muted-foreground shrink-0">Status</span>
                    <span className={`pill-shape ${
                      status === 'APPROVED' ? 'bg-success-muted text-success-text' :
                      status === 'REJECTED' ? 'bg-destructive-muted text-destructive-text' :
                      status === 'SUBMITTED' ? 'bg-destructive text-destructive-foreground' : 'bg-muted text-muted-foreground'
                    }`}>
                      {status === 'SUBMITTED' ? 'Pending' : status}
                    </span>
                  </div>

                  {/* Actions — labeled on mobile, plain on desktop */}
                  <div className="flex items-center sm:justify-end justify-between gap-1 pt-2 sm:pt-0 border-t sm:border-t-0 border-border">
                    <span className="sm:hidden text-eyebrow-xs text-muted-foreground shrink-0">Actions</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => navigate('/admin/applications/' + app.id)}
                        className="p-1.5 touch-target bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all rounded border-none cursor-pointer"
                        title="View Details"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(app.id)}
                        className="p-1.5 touch-target text-muted-foreground hover:text-destructive-text transition-colors border-none cursor-pointer bg-transparent"
                        title="Remove"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="p-12 text-center text-primary font-sans italic">No applications found.</div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Multi-select Export Action Bar */}
      {appMultiSelectMode && appSelectedIds.size > 0 && (
        <div className="bg-primary text-primary-foreground p-4 flex items-center justify-between sticky bottom-0 z-40 shadow-prominent rounded-card">
          <span className="text-eyebrow-sm">{appSelectedIds.size} applicant{appSelectedIds.size !== 1 ? 's' : ''} selected</span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAppExportDialog(true)}
              className="flex items-center gap-2 px-5 py-2 min-h-[var(--touch-target-min)] bg-card text-foreground text-eyebrow-sm font-bold hover:bg-muted transition-colors border-none cursor-pointer rounded-input"
            >
              <Download className="w-3.5 h-3.5" /> Export Selected
            </button>
            <button
              onClick={() => setAppSelectedIds(new Set())}
              className="px-4 py-2 touch-target text-muted-foreground hover:text-primary-foreground text-eyebrow-sm transition-colors bg-transparent border-none cursor-pointer"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Export Dialog */}
      {showAppExportDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setShowAppExportDialog(false)} />
          <div className="relative bg-card rounded-card border border-border shadow-prominent w-full max-w-md rounded-card p-8">
            <h3 className="font-display text-2xl text-foreground mb-6">Export {appSelectedIds.size} Applicant{appSelectedIds.size !== 1 ? 's' : ''}</h3>
            <div className="space-y-3 mb-8">
              {Object.entries(appExportSections).map(([key, value]) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={() => setAppExportSections(prev => ({ ...prev, [key]: !prev[key as keyof ExportSections] }))}
                    className="w-4 h-4 rounded border-border text-foreground focus:ring-ring"
                  />
                  <span className="font-sans text-xs uppercase tracking-widest text-muted-foreground">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  const selected = applicants.filter(a => appSelectedIds.has(a.id));
                  const exportData = selected.map(p => {
                    const profile = p.yearProfiles?.find(pr => pr.competitionYear === competitionYear);
                    return buildExportData(p, profile);
                  });
                  exportListPdf(exportData, appExportSections, tenantCopy);
                  setShowAppExportDialog(false);
                }}
                className="flex-grow bg-primary text-primary-foreground py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary transition-colors border-none cursor-pointer"
              >
                Export PDF
              </button>
              <button
                onClick={() => setShowAppExportDialog(false)}
                className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer bg-transparent"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (() => {
        const target = applicants.find(a => a.id === deleteConfirmId);
        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
            <div className="relative bg-card rounded-card border border-border shadow-prominent max-w-md w-full mx-4 p-8">
              <h3 className="font-display text-2xl text-foreground mb-4">Delete Application</h3>
              <p className="font-sans text-sm text-muted-foreground mb-6">
                Are you sure you want to delete <span className="font-bold text-foreground">{target?.fullName || 'this applicant'}</span>'s application? This will remove all their data and cannot be undone.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={async () => {
                    await handleDelete(deleteConfirmId);
                    setDeleteConfirmId(null);
                  }}
                  className="flex-grow bg-destructive text-destructive-foreground py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-destructive/90 transition-colors"
                >
                  Yes, Delete
                </button>
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="flex-grow border border-border py-3 min-h-[var(--touch-target-min)] text-eyebrow-sm hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Detail route loading / not-found fallback */}
      {detailRoute && !(selectedApp && selectedProfile) && (
        <div className="max-w-5xl mx-auto w-full">
          <button
            onClick={() => navigate('/admin/approvals')}
            className="mb-6 inline-flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-primary hover:text-foreground transition-colors bg-transparent border-none cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" /> Back to applications
          </button>
          <div className="rounded-card border border-border bg-card shadow-subtle p-12 text-center text-muted-foreground font-sans">
            {loading ? 'Loading application…' : 'Application not found.'}
          </div>
        </div>
      )}

      {/* Detail — full-page, in-flow inside the admin layout (rendered by
          the dedicated /admin/applications/:id route, not a slide-over). */}
      {selectedApp && selectedProfile && (
        <div className="max-w-5xl mx-auto w-full">
          <button
            onClick={() => navigate('/admin/approvals')}
            className="mb-6 inline-flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-primary hover:text-foreground transition-colors bg-transparent border-none cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" /> Back to applications
          </button>
          <div className="rounded-card border border-border bg-card shadow-subtle flex flex-col overflow-hidden">
            <div className="p-8 border-b border-border flex items-center justify-between bg-muted/50">
              <div className="flex items-center gap-4">
                <LegacyAvatar profilePhotoS3Key={selectedApp.profilePhotoS3Key} fullName={selectedApp.fullName} size="lg" variant="contestant" />
                <div>
                  <h2 className="text-3xl font-display ">{selectedApp.fullName}</h2>
                  <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                    {divisionTitle(tenantCopy, selectedProfile.applicantType)} Applicant
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {isCommunityEnabled() && (
                  <button
                    onClick={() => navigate('/admin/community/' + selectedApp.id)}
                    className="p-2 touch-target hover:bg-muted rounded-full transition-colors bg-transparent border-none cursor-pointer"
                    title="View community record"
                    aria-label="View community record"
                  >
                    <Users className="w-6 h-6 text-muted-foreground" />
                  </button>
                )}
                <button onClick={() => navigate('/admin/approvals')} className="p-2 touch-target hover:bg-muted rounded-full transition-colors bg-transparent border-none cursor-pointer">
                  <X className="w-6 h-6 text-muted-foreground" />
                </button>
              </div>
            </div>

            <div className="p-8 space-y-10">
              {/* Title Designation */}
              <section className="space-y-4">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Title</h3>
                {selectedProfile?.titleApproved ? (
                  <div className="flex items-center gap-3 bg-success-muted p-4 border border-success/20">
                    <Crown className="w-4 h-4 text-success-text flex-shrink-0" />
                    <span className="font-display text-lg text-foreground">{selectedProfile.approvedTitle}</span>
                    <span className="text-eyebrow-xs text-success-text ml-auto">Approved</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedProfile?.requestedTitle && (
                      <p className="font-sans text-sm text-muted-foreground">
                        Requested: <span className="text-foreground font-medium">{selectedProfile.requestedTitle}</span>
                      </p>
                    )}
                    <div className="flex items-center gap-3">
                      <Crown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <input
                        type="text"
                        value={approvedTitleInput}
                        onChange={(e) => {
                          const val = e.target.value;
                          setApprovedTitleInput(val);
                          // Real-time duplicate check
                          if (val.trim()) {
                            const dup = applicants.find(a => {
                              if (a.id === selectedApp?.id) return false;
                              const p = a.yearProfiles?.find((pr: any) => pr.competitionYear === competitionYear);
                              return p?.approvedTitle && p.approvedTitle.toLowerCase() === val.trim().toLowerCase();
                            });
                            setTitleDuplicateError(dup ? `This title is already approved for ${dup.fullName}` : null);
                          } else {
                            setTitleDuplicateError(null);
                          }
                        }}
                        placeholder={selectedProfile?.requestedTitle || 'Enter title…'}
                        className={`flex-grow border-b py-2 focus:border-primary outline-none transition-colors font-sans text-sm ${titleDuplicateError ? 'border-destructive/40' : 'border-border'}`}
                      />
                      {titleDuplicateError ? (
                        <span className="relative flex items-center gap-2 px-4 py-2 text-warning-text flex-shrink-0 group/tip">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="absolute bottom-full right-0 mb-2 hidden group-hover/tip:block w-52 bg-primary text-primary-foreground text-[10px] font-sans px-3 py-2 rounded shadow-lg z-50 pointer-events-none">
                            Title has already been assigned
                          </span>
                        </span>
                      ) : (
                        <button
                          onClick={async () => {
                            if (!selectedApp || !approvedTitleInput.trim()) return;
                            try {
                              await client.graphql({
                                query: `mutation($input: UpdateApplicantYearProfileInput!) { updateApplicantYearProfile(input: $input) { applicantId } }`,
                                variables: { input: { applicantId: selectedApp.id, competitionYear, approvedTitle: approvedTitleInput.trim(), titleApproved: true } },
                                authMode: 'userPool',
                              });
                              setSelectedProfile((prev: any) => prev ? { ...prev, approvedTitle: approvedTitleInput.trim(), titleApproved: true } : prev);
                              try {
                                await client.graphql({
                                  query: `mutation($input: UpdateApplicantYearProfileInput!) { updateApplicantYearProfile(input: $input) { applicantId } }`,
                                  variables: { input: { applicantId: selectedApp.id, competitionYear, titleApprovalDate: new Date().toISOString() } },
                                  authMode: 'userPool',
                                });
                              } catch {}
                              fetchApplicants();
                            } catch (err) { console.error('Failed to approve title:', err); }
                          }}
                          disabled={!approvedTitleInput.trim()}
                          className="flex items-center gap-2 px-4 py-2 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        >
                          <Check className="w-3 h-3" />
                          Approve
                        </button>
                      )}
                    </div>
                    {titleDuplicateError && (
                      <p className="text-eyebrow-sm text-destructive-text">{titleDuplicateError}</p>
                    )}
                  </div>
                )}
              </section>

              {/* Contact Info */}
              <section className="space-y-6">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Contact Details</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="font-sans text-sm text-foreground">{selectedApp.email}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span className="font-sans text-sm text-foreground">{(() => {
                      const bg = selectedProfile.phase1Data?.generalBackground;
                      if (bg) { try { const parsed = JSON.parse(bg); if (parsed.phoneNumber) return parsed.phoneNumber; } catch {} }
                      return selectedApp.mobilePhone || selectedApp.homePhone || '—';
                    })()}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <span className="font-sans text-sm text-foreground whitespace-pre-line">{(() => {
                      const bg = selectedProfile.phase1Data?.generalBackground;
                      if (bg) { try { return formatAddress(JSON.parse(bg), selectedProfile.phase1Data?.birthplace); } catch {} }
                      return selectedProfile.phase1Data?.birthplace || '—';
                    })()}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-sans text-[10px] font-bold text-muted-foreground">AGE</span>
                    <span className="font-sans text-sm text-foreground">{(() => {
                      const dob = selectedProfile.phase1Data?.dateOfBirth;
                      if (!dob) return '—';
                      const birth = new Date(dob);
                      const today = new Date();
                      let age = today.getFullYear() - birth.getFullYear();
                      const monthDiff = today.getMonth() - birth.getMonth();
                      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
                      return age > 0 ? `${age} Years Old` : '—';
                    })()}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <AtSign className="w-4 h-4 text-muted-foreground" />
                    <span className="font-sans text-sm text-foreground">{(() => {
                      const bg = selectedProfile.phase1Data?.generalBackground;
                      if (!bg) return '—';
                      try { const parsed = JSON.parse(bg); return parsed.socialMediaHandle || '—'; } catch { return '—'; }
                    })()}</span>
                  </div>
                </div>
              </section>

              {/* Background */}
              <section className="space-y-6">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Background</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Briefcase className="w-4 h-4 text-muted-foreground mt-1" />
                    <div>
                      <p className="text-eyebrow-sm text-primary">Occupation</p>
                      <p className="font-sans text-sm text-foreground">{selectedProfile.phase1Data?.occupation || '—'}</p>
                    </div>
                  </div>
                  {selectedProfile.phase1Data?.currentCommunityInvolvement && (
                    <div className="flex items-start gap-3">
                      <Heart className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Community Involvement</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.phase1Data.currentCommunityInvolvement}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.phase1Data?.hobbies && (
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Hobbies & Talents</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.phase1Data.hobbies}{selectedProfile.phase1Data.talents ? ` · ${selectedProfile.phase1Data.talents}` : ''}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.applicantType === 'MRS' && selectedProfile.phase1Data?.spouseName && (
                    <div className="flex items-start gap-3">
                      <Heart className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Spouse</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.phase1Data.spouseName}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* Status Info */}
              <section className="space-y-6">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Application Status</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-4 border rounded flex items-center gap-2 ${
                    selectedProfile.submissionStatus === 'APPROVED' ? 'bg-success-muted border-success/20' :
                    selectedProfile.submissionStatus === 'REJECTED' ? 'bg-destructive-muted border-destructive/20' :
                    'bg-muted border-border'
                  }`}>
                    {selectedProfile.submissionStatus === 'APPROVED' ? (
                      <CheckCircle className="w-4 h-4 text-success-text flex-shrink-0" />
                    ) : selectedProfile.submissionStatus === 'REJECTED' ? (
                      <AlertCircle className="w-4 h-4 text-destructive-text flex-shrink-0" />
                    ) : (
                      <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                    <span className={`font-sans text-[10px] uppercase tracking-[0.2em] font-bold ${
                      selectedProfile.submissionStatus === 'APPROVED' ? 'text-success-text' :
                      selectedProfile.submissionStatus === 'REJECTED' ? 'text-destructive-text' :
                      'text-foreground'
                    }`}>{selectedProfile.submissionStatus}</span>
                  </div>
                  <div className={`p-4 border rounded flex items-center gap-2 ${
                    selectedProfile.digitalSignature ? 'bg-success-muted border-success/20' : 'bg-muted border-border'
                  }`}>
                    {selectedProfile.digitalSignature ? (
                      <CheckCircle className="w-4 h-4 text-success-text flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}
                    <span className={`font-sans text-[10px] uppercase tracking-[0.2em] font-bold ${
                      selectedProfile.digitalSignature ? 'text-success-text' : 'text-foreground'
                    }`}>{selectedProfile.digitalSignature ? 'Contract Signed' : 'Not Signed'}</span>
                  </div>
                </div>
                {selectedProfile.directorNotes && (
                  <div className="bg-primary/20 p-4">
                    <p className="text-eyebrow-sm text-foreground font-bold mb-1">Director Notes</p>
                    <p className="font-sans text-sm text-muted-foreground italic">{selectedProfile.directorNotes}</p>
                  </div>
                )}
              </section>

              {/* Quick link to documents */}
              {(() => {
                const allVerified = selectedAppDocStatus && selectedAppDocStatus.total > 0 && selectedAppDocStatus.verified === selectedAppDocStatus.total;
                const hasUnverified = selectedAppDocStatus && selectedAppDocStatus.total > 0 && selectedAppDocStatus.verified < selectedAppDocStatus.total;
                return (
                  <Link
                    to={`/admin/documents?applicant=${selectedApp.id}`}
                    className={`flex items-center justify-between p-4 border transition-all group no-underline ${
                      allVerified
                        ? 'border-success/20 bg-success-muted hover:border-success'
                        : 'border-border bg-muted hover:border-primary'
                    }`}
                    onClick={() => { setSelectedApp(null); setSelectedProfile(null); }}
                  >
                    <div className="flex items-center gap-3">
                      {allVerified ? <CheckCircle className="w-4 h-4 text-success-text" /> : <FileText className="w-4 h-4 text-muted-foreground" />}
                      <span className={`font-sans text-[10px] uppercase tracking-[0.2em] font-bold ${allVerified ? 'text-success-text' : 'text-foreground'}`}>
                        {allVerified ? 'Documents Verified' : 'View Documents'}
                      </span>
                      {selectedAppDocStatus && (
                        <span className={`text-eyebrow-xs ${allVerified ? 'text-success-text' : hasUnverified ? 'text-destructive-text' : 'text-muted-foreground'}`}>
                          {selectedAppDocStatus.verified}/{selectedAppDocStatus.total}
                        </span>
                      )}
                    </div>
                    <ExternalLink className={`w-4 h-4 transition-colors ${allVerified ? 'text-success-text group-hover:text-success-text' : 'text-muted-foreground group-hover:text-foreground'}`} />
                  </Link>
                );
              })()}

              {/* Pageant Application Fee Paid */}
              <div className={`flex items-center gap-3 p-4 border rounded cursor-pointer transition-all ${
                selectedAppDuesPaid ? 'bg-success-muted border-success/20' : 'bg-muted border-border hover:border-primary'
              }`} onClick={async () => {
                const next = !selectedAppDuesPaid;
                setSelectedAppDuesPaid(next);
                try {
                  await client.graphql({
                    query: `mutation($input: UpdateApplicantYearProfileInput!) { updateApplicantYearProfile(input: $input) { applicantId } }`,
                    variables: { input: { applicantId: selectedApp.id, competitionYear, duesPaid: next } },
                    authMode: 'userPool',
                  });
                } catch (err) { console.error('Failed to update dues paid:', err); }
              }}>
                <input type="checkbox" checked={selectedAppDuesPaid} readOnly className="w-4 h-4 accent-green-600 cursor-pointer" />
                <span className={`font-sans text-[10px] uppercase tracking-[0.2em] font-bold ${selectedAppDuesPaid ? 'text-success-text' : 'text-foreground'}`}>
                  {selectedAppDuesPaid ? 'Pageant Application Fee Paid' : 'Pageant Application Fee Not Paid'}
                </span>
              </div>

              {/* Export Profile button — shown when approved */}
              {selectedProfile.submissionStatus === 'APPROVED' && (
                <button
                  onClick={() => {
                    const data = buildExportData(selectedApp, selectedProfile);
                    exportProfilePdf(data, tenantCopy);
                  }}
                  className="w-full flex items-center justify-between p-4 min-h-[var(--touch-target-min)] border border-border bg-card hover:border-primary transition-all group mt-4 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <Download className="w-4 h-4 text-muted-foreground" />
                    <span className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-foreground">Export Profile</span>
                  </div>
                </button>
              )}

              {/* ── Phase 2 (post-approval) read-only sections ──────────────
                  Visible once the applicant is APPROVED. Mirrors the
                  read-only render approach used on the Participants page
                  (admin detail), reusing the same profile data sources
                  (stageFormData / interviewInfoData / placementQuestionsData). */}
              {selectedProfile.submissionStatus === 'APPROVED' && selectedProfile.stageFormData && (
                <section className="space-y-6">
                  <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Stage Form</h3>
                  <div className="space-y-4">
                    {selectedProfile.stageFormData.stageName && (
                      <div className="flex items-start gap-3">
                        <Star className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Stage Name</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.stageFormData.stageName}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.stageFormData.height && (
                      <div className="flex items-start gap-3">
                        <User className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Height</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.stageFormData.height}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.stageFormData.occupation && (
                      <div className="flex items-start gap-3">
                        <Briefcase className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Occupation</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.stageFormData.occupation}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.stageFormData.fitnessRoutine && (
                      <div className="flex items-start gap-3">
                        <Heart className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Fitness Routine</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.stageFormData.fitnessRoutine}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.stageFormData.mostImportantLearning && (
                      <div className="flex items-start gap-3">
                        <FileText className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Most Important Learning</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.stageFormData.mostImportantLearning}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {selectedProfile.submissionStatus === 'APPROVED' && selectedProfile.interviewInfoData && (
                <section className="space-y-6">
                  <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Interview Info Sheet</h3>
                  <div className="space-y-4">
                    {selectedProfile.interviewInfoData.education && (
                      <div className="flex items-start gap-3">
                        <FileText className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Education</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.education}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.interviewInfoData.occupation && (
                      <div className="flex items-start gap-3">
                        <Briefcase className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Occupation</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.occupation}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.interviewInfoData.careerGoals && (
                      <div className="flex items-start gap-3">
                        <Star className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Career Goals</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.careerGoals}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.interviewInfoData.communityInvolvement && (
                      <div className="flex items-start gap-3">
                        <Heart className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Community Involvement</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.communityInvolvement}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.interviewInfoData.leadershipRoles && (
                      <div className="flex items-start gap-3">
                        <Crown className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Leadership Roles</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.leadershipRoles}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.interviewInfoData.interestingFacts && (
                      <div className="flex items-start gap-3">
                        <Star className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Interesting Facts</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.interestingFacts}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.interviewInfoData.pageantAccomplishmentGoals && (
                      <div className="flex items-start gap-3">
                        <FileText className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Pageant Accomplishment Goals</p>
                          <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.pageantAccomplishmentGoals}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {selectedProfile.submissionStatus === 'APPROVED' && selectedProfile.placementQuestionsData && (
                <section className="space-y-6">
                  <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Placement Questions</h3>
                  <div className="space-y-4">
                    {selectedProfile.placementQuestionsData.top15Response && (
                      <div className="flex items-start gap-3">
                        <FileText className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Top 15 Response</p>
                          <p className="font-sans text-sm text-foreground leading-relaxed whitespace-pre-line">{selectedProfile.placementQuestionsData.top15Response}</p>
                        </div>
                      </div>
                    )}
                    {selectedProfile.placementQuestionsData.interviewJudgesQuestion && (
                      <div className="flex items-start gap-3">
                        <FileText className="w-4 h-4 text-muted-foreground mt-1" />
                        <div>
                          <p className="text-eyebrow-sm text-primary">Interview Judges Question</p>
                          <p className="font-sans text-sm text-foreground leading-relaxed whitespace-pre-line">{selectedProfile.placementQuestionsData.interviewJudgesQuestion}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>

            {/* Footer actions */}
            <div className="p-8 border-t border-border bg-muted/50">
              {selectedProfile.submissionStatus === 'SUBMITTED' ? (
                showRejectInput ? (
                  <div className="space-y-4">
                    <textarea
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      placeholder="Reason for rejection (required)..."
                      rows={3}
                      className="w-full border border-border p-3 font-sans text-sm outline-none focus:border-primary transition-colors"
                    />
                    <div className="flex gap-4">
                      <button
                        onClick={() => handleReject(selectedApp.id, rejectNote)}
                        disabled={!rejectNote.trim() || processing}
                        className="flex-grow bg-destructive text-destructive-foreground py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {processing ? 'Rejecting…' : 'Confirm Rejection'}
                      </button>
                      <button
                        onClick={() => { setShowRejectInput(false); setRejectNote(''); }}
                        className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : showReturnInput ? (
                  <div className="space-y-4">
                    <textarea
                      value={returnNote}
                      onChange={(e) => setReturnNote(e.target.value)}
                      placeholder="Explain what needs to be corrected..."
                      rows={3}
                      className="w-full border border-border p-3 font-sans text-sm outline-none focus:border-primary transition-colors"
                    />
                    <div className="flex gap-4">
                      <button
                        onClick={() => handleReturn(selectedApp.id, returnNote)}
                        disabled={!returnNote.trim() || processing}
                        className="flex-grow border border-primary bg-primary text-primary-foreground py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {processing ? 'Returning…' : 'Return to Applicant'}
                      </button>
                      <button
                        onClick={() => { setShowReturnInput(false); setReturnNote(''); }}
                        className="px-6 py-3 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Approval readiness check */}
                    {(() => {
                      const docsVerified = selectedAppDocStatus && selectedAppDocStatus.total > 0 && selectedAppDocStatus.verified === selectedAppDocStatus.total;
                      const titleOk = !!selectedProfile?.titleApproved;
                      const duesPaid = selectedAppDuesPaid;
                      const canApprove = docsVerified && titleOk && duesPaid;
                      const blockers: string[] = [];
                      if (!titleOk) blockers.push('Title not approved');
                      if (!docsVerified) blockers.push('Documents not verified');
                      if (!duesPaid) blockers.push('Pageant application fee not paid');
                      return (
                        <>
                          {!canApprove && blockers.length > 0 && (
                            <div className="text-center space-y-1">
                              {blockers.map((b, i) => (
                                <p key={i} className="text-eyebrow-xs text-destructive-text">⚠ {b}</p>
                              ))}
                            </div>
                          )}
                          {approvalError && (
                            <p className="text-destructive-text font-sans text-xs mb-3">{approvalError}</p>
                          )}
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleApprove(selectedApp.id)}
                              disabled={processing || !canApprove}
                              className="flex-grow py-4 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold transition-all rounded bg-success-muted text-success-text hover:bg-success hover:text-success-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {processing ? 'Processing…' : 'Approve'}
                            </button>
                            <button
                              onClick={() => setShowReturnInput(true)}
                              disabled={processing}
                              className="flex-grow py-4 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold transition-all rounded border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary disabled:opacity-50"
                            >
                              Return
                            </button>
                            <button
                              onClick={() => setShowRejectInput(true)}
                              disabled={processing}
                              className="flex-grow py-4 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold transition-all rounded bg-destructive-muted text-destructive-text hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )
              ) : selectedProfile.submissionStatus === 'APPROVED' ? (
                <div className="space-y-3">
                  <div className="w-full text-center py-4 font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-success-text bg-success-muted border border-success/20 flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Application Approved
                  </div>
                  <button
                    onClick={async () => {
                      if (!selectedApp) return;
                      setProcessing(true);
                      try {
                        if (!selectedApp.cognitoSub) throw new Error(`${selectedApp.fullName || 'Applicant'} has no linked account to revoke approval on the application record.`);
                        const competitionId = resolveActiveCompetitionId(competitionYear, user?.activeTenantId);
                        const applicantPersonId = await personIdForCognitoSub(selectedApp.cognitoSub);
                        // Revoke approval = REOPEN the decided application (→ SUBMITTED) on the SSOT.
                        await applicationFormClient.reviewApplicationResponse(competitionId, applicantPersonId, 'REOPEN');
                        logSystemAction({ action: 'RETURN_APPLICATION', performedBy: user?.email || 'Admin', targetName: selectedApp.fullName, targetEmail: selectedApp.email, targetId: selectedApp.id, details: 'Revoked application approval — returned to SUBMITTED' });
                        fetchApplicants();
                        invalidateApprovalAccess();
                        setSelectedProfile((prev: any) => prev ? { ...prev, submissionStatus: 'SUBMITTED' } : prev);
                      } catch (err) { console.error('Revoke failed:', err); }
                      finally { setProcessing(false); }
                    }}
                    disabled={processing}
                    className="w-full py-3 min-h-[var(--touch-target-min)] font-sans text-[10px] uppercase tracking-[0.2em] font-bold transition-all border border-warning/40 text-warning-text hover:bg-warning-muted bg-transparent cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Undo2 className="w-4 h-4" /> {processing ? 'Revoking…' : 'Revoke Approval'}
                  </button>
                </div>
              ) : (
                <div className="w-full text-center py-4 font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground bg-card border border-border rounded-card">
                  Application Status: {selectedProfile.submissionStatus}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Admin Calendar Wrapper (hides create for judges) ───────────────────────
function AdminCalendarWrapper() {
  const { role, isPowerUser } = useAuth();
  // Admins and PowerUsers both edit events. The countdown-anchor checkbox in
  // the create/edit modals is role-gated by `isPowerUser` separately so that
  // future role splits don't have to re-derive it from `isAdmin`.
  return <CalendarPage isAdmin={role === 'admin' || role === 'poweruser'} isPowerUser={isPowerUser} />;
}

// ─── Admin Participants Page (card grid) ────────────────────────────────────

function AdminParticipantsPage() {
  const { role, isDirector } = useAuth();
  const tenantCopy = useTenantCopy();
  const [applicants, setApplicants] = useState<ApplicantWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState<ApplicantWithProfile | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  // Item 4c (May 2026): tabbed participant detail. The slide-over now
  // renders one tab at a time instead of all sections in a single
  // scroll. Tabs that depend on Phase 2 data (Stage Form, Interview
  // Info, Placement Questions) auto-appear once the contestant
  // submits the corresponding form. The State Costume tab is always
  // visible to admins/judges since costume submission lives in
  // phase1Data.generalBackground regardless of approval status.
  type ParticipantTab =
    | 'basic-info'
    | 'additional-details'
    | 'costume'
    | 'stage-form'
    | 'interview-info'
    | 'placement-questions';
  const [participantTab, setParticipantTab] = useState<ParticipantTab>('basic-info');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportSections, setExportSections] = useState<ExportSections>({ ...DEFAULT_EXPORT_SECTIONS });
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lockingProfiles, setLockingProfiles] = useState(false);
  const [cardFilter, setCardFilter] = useState<'all' | 'mrs' | 'miss' | 'locked' | 'unlocked'>('all');
  // Season SSOT: operative competition year from the active-season seam.
  const { operativeYear: competitionYear } = useActiveSeason();

  // Crop modal state (avatar-crop feature, Requirements: 11.1, 11.2, 11.3, 11.4)
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState<string>('');
  const [cropTargetParticipant, setCropTargetParticipant] = useState<ApplicantWithProfile | null>(null);
  const [cropTargetCropParams, setCropTargetCropParams] = useState<CropParams | null>(null);
  const { cropDirectorPhoto } = useCropProfilePhoto();

  /**
   * Opens the CropModal for a participant's avatar.
   * Fetches the presigned URL for the original image and existing crop params.
   * Requirements: 11.1, 11.2
   */
  const handleEditCrop = useCallback(async (participant: ApplicantWithProfile) => {
    const s3Key = (participant as any).profilePhotoS3Key;
    if (!s3Key) return;

    try {
      // Fetch presigned URL for the original image
      const urlResult = (await client.graphql({
        query: getProfilePhotoDownloadUrl,
        variables: { s3Key },
        authMode: 'userPool',
      })) as GraphQLResult<{ getProfilePhotoDownloadUrl: string }>;

      const url = urlResult.data?.getProfilePhotoDownloadUrl;
      if (!url) return;

      // Fetch existing crop params for this participant
      let existingCropParams: CropParams | null = null;
      try {
        const userResult = (await client.graphql({
          query: `query($email: String!) { getUserByEmail(email: $email) { cognitoSub cropParams { x y width height } } }`,
          variables: { email: participant.email },
          authMode: 'userPool',
        })) as GraphQLResult<{ getUserByEmail: { cognitoSub?: string; cropParams?: CropParams | null } | null }>;
        existingCropParams = userResult.data?.getUserByEmail?.cropParams ?? null;
      } catch {
        // Non-fatal — proceed without existing crop params
      }

      setCropImageUrl(url);
      setCropTargetParticipant(participant);
      setCropTargetCropParams(existingCropParams);
      setCropModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch image for crop:', err);
    }
  }, []);

  /**
   * Called when Director confirms crop in the CropModal.
   * Calls cropDirectorProfilePhoto mutation with targetUserId.
   * Requirements: 11.3
   */
  const handleCropConfirm = useCallback(async (params: CropParams): Promise<void> => {
    if (!cropTargetParticipant) return;

    const s3Key = (cropTargetParticipant as any).profilePhotoS3Key;
    if (!s3Key) return;

    // Get the target user's cognitoSub via email lookup
    let targetUserId: string | null = null;
    try {
      const userResult = (await client.graphql({
        query: `query($email: String!) { getUserByEmail(email: $email) { cognitoSub } }`,
        variables: { email: cropTargetParticipant.email },
        authMode: 'userPool',
      })) as GraphQLResult<{ getUserByEmail: { cognitoSub: string } | null }>;
      targetUserId = userResult.data?.getUserByEmail?.cognitoSub ?? null;
    } catch {
      // Fall back to applicant's cognitoSub if available
      targetUserId = cropTargetParticipant.cognitoSub ?? null;
    }

    if (!targetUserId) {
      console.error('Could not determine target user ID for crop');
      return;
    }

    const result = await cropDirectorPhoto(targetUserId, s3Key, params);
    if (result) {
      setCropModalOpen(false);
      setCropTargetParticipant(null);
    }
    // If result is null, the hook already showed a toast error.
    // Modal stays open for retry.
  }, [cropTargetParticipant, cropDirectorPhoto]);

  /**
   * Called when Director cancels the CropModal.
   */
  const handleCropCancel = useCallback(() => {
    setCropModalOpen(false);
    setCropTargetParticipant(null);
    setCropTargetCropParams(null);
  }, []);

  useEffect(() => {
    async function fetch() {
      try {
        // Fetch applicants and all Users in parallel. The User table is the
        // single source of truth for profilePhotoS3Key (the auth/display
        // projection of the Person SSOT). The legacy Applicant.profilePhotoS3Key
        // duplicate was removed (meridian-data-model task 12.1), so the avatar
        // is sourced entirely from the User table and overlaid by email.
        const [applicantResult, usersResult] = await Promise.all([
          client.graphql({
            query: `query { listApplicants(limit: 200) { items { id fullName email mobilePhone } } }`,
            authMode: 'userPool',
          }) as Promise<GraphQLResult<{ listApplicants: { items: ApplicantWithProfile[] } }>>,
          client.graphql({
            query: `query { listUsers(limit: 1000) { items { email profilePhotoS3Key } } }`,
            authMode: 'userPool',
          }) as Promise<GraphQLResult<{ listUsers: { items: Array<{ email: string; profilePhotoS3Key?: string | null }> } }>>,
        ]);
        const userPhotoByEmail = new Map<string, string>();
        for (const u of (usersResult.data?.listUsers?.items || [])) {
          if (u.email && u.profilePhotoS3Key) {
            userPhotoByEmail.set(u.email.toLowerCase(), u.profilePhotoS3Key);
          }
        }
        const rawApps = applicantResult.data?.listApplicants?.items || [];
        const apps = rawApps.map(a => {
          const userPhoto = a.email ? userPhotoByEmail.get(a.email.toLowerCase()) : undefined;
          // Avatar comes from the User table (SSOT).
          return { ...a, profilePhotoS3Key: userPhoto };
        });
        const withProfiles = await Promise.all(apps.map(async (app) => {
          try {
            const profileResult = await client.graphql({
              query: `query($aid: ID!) { listApplicantYearProfiles(applicantId: $aid) { competitionYear applicantType submissionStatus profileLocked requestedTitle approvedTitle titleApproved digitalSignature phase1Data { dateOfBirth birthplace usCitizen eyeColor hairColor occupation currentCommunityInvolvement hobbies talents spouseName marriageDate generalBackground } stageFormData { stageName height hairColor eyeColor occupation fitnessRoutine mostImportantLearning husbandName yearsMarried } interviewInfoData { nameAndTitle dateOfBirth cityOfResidency education occupation careerGoals activitiesAndHobbies communityInvolvement leadershipRoles interestingFacts pageantAccomplishmentGoals spouseName childrenNames childrenAges } placementQuestionsData { top15Response interviewJudgesQuestion } } }`,
              variables: { aid: app.id },
              authMode: 'userPool',
            }) as GraphQLResult<{ listApplicantYearProfiles: ApplicantWithProfile['yearProfiles'] }>;
            return { ...app, yearProfiles: profileResult.data?.listApplicantYearProfiles || [] };
          } catch { return { ...app, yearProfiles: [] }; }
        }));
        const withDocs = await Promise.all(withProfiles.map(async (app) => {
          let serviceHours = 0;
          try {
            const volResult = await client.graphql({
              query: `query($aid: ID!) { listVolunteerActivities(applicantId: $aid) { hoursContributed } }`,
              variables: { aid: app.id },
              authMode: 'userPool',
            }) as GraphQLResult<{ listVolunteerActivities: Array<{ hoursContributed: number }> }>;
            const activities = volResult.data?.listVolunteerActivities || [];
            serviceHours = activities.reduce((sum: number, a: { hoursContributed: number }) => sum + (a.hoursContributed || 0), 0);
          } catch { serviceHours = 0; }
          return { ...app, _serviceHours: serviceHours };
        }));
        setApplicants(withDocs as ApplicantWithProfile[]);
      } catch (err) { console.error('Failed to fetch:', err); }
      finally { setLoading(false); }
    }
    fetch();
  }, []);

  const participants = applicants.filter(a =>
    a.yearProfiles?.some(p => p.competitionYear === competitionYear && p.submissionStatus === 'APPROVED')
  );

  const filtered = participants.filter(a => {
    const profile = a.yearProfiles?.find(p => p.competitionYear === competitionYear);
    // Category filter
    if (cardFilter === 'mrs' && profile?.applicantType !== 'MRS') return false;
    if (cardFilter === 'miss' && profile?.applicantType !== 'MISS') return false;
    if (cardFilter === 'locked' && !profile?.profileLocked) return false;
    if (cardFilter === 'unlocked' && profile?.profileLocked) return false;
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return a.fullName.toLowerCase().includes(q) || a.email.toLowerCase().includes(q);
    }
    return true;
  });

  const openParticipantDetail = (p: ApplicantWithProfile) => {
    setSelectedParticipant(p);
    setParticipantTab('basic-info');
    const profile = p.yearProfiles?.find(pr => pr.competitionYear === competitionYear);
    setSelectedProfile(profile || null);
  };

  return (
    <div className="space-y-8">
      <PageHeader
        action={
          <div className="flex items-center gap-4 flex-wrap">
            {canPerformActions(role) && (
              <button
                onClick={() => { setMultiSelectMode(!multiSelectMode); setSelectedIds(new Set()); }}
                className={`pill-shape touch-target transition-colors cursor-pointer flex items-center gap-2 ${
                  multiSelectMode ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                <CheckCircle className="w-3.5 h-3.5" /> {multiSelectMode ? 'Cancel' : 'Select'}
              </button>
            )}
            {multiSelectMode && canPerformActions(role) && (
              <button
                onClick={() => {
                  if (selectedIds.size === filtered.length) {
                    setSelectedIds(new Set());
                  } else {
                    setSelectedIds(new Set(filtered.map(p => p.id)));
                  }
                }}
                className="pill-shape touch-target transition-colors cursor-pointer flex items-center gap-2 bg-card border border-border text-muted-foreground hover:bg-muted"
              >
                {selectedIds.size === filtered.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search contestants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-border bg-card font-sans text-xs outline-none focus:border-primary transition-colors w-full sm:w-64 rounded-input"
              />
            </div>
            {canPerformActions(role) && participants.length > 0 && (
              <button
                onClick={() => setShowExportDialog(true)}
                className="flex items-center gap-2 px-6 py-2 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground text-eyebrow-sm hover:bg-primary/90 transition-colors border-none cursor-pointer shadow-raised rounded-input"
              >
                <Download className="w-4 h-4" /> Export List
              </button>
            )}
          </div>
        }
      >
        Community
      </PageHeader>

      {/* Multi-select action bar */}
      {canPerformActions(role) && multiSelectMode && selectedIds.size > 0 && (() => {
        const filteredIds = new Set(filtered.map(p => p.id));
        const visibleSelected = [...selectedIds].filter(id => filteredIds.has(id));
        if (visibleSelected.length === 0) return null;
        return (
        <div className="flex items-center gap-4 bg-primary text-primary-foreground px-6 py-3 rounded-card shadow-prominent">
          <span className="font-sans text-xs uppercase tracking-widest font-bold">{visibleSelected.length} selected</span>
          <div className="flex-grow" />
          <button
            onClick={async () => {
              setLockingProfiles(true);
              for (const id of visibleSelected) {
                try {
                  await client.graphql({
                    query: `mutation($aid: ID!, $yr: Int!) { lockProfile(applicantId: $aid, competitionYear: $yr) { applicantId } }`,
                    variables: { aid: id, yr: competitionYear },
                    authMode: 'userPool',
                  });
                } catch (err) { console.error('Lock failed for', id, err); }
              }
              // Update local state to reflect lock
              setApplicants(prev => prev.map(a => {
                if (visibleSelected.includes(a.id)) {
                  return { ...a, yearProfiles: a.yearProfiles?.map(p => p.competitionYear === competitionYear ? { ...p, profileLocked: true } : p) };
                }
                return a;
              }));
              setLockingProfiles(false);
              setSelectedIds(new Set());
              setMultiSelectMode(false);
            }}
            disabled={lockingProfiles}
            className="flex items-center gap-2 px-5 py-2 min-h-[var(--touch-target-min)] bg-warning text-warning-foreground text-eyebrow-sm font-bold hover:bg-warning/90 transition-colors border-none cursor-pointer rounded-input disabled:opacity-50"
            title="Lock selected profiles"
          >
            <Lock className="w-3.5 h-3.5" /> {lockingProfiles ? 'Processing…' : 'Lock Profiles'}
          </button>
          <button
            onClick={async () => {
              setLockingProfiles(true);
              for (const id of visibleSelected) {
                try {
                  await client.graphql({
                    query: `mutation($input: UpdateApplicantYearProfileInput!) { updateApplicantYearProfile(input: $input) { applicantId } }`,
                    variables: { input: { applicantId: id, competitionYear, profileLocked: false } },
                    authMode: 'userPool',
                  });
                } catch (err) { console.error('Unlock failed for', id, err); }
              }
              // Update local state to reflect unlock
              setApplicants(prev => prev.map(a => {
                if (visibleSelected.includes(a.id)) {
                  return { ...a, yearProfiles: a.yearProfiles?.map(p => p.competitionYear === competitionYear ? { ...p, profileLocked: false } : p) };
                }
                return a;
              }));
              setLockingProfiles(false);
              setSelectedIds(new Set());
              setMultiSelectMode(false);
            }}
            disabled={lockingProfiles}
            className="flex items-center gap-2 px-5 py-2 min-h-[var(--touch-target-min)] bg-success text-success-foreground text-eyebrow-sm font-bold hover:bg-success/90 transition-colors border-none cursor-pointer rounded-input disabled:opacity-50"
          >
            <Lock className="w-3.5 h-3.5" /> Unlock Profiles
          </button>
          <button
            onClick={() => setShowExportDialog(true)}
            className="flex items-center gap-2 px-5 py-2 min-h-[var(--touch-target-min)] bg-card text-foreground text-eyebrow-sm font-bold hover:bg-muted transition-colors border-none cursor-pointer rounded-input"
          >
            <Download className="w-3.5 h-3.5" /> Export Selected
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-4 py-2 touch-target text-muted-foreground hover:text-primary-foreground text-eyebrow-sm transition-colors bg-transparent border-none cursor-pointer"
          >
            Clear
          </button>
        </div>
        );
      })()}

      {/* Filter buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        {([
          { label: 'All', value: 'all' as const },
          { label: 'Mrs.', value: 'mrs' as const },
          { label: 'Miss', value: 'miss' as const },
          ...(canPerformActions(role) ? [
            { label: 'Locked', value: 'locked' as const },
            { label: 'Unlocked', value: 'unlocked' as const },
          ] : []),
        ]).map(f => (
          <button
            key={f.value}
            onClick={() => setCardFilter(f.value)}
            className={`pill-shape touch-target transition-colors ${
              cardFilter === f.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-card border border-border text-muted-foreground hover:bg-muted'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground font-sans">Loading contestants…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-card shadow-sm p-12 text-center">
          <p className="text-muted-foreground font-sans italic">{searchQuery ? 'No matching contestants.' : 'No contestants found.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {filtered.map((p) => {
            const profile = p.yearProfiles?.find(pr => pr.competitionYear === competitionYear);
            // Req 2.3: the title fallback is the ACTIVE tenant's division title,
            // never an original-client literal.
            const title =
              profile?.approvedTitle || profile?.requestedTitle || divisionTitle(tenantCopy, profile?.applicantType);
            const city = (() => {
              const bg = profile?.phase1Data?.generalBackground;
              if (bg) { try { const parsed = JSON.parse(bg); if (parsed.addressCity) return parsed.addressCity; } catch {} }
              const raw = profile?.phase1Data?.birthplace || '';
              return raw.split(',')[0]?.trim() || '—';
            })();
            const serviceHours = (p as any)._serviceHours || 0;

            return (
              <div
                key={p.id}
                className={`bg-card border shadow-subtle group transition-all relative min-w-[280px] flex flex-col rounded-card overflow-hidden ${
                  multiSelectMode && selectedIds.has(p.id) ? 'border-primary ring-2 ring-ring' : 'border-border hover:shadow-xl hover:border-primary'
                }`}
                onClick={() => { if (multiSelectMode) { setSelectedIds(prev => { const next = new Set(prev); if (next.has(p.id)) next.delete(p.id); else next.add(p.id); return next; }); } }}
              >
                {/* Multi-select checkbox */}
                {canPerformActions(role) && multiSelectMode && (
                  <div className="absolute top-3 left-3 z-10">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedIds.has(p.id) ? 'bg-primary border-primary' : 'bg-card border-border'
                    }`}>
                      {selectedIds.has(p.id) && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                  </div>
                )}
                {/* Lock icon */}
                {canPerformActions(role) && profile?.profileLocked && (
                  <div className="absolute top-3 right-3 z-10" title="Profile Locked">
                    <Lock className="w-4 h-4 text-warning-text" />
                  </div>
                )}

                {/* Large photo area */}
                <div className="aspect-[3/4] overflow-hidden bg-muted relative">
                  <LegacyAvatar
                    profilePhotoS3Key={(p as any).profilePhotoS3Key ?? null}
                    fullName={p.fullName}
                    size="lg"
                    variant="contestant"
                    shape="rounded"
                    className="!w-full !h-full !min-w-0 !rounded-none !text-5xl"
                  />
                  {/* Edit crop button — Director only (Requirements: 11.1, 11.4) */}
                  {isDirector && (p as any).profilePhotoS3Key && (
                    <button
                      type="button"
                      data-testid="edit-crop-button"
                      onClick={(e) => { e.stopPropagation(); handleEditCrop(p); }}
                      className="absolute bottom-2 right-2 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-card/90 backdrop-blur-sm border border-border text-foreground text-[10px] font-sans uppercase tracking-widest font-bold rounded-input shadow-sm hover:bg-card transition-colors cursor-pointer"
                      aria-label={`Edit crop for ${p.fullName}`}
                    >
                      <Crop className="w-3 h-3" /> Edit crop
                    </button>
                  )}
                </div>

                {/* Details */}
                <div className="p-6 flex flex-col flex-grow cursor-pointer" onClick={() => openParticipantDetail(p)}>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xl font-display text-foreground">{p.fullName}</h3>
                    <span className="text-eyebrow-xs text-muted-foreground shrink-0 ml-3 flex items-center gap-1"><Crown className="w-3 h-3 shrink-0" />{title}</span>
                  </div>
                  <div className="w-10 h-[1px] bg-primary mb-4 mt-3" />
                  <div className="space-y-3">
                  {canSeePhase1(role) && role === 'admin' && (
                    <>
                      <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                        <span className="text-muted-foreground">Location</span>
                        <span className="text-foreground truncate ml-2">{city}</span>
                      </div>
                      <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                        <span className="text-muted-foreground">Service Hours</span>
                        <span className="text-foreground">{serviceHours} hrs</span>
                      </div>
                      {(() => {
                        const bg = profile?.phase1Data?.generalBackground;
                        let costumeStatus = 'Draft';
                        let pillClass = 'bg-muted text-primary';
                        if (bg) {
                          try {
                            const parsed = JSON.parse(bg);
                            if (parsed.costumeApproved) { costumeStatus = 'Approved'; pillClass = 'bg-success-muted text-success-text'; }
                            else if (parsed.costumeSubmitted) { costumeStatus = 'Pending Approval'; pillClass = 'bg-muted text-muted-foreground'; }
                          } catch {}
                        }
                        return (
                          <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                            <span className="text-muted-foreground">Costume</span>
                            <span className={`pill-shape ${pillClass}`}>{costumeStatus}</span>
                          </div>
                        );
                      })()}
                    </>
                  )}
                  {(role === 'judge' || role === 'poweruser') && (
                    <>
                      <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                        <span className="text-muted-foreground">Location</span>
                        <span className="text-foreground truncate ml-2">{city}</span>
                      </div>
                      <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                        <span className="text-muted-foreground">Service Hours</span>
                        <span className="text-foreground">{serviceHours} hrs</span>
                      </div>
                      {(() => {
                        const bg = profile?.phase1Data?.generalBackground;
                        let socialHandle = '';
                        let costumeTitle = '';
                        if (bg) { try { const parsed = JSON.parse(bg); socialHandle = parsed.socialMediaHandle || ''; costumeTitle = parsed.costumeTitle || ''; } catch {} }
                        return (
                          <>
                            {socialHandle && (
                              <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                                <span className="text-muted-foreground">Social</span>
                                <span className="text-foreground truncate ml-2">{socialHandle}</span>
                              </div>
                            )}
                            {costumeTitle && (
                              <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                                <span className="text-muted-foreground">Costume</span>
                                <span className="text-foreground truncate ml-2">{costumeTitle}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                  {canSeePhase2(role) && role === 'admin' && (
                    <>
                      <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                        <span className="text-muted-foreground">Stage Form</span>
                        <span className={`pill-shape ${profile?.stageFormData?.stageName ? 'bg-success-muted text-success-text' : 'bg-muted text-primary'}`}>
                          {profile?.stageFormData?.stageName ? 'Submitted' : 'Pending'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                        <span className="text-muted-foreground">Interview Info</span>
                        <span className={`pill-shape ${profile?.interviewInfoData?.nameAndTitle ? 'bg-success-muted text-success-text' : 'bg-muted text-primary'}`}>
                          {profile?.interviewInfoData?.nameAndTitle ? 'Submitted' : 'Pending'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                        <span className="text-muted-foreground">Placement</span>
                        <span className={`pill-shape ${profile?.placementQuestionsData?.top15Response ? 'bg-success-muted text-success-text' : 'bg-muted text-primary'}`}>
                          {profile?.placementQuestionsData?.top15Response ? 'Submitted' : 'Pending'}
                        </span>
                      </div>
                    </>
                  )}
                  {canSeeMarriageInfo(role) && role === 'admin' && profile?.applicantType === 'MRS' && profile?.phase1Data?.spouseName && (
                    <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest">
                      <span className="text-muted-foreground">Spouse</span>
                      <span className="text-foreground truncate ml-2">{profile.phase1Data.spouseName}</span>
                    </div>
                  )}
                  </div>
                  {/* Phase 2 Progress Bar (only for approved contestants, admin only) */}
                  {role === 'admin' && profile?.submissionStatus === 'APPROVED' && (() => {
                    const bg = profile?.phase1Data?.generalBackground;
                    let costumeSubmitted = false;
                    try { const parsed = JSON.parse(bg || '{}'); costumeSubmitted = !!parsed.costumeSubmitted; } catch {}
                    const hasStageForm = !!(profile?.stageFormData?.stageName || profile?.stageFormData?.height);
                    const hasInterviewInfo = !!(profile?.interviewInfoData?.nameAndTitle || profile?.interviewInfoData?.education);
                    const hasPlacement = !!(profile?.placementQuestionsData?.top15Response);
                    const items = [costumeSubmitted, hasStageForm, hasInterviewInfo, hasPlacement];
                    const done = items.filter(Boolean).length;
                    const total = items.length;
                    const pct = Math.round((done / total) * 100);
                    return (
                      <div className="pt-2">
                        <div className="flex items-center justify-between text-[10px] font-sans uppercase tracking-widest mb-1.5">
                          <span className="text-muted-foreground">Phase 2</span>
                          <span className="text-muted-foreground font-bold">{pct}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[8px] text-primary font-sans mt-1">{done}/{total} forms completed</p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Export List Dialog — Admin only */}
      {canPerformActions(role) && showExportDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setShowExportDialog(false)} />
          <div className="relative bg-card rounded-card border border-border shadow-prominent w-full max-w-md overflow-hidden">
            <div className="p-8 border-b border-border flex items-center justify-between bg-muted/50">
              <h3 className="text-2xl font-display ">Export Contestant List</h3>
              <button onClick={() => setShowExportDialog(false)} className="text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none cursor-pointer touch-target">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-8 space-y-6">
              <p className="font-sans text-sm text-muted-foreground">
                Select which sections to include in the PDF export for {participants.length} participant{participants.length !== 1 ? 's' : ''}.
              </p>
              <div className="space-y-3">
                {([
                  { key: 'contactInfo' as const, label: 'Contact Information', desc: 'Email, phone, address, social media' },
                  { key: 'basicInfo' as const, label: 'Basic Information', desc: 'DOB, age, eye/hair color, occupation' },
                  { key: 'additionalDetails' as const, label: 'Additional Details', desc: 'Community involvement, hobbies, talents' },
                  { key: 'marriageInfo' as const, label: 'Marriage Information', desc: 'Spouse name, marriage date (Mrs. only)' },
                  { key: 'stageForm' as const, label: 'Stage Form', desc: 'Stage presentation details' },
                  { key: 'interviewInfo' as const, label: 'Interview Info Sheet', desc: 'Interview preparation data' },
                  { key: 'placementQuestions' as const, label: 'Placement Questions', desc: 'Competition placement responses' },
                ]).map(({ key, label, desc }) => (
                  <label key={key} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={exportSections[key]}
                      onChange={(e) => setExportSections(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="mt-1 h-4 w-4 rounded bg-muted border-none text-foreground focus:ring-ring/30"
                    />
                    <div>
                      <span className="font-sans text-xs font-bold text-foreground group-hover:text-muted-foreground transition-colors">{label}</span>
                      <p className="font-sans text-[9px] text-muted-foreground uppercase tracking-widest">{desc}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => {
                    const exportData = participants.map(p => {
                      const profile = p.yearProfiles?.find(pr => pr.competitionYear === competitionYear);
                      return buildExportData(p, profile);
                    });
                    exportListPdf(exportData, exportSections, tenantCopy);
                    setShowExportDialog(false);
                  }}
                  disabled={!Object.values(exportSections).some(Boolean)}
                  className="flex-grow flex items-center justify-center gap-2 py-4 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary transition-colors border-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" /> Export {participants.length} Profile{participants.length !== 1 ? 's' : ''}
                </button>
                <button
                  onClick={() => setShowExportDialog(false)}
                  className="px-6 py-4 min-h-[var(--touch-target-min)] border border-border rounded-input text-eyebrow-sm hover:bg-muted transition-colors cursor-pointer bg-transparent"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Crop Modal — Director crop for participant avatars (Requirements: 11.1, 11.2, 11.3) */}
      <CropModal
        open={cropModalOpen}
        imageUrl={cropImageUrl}
        initialCropParams={cropTargetCropParams}
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
      />

      {/* Participant Detail Slide-over */}
      {selectedParticipant && selectedProfile && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => { setSelectedParticipant(null); setSelectedProfile(null); }} />
          <div className="relative w-full max-w-xl bg-card h-full shadow-2xl flex flex-col">
            <div className="p-8 border-b border-border flex items-center justify-between bg-muted/50">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-display text-2xl ">
                  {selectedParticipant.fullName.charAt(0)}
                </div>
                <div>
                  <h2 className="text-3xl font-display ">{selectedParticipant.fullName}</h2>
                  <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                    {selectedProfile.approvedTitle || selectedProfile.requestedTitle || divisionTitle(tenantCopy, selectedProfile.applicantType)}
                  </span>
                </div>
              </div>
              <button onClick={() => { setSelectedParticipant(null); setSelectedProfile(null); }} className="p-2 touch-target hover:bg-muted rounded-full transition-colors bg-transparent border-none cursor-pointer">
                <X className="w-6 h-6 text-muted-foreground" />
              </button>
            </div>

            {/* Tab bar (item 4c) — Basic Info / Additional Details
                always visible; Phase 2 tabs auto-appear once the
                contestant has submitted the corresponding form. */}
            {(() => {
              const hasStageForm = !!selectedProfile.stageFormData;
              const hasInterview = !!selectedProfile.interviewInfoData;
              const hasPlacement = !!selectedProfile.placementQuestionsData;
              const tabs: Array<{ key: ParticipantTab; label: string; visible: boolean }> = [
                { key: 'basic-info', label: 'Basic Info', visible: true },
                { key: 'additional-details', label: 'Additional Details', visible: true },
                { key: 'costume', label: 'State Costume', visible: canSeePhase1(role) || role === 'judge' },
                { key: 'stage-form', label: 'Stage Form', visible: hasStageForm && canSeePhase2(role) && role !== 'judge' },
                { key: 'interview-info', label: 'Interview Info', visible: hasInterview && canSeePhase2(role) && role !== 'judge' },
                { key: 'placement-questions', label: 'Placement', visible: hasPlacement && canSeePhase2(role) && role !== 'judge' },
              ];
              const visibleTabs = tabs.filter(t => t.visible);
              // If the selected tab is no longer visible (e.g. data
              // changed and Phase 2 tab disappeared), fall back to
              // basic-info on the next render.
              if (!visibleTabs.find(t => t.key === participantTab)) {
                // Defer to avoid setState during render.
                queueMicrotask(() => setParticipantTab('basic-info'));
              }
              return (
                <div className="border-b border-border bg-card overflow-x-auto">
                  <div className="flex gap-1 px-4">
                    {visibleTabs.map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setParticipantTab(tab.key)}
                        className={`px-4 py-3 min-h-[var(--touch-target-min)] text-eyebrow-sm font-bold border-b-2 -mb-[1px] transition-colors whitespace-nowrap bg-transparent cursor-pointer ${
                          participantTab === tab.key
                            ? 'border-primary text-foreground'
                            : 'border-transparent text-primary hover:text-muted-foreground'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="flex-grow overflow-y-auto p-8 space-y-10">
              {/* Contact Info — Phase 1 data */}
              {participantTab === 'basic-info' && canSeePhase1(role) && (
              <section className="space-y-6">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Contact Details</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="font-sans text-sm text-foreground">{selectedParticipant.email}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span className="font-sans text-sm text-foreground">{(() => {
                      const bg = selectedProfile.phase1Data?.generalBackground;
                      if (bg) { try { const parsed = JSON.parse(bg); if (parsed.phoneNumber) return parsed.phoneNumber; } catch {} }
                      return selectedParticipant.mobilePhone || '—';
                    })()}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <span className="font-sans text-sm text-foreground whitespace-pre-line">{(() => {
                      const bg = selectedProfile.phase1Data?.generalBackground;
                      if (bg) { try { return formatAddress(JSON.parse(bg), selectedProfile.phase1Data?.birthplace); } catch {} }
                      return selectedProfile.phase1Data?.birthplace || '—';
                    })()}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-sans text-[10px] font-bold text-muted-foreground">AGE</span>
                    <span className="font-sans text-sm text-foreground">{(() => {
                      const dob = selectedProfile.phase1Data?.dateOfBirth;
                      if (!dob) return '—';
                      const birth = new Date(dob);
                      const today = new Date();
                      let age = today.getFullYear() - birth.getFullYear();
                      const monthDiff = today.getMonth() - birth.getMonth();
                      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
                      return age > 0 ? `${age} Years Old` : '—';
                    })()}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <AtSign className="w-4 h-4 text-muted-foreground" />
                    <span className="font-sans text-sm text-foreground">{(() => {
                      const bg = selectedProfile.phase1Data?.generalBackground;
                      if (!bg) return '—';
                      try { const parsed = JSON.parse(bg); return parsed.socialMediaHandle || '—'; } catch { return '—'; }
                    })()}</span>
                  </div>
                </div>
              </section>
              )}

              {/* Social Media — visible to judges (and admins via Contact Details above) */}
              {participantTab === 'additional-details' && !canSeePhase1(role) && canSeeSocialMedia(role) && (
              <section className="space-y-6">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Social Media</h3>
                <div className="flex items-center gap-3">
                  <AtSign className="w-4 h-4 text-muted-foreground" />
                  <span className="font-sans text-sm text-foreground">{(() => {
                    const bg = selectedProfile.phase1Data?.generalBackground;
                    if (!bg) return '—';
                    try { const parsed = JSON.parse(bg); return parsed.socialMediaHandle || '—'; } catch { return '—'; }
                  })()}</span>
                </div>
              </section>
              )}

              {/* Marriage Info — visible to judges (and admins via Background below) */}
              {participantTab === 'additional-details' && !canSeePhase1(role) && canSeeMarriageInfo(role) && selectedProfile.applicantType === 'MRS' && (
              <section className="space-y-6">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Marriage Information</h3>
                <div className="space-y-4">
                  {selectedProfile.phase1Data?.spouseName && (
                    <div className="flex items-start gap-3">
                      <Heart className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Spouse Name</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.phase1Data.spouseName}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.phase1Data?.marriageDate && (
                    <div className="flex items-start gap-3">
                      <Calendar className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Marriage Date</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.phase1Data.marriageDate}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
              )}

              {/* Background — Phase 1 data (visible to admins, powerusers, and judges) */}
              {participantTab === 'additional-details' && (canSeePhase1(role) || role === 'judge') && (
              <section className="space-y-6">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Background</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Briefcase className="w-4 h-4 text-muted-foreground mt-1" />
                    <div>
                      <p className="text-eyebrow-sm text-primary">Occupation</p>
                      <p className="font-sans text-sm text-foreground">{selectedProfile.phase1Data?.occupation || '—'}</p>
                    </div>
                  </div>
                  {selectedProfile.phase1Data?.currentCommunityInvolvement && (
                    <div className="flex items-start gap-3">
                      <Heart className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Community Involvement</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.phase1Data.currentCommunityInvolvement}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.phase1Data?.hobbies && (
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Hobbies & Talents</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.phase1Data.hobbies}{selectedProfile.phase1Data.talents ? ` · ${selectedProfile.phase1Data.talents}` : ''}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.applicantType === 'MRS' && selectedProfile.phase1Data?.spouseName && (
                    <div className="flex items-start gap-3">
                      <Heart className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Spouse</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.phase1Data.spouseName}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
              )}

              {/* Phase 2 Data — Stage Form (admin only, not judges) */}
              {participantTab === 'stage-form' && canSeePhase2(role) && role !== 'judge' && selectedProfile.stageFormData && (
              <section className="space-y-6">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Stage Form</h3>
                <div className="space-y-4">
                  {selectedProfile.stageFormData.stageName && (
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Stage Name</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.stageFormData.stageName}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.stageFormData.height && (
                    <div className="flex items-start gap-3">
                      <User className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Height</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.stageFormData.height}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.stageFormData.occupation && (
                    <div className="flex items-start gap-3">
                      <Briefcase className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Occupation</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.stageFormData.occupation}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.stageFormData.fitnessRoutine && (
                    <div className="flex items-start gap-3">
                      <Heart className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Fitness Routine</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.stageFormData.fitnessRoutine}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.stageFormData.mostImportantLearning && (
                    <div className="flex items-start gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Most Important Learning</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.stageFormData.mostImportantLearning}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
              )}

              {/* Phase 2 Data — Interview Info Sheet (admin only, not judges) */}
              {participantTab === 'interview-info' && canSeePhase2(role) && role !== 'judge' && selectedProfile.interviewInfoData && (
              <section className="space-y-6">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Interview Info Sheet</h3>
                <div className="space-y-4">
                  {selectedProfile.interviewInfoData.education && (
                    <div className="flex items-start gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Education</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.education}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.interviewInfoData.occupation && (
                    <div className="flex items-start gap-3">
                      <Briefcase className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Occupation</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.occupation}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.interviewInfoData.careerGoals && (
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Career Goals</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.careerGoals}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.interviewInfoData.communityInvolvement && (
                    <div className="flex items-start gap-3">
                      <Heart className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Community Involvement</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.communityInvolvement}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.interviewInfoData.leadershipRoles && (
                    <div className="flex items-start gap-3">
                      <Crown className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Leadership Roles</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.leadershipRoles}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.interviewInfoData.interestingFacts && (
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Interesting Facts</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.interestingFacts}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.interviewInfoData.pageantAccomplishmentGoals && (
                    <div className="flex items-start gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Pageant Accomplishment Goals</p>
                        <p className="font-sans text-sm text-foreground">{selectedProfile.interviewInfoData.pageantAccomplishmentGoals}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
              )}

              {/* Phase 2 Data — Placement Questions (admin only, not judges) */}
              {participantTab === 'placement-questions' && canSeePhase2(role) && role !== 'judge' && selectedProfile.placementQuestionsData && (
              <section className="space-y-6">
                <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">Placement Questions</h3>
                <div className="space-y-4">
                  {selectedProfile.placementQuestionsData.top15Response && (
                    <div className="flex items-start gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Top 15 Response</p>
                        <p className="font-sans text-sm text-foreground leading-relaxed whitespace-pre-line">{selectedProfile.placementQuestionsData.top15Response}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.placementQuestionsData.interviewJudgesQuestion && (
                    <div className="flex items-start gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-eyebrow-sm text-primary">Interview Judges Question</p>
                        <p className="font-sans text-sm text-foreground leading-relaxed whitespace-pre-line">{selectedProfile.placementQuestionsData.interviewJudgesQuestion}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
              )}

              {/* State Costume Submission — visible to admins, powerusers, and judges */}
              {participantTab === 'costume' && (canSeePhase1(role) || role === 'judge') && (() => {
                const bg = selectedProfile.phase1Data?.generalBackground;
                let costume: any = {};
                if (bg) { try { costume = JSON.parse(bg); } catch {} }
                if (!costume.costumeSubmission) return null;
                return (
                  <section className="space-y-4">
                    <h3 className="text-eyebrow-sm font-bold text-foreground border-b border-border pb-2 font-sans">State Costume Submission</h3>
                    {costume.costumeTitle && (
                      <p className="font-display text-lg text-foreground">{costume.costumeTitle}</p>
                    )}
                    <p className="font-sans text-sm text-foreground leading-relaxed whitespace-pre-line">{costume.costumeSubmission}</p>
                    {costume.costumeApproved ? (
                      <div className="flex items-center gap-2 bg-success-muted px-3 py-2 rounded">
                        <CheckCircle className="w-4 h-4 text-success-text" />
                        <span className="text-eyebrow-xs text-success-text font-bold">Costume Approved</span>
                      </div>
                    ) : costume.costumeSubmitted && canPerformActions(role) ? (
                      <button
                        onClick={async () => {
                          try {
                            const updatedBg = { ...costume, costumeApproved: true };
                            // Preserve all existing phase1Data fields — only update generalBackground
                            const { generalBackground: _gb, ...existingPhase1 } = (selectedProfile.phase1Data || {});
                            await client.graphql({
                              query: `mutation($input: UpdateApplicantYearProfileInput!) { updateApplicantYearProfile(input: $input) { applicantId } }`,
                              variables: { input: { applicantId: selectedParticipant.id, competitionYear, phase1Data: { ...existingPhase1, generalBackground: JSON.stringify(updatedBg) } } },
                              authMode: 'userPool',
                            });
                            setSelectedProfile((prev: any) => {
                              if (!prev) return prev;
                              const p1 = { ...prev.phase1Data, generalBackground: JSON.stringify(updatedBg) };
                              return { ...prev, phase1Data: p1 };
                            });
                          } catch (err) { console.error('Failed to approve costume:', err); }
                        }}
                        className="flex items-center gap-2 px-4 py-2 min-h-[var(--touch-target-min)] bg-primary text-primary-foreground font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-primary transition-colors border-none cursor-pointer"
                      >
                        <Check className="w-3 h-3" /> Approve Costume
                      </button>
                    ) : null}
                  </section>
                );
              })()}

              {/* Export Profile — Admin only */}
              {canPerformActions(role) && (
              <button
                onClick={() => {
                  const data = buildExportData(selectedParticipant, selectedProfile);
                  exportProfilePdf(data, tenantCopy);
                }}
                className="w-full flex items-center justify-between p-4 min-h-[var(--touch-target-min)] border border-border bg-card hover:border-primary transition-all group cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <Download className="w-4 h-4 text-muted-foreground" />
                  <span className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-foreground">Export Profile</span>
                </div>
              </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
